import { useState, useEffect, useRef, createContext, useContext, useCallback, useMemo, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useSearchParams, useNavigate, useLocation, useNavigationType } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Menu, X, Search, MapPin, Phone, Star, MessageCircle, LayoutGrid, ChevronRight, ChevronLeft, ChevronDown, Truck, Shield, Clock, Package, Heart, Plus, Minus, Trash2, ArrowRight, Filter, SlidersHorizontal, Tag, Store, Facebook, Instagram, Send, CreditCard, Check, AlertCircle, Sparkles, Timer, Users, Award, Percent, ChevronUp, ExternalLink, Camera, Upload, Eye, Scale, Mail, Download } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";
import { LanguageProvider, useLanguage, useTranslation, buildLocalizedPath, langFromPath, currentLangForAxios } from "./i18n";
import { LanguageToggle } from "./components/LanguageToggle";
import { AffirmCalculatorModal } from "./components/AffirmCalculatorModal";

// Lazy-loaded admin & dashboard pages (code-split for performance)
const AdminDashboard = lazy(() => import("./pages/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const StoreDashboard = lazy(() => import("./pages/DashboardPages").then(m => ({ default: m.StoreDashboard })));
const MasterDashboard = lazy(() => import("./pages/DashboardPages").then(m => ({ default: m.MasterDashboard })));
const MobileDashboard = lazy(() => import("./pages/DashboardPages").then(m => ({ default: m.MobileDashboard })));
const WhiteLabelStorefront = lazy(() => import("./pages/DashboardPages").then(m => ({ default: m.WhiteLabelStorefront })));

function LazyFallback() {
  return <div className="flex items-center justify-center min-h-[40vh]"><div className="animate-spin h-8 w-8 border-4 border-[#C05621] border-t-transparent rounded-full"></div></div>;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin auth uses Bearer tokens in Authorization header (NOT cookies)
// withCredentials is NOT needed and breaks CORS on custom domains
axios.interceptors.request.use(config => {
  // Auto-append lang=es to every /api/ request when user is on /es/* path.
  // English site is unchanged: when lang=en, we don't append anything.
  try {
    const lang = currentLangForAxios();
    if (lang === "es" && typeof config.url === "string" && config.url.includes("/api/")) {
      const sep = config.url.includes("?") ? "&" : "?";
      if (!/([?&])lang=/.test(config.url)) {
        config.url = config.url + sep + "lang=es";
      }
    }
  } catch (e) {
    // fail open — never block a request because of i18n
  }
  return config;
});

// ─── SEO Helper: Updates title, meta description, canonical, Open Graph tags, and JSON-LD ───
// jsonLd: optional array of schema.org objects (FAQPage, LocalBusiness, HowTo, etc.)
// They're tagged with data-page-jsonld="1" so we can clean up between routes.
function updatePageSEO({ title, description, canonical, ogTitle, ogDescription, ogImage, ogType, jsonLd }) {
  if (title) document.title = title;
  // Any prior page may have set <meta name="robots" content="noindex"> (e.g. product-not-found).
  // We're now on a valid indexable page — clear it so we don't accidentally noindex real pages.
  try {
    const r = document.querySelector('meta[name="robots"]');
    if (r) r.remove();
  } catch (e) { /* non-fatal */ }
  const setMeta = (selector, content) => {
    if (!content) return;
    let el = document.querySelector(selector);
    if (!el) { el = document.createElement("meta"); const attr = selector.match(/\[(\w+)="([^"]+)"\]/); if (attr) el.setAttribute(attr[1], attr[2]); document.head.appendChild(el); }
    el.setAttribute("content", content);
  };
  if (description) setMeta('meta[name="description"]', description);
  if (ogTitle || title) setMeta('meta[property="og:title"]', ogTitle || title);
  if (ogDescription || description) setMeta('meta[property="og:description"]', ogDescription || description);
  if (ogImage) setMeta('meta[property="og:image"]', ogImage);
  if (ogType) setMeta('meta[property="og:type"]', ogType);
  setMeta('meta[property="og:url"]', canonical || window.location.href);
  setMeta('meta[name="twitter:title"]', ogTitle || title || "");
  setMeta('meta[name="twitter:description"]', ogDescription || description || "");
  if (ogImage) setMeta('meta[name="twitter:image"]', ogImage);
  // Canonical link
  if (canonical) {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = canonical;
  }
  // hreflang alternate links — tells Google we have en-US + es-US versions of this page.
  // Accepts either canonical ending in /es/* or /... and derives the other variant.
  try {
    const base = "https://labodegabayfurniture.com";
    const cur = canonical || window.location.href;
    // Derive path segment from cur
    const url = new URL(cur, base);
    let path = url.pathname;
    const isEs = path === "/es" || path.startsWith("/es/");
    const enPath = isEs ? (path === "/es" ? "/" : path.slice(3)) : path;
    const esPath = enPath === "/" ? "/es" : `/es${enPath.startsWith("/") ? enPath : "/" + enPath}`;
    // Remove any existing hreflang we own
    document.querySelectorAll('link[rel="alternate"][data-i18n="1"]').forEach(el => el.remove());
    const addAlt = (hreflang, href) => {
      const l = document.createElement("link");
      l.rel = "alternate";
      l.hreflang = hreflang;
      l.href = href;
      l.setAttribute("data-i18n", "1");
      document.head.appendChild(l);
    };
    addAlt("en-US", `${base}${enPath}${url.search || ""}`);
    addAlt("es-US", `${base}${esPath}${url.search || ""}`);
    addAlt("x-default", `${base}${enPath}${url.search || ""}`);
  } catch (e) {
    // non-fatal
  }
  // JSON-LD schemas: clean up previous page's, then inject this page's.
  // All page-injected schemas carry data-page-jsonld="1" so we never touch the
  // global FurnitureStore schema embedded in index.html.
  try {
    document.querySelectorAll('script[type="application/ld+json"][data-page-jsonld="1"]').forEach(el => el.remove());
    if (Array.isArray(jsonLd)) {
      jsonLd.forEach(obj => {
        if (!obj) return;
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-page-jsonld", "1");
        s.textContent = JSON.stringify(obj);
        document.head.appendChild(s);
      });
    }
  } catch (e) {
    // non-fatal
  }
}


// Context
const CartContext = createContext();
const useCart = () => useContext(CartContext);
const CompareContext = createContext();
const useCompare = () => useContext(CompareContext);

function getSessionId() {
  let sid = localStorage.getItem("lbb_session");
  if (!sid) {
    sid = "s_" + Math.random().toString(36).substring(2, 15);
    localStorage.setItem("lbb_session", sid);
  }
  return sid;
}

// ─── IMAGE OPTIMIZATION ───
function rawOptimizedImage(url, size = 400) {
  if ((url.includes("ashleyfurniture.com") || url.includes("cdn.ashley.com")) && !url.includes("?")) {
    return `${url}?wid=${size}&fmt=webp`;
  }
  // Homelegance serves raw 5-7MB uploads and Coaster ~230KB with no resize
  // service — route through Photon (i0.wp.com, WordPress.com's free image CDN)
  // for ~98% smaller auto-WebP with PERMANENT edge caching (the previous proxy
  // evicted constantly, causing 20-30s cold loads). onError falls back to the original.
  if (url.includes("homelegance.com") || url.includes("coastercenter.com") || url.includes("miltongreensstars.com")) {
    return `https://i0.wp.com/${url.replace(/^https?:\/\//, "")}?w=${size}&quality=75&strip=all`;
  }
  return url;
}
// Serve product images through our OWN hotlink-protected proxy so other sites
// can't embed them and scrapers can't bulk-pull them. Photon still does the
// heavy lifting behind the proxy; long immutable caching keeps it fast.
function getOptimizedImage(url, size = 400) {
  if (!url) return "https://placehold.co/400x400/F7FAFC/718096?text=No+Image";
  const src = rawOptimizedImage(url, size);
  if (!/^https?:\/\//.test(src) || src.includes("placehold.co")) return src;
  return `${API}/img?u=${encodeURIComponent(src)}`;
}
// Fallback chain if the proxy or Photon ever fails: proxy → underlying (Photon/vendor) → raw origin → give up
function imgFallback(e) {
  const src = e.target.src || "";
  try {
    if (src.includes("/api/img")) {
      const u = new URL(src).searchParams.get("u");
      if (u && u !== src) { e.target.src = u; return; }  // keep onerror for next hop
    }
    if (src.includes("i0.wp.com/")) {
      const orig = "https://" + new URL(src).pathname.slice(1);
      if (orig !== src) { e.target.src = orig; e.target.onerror = null; return; }
    }
  } catch { /* leave as-is */ }
  e.target.onerror = null;
}
function getCardImage(url) { return getOptimizedImage(url, 400); }
function getDetailImage(url) { return getOptimizedImage(url, 800); }
function getThumbnail(url) { return getOptimizedImage(url, 400); }

const GOOGLE_REVIEWS_URL = "https://www.google.com/maps/search/?api=1&query=La+Bodega+Bay+Furniture+Los+Banos+CA&query_place_id=ChIJCYIQ2Yt-kYARz02WGkN_lmE";
// Affirm hosted prequalification — soft check that doesn't affect credit score.
// Pass the product price so Affirm pre-fills the purchase amount (unit_price is in cents).
const affirmPrequalUrl = (amount) =>
  `https://www.affirm.com/apps/prequal/?public_api_key=G29FAWJVTU5I8U77&use_promo=true` +
  (amount ? `&unit_price=${Math.round(amount * 100)}&page_type=product` : `&page_type=landing`) +
  `&referring_url=https://labodegabayfurniture.com`;

// ─── Ad platform tracking helpers (Google Ads AW tag + Meta Pixel) ───
const AW_ID = "AW-775860298";
const META_PIXEL_ID = "1228066817928219";
// Affirm-sourced promotional pricing — numbers come from Affirm's own promo API
// (via our cached backend proxy), never hand-rolled. Compliance-critical.
const affirmPromoCache = new Map();
const fetchAffirmPromo = (key) => {
  if (!affirmPromoCache.has(key)) {
    affirmPromoCache.set(key, axios.get(`${API}/payments/affirm-promo?amount=${key}`).then(r => r.data).catch(() => null));
  }
  return affirmPromoCache.get(key);
};
function useAffirmPromo(amount) {
  const key = amount && amount >= 50 ? Math.round(amount) : 0;
  const [promo, setPromo] = useState(null);
  useEffect(() => {
    if (!key) { setPromo(null); return; }
    let alive = true;
    fetchAffirmPromo(key).then(d => { if (alive) setPromo(d); });
    return () => { alive = false; };
  }, [key]);
  return promo;
}
const affirmAlaText = (promo, lang) => {
  if (!promo?.monthly) return null;
  const es = lang === "es";
  if (promo.zero_apr) return es ? `0% APR o desde $${promo.monthly}/mes con Affirm` : `0% APR or as low as $${promo.monthly}/mo with Affirm`;
  if (promo.pay_in_4) return es ? `4 pagos sin interés o desde $${promo.monthly}/mes con Affirm` : `4 interest-free payments or as low as $${promo.monthly}/mo with Affirm`;
  return es ? `Desde $${promo.monthly}/mes con Affirm` : `As low as $${promo.monthly}/mo with Affirm`;
};

const track = (name, params = {}) => { try { if (typeof window !== "undefined" && window.gtag) window.gtag("event", name, params); } catch { /* noop */ } };
const fbTrack = (name, params = {}, eventId) => { try { if (typeof window !== "undefined" && window.fbq) window.fbq("track", name, params, eventId ? { eventID: eventId } : undefined); } catch { /* noop */ } };
const adsConsentAllowed = () => {
  try {
    const c = JSON.parse(localStorage.getItem("lbb_privacy_consent") || "null");
    if (c && typeof c.adsAllowed === "boolean") return c.adsAllowed;
    return navigator.globalPrivacyControl !== true;
  } catch { return true; }
};
const fbIdentify = (email, phone) => { try { if (typeof window !== "undefined" && window.fbq && email) window.fbq("init", META_PIXEL_ID, { em: String(email).trim().toLowerCase(), ph: String(phone || "").replace(/\D/g, "") || undefined }); } catch { /* noop */ } };
const getCookie = (n) => { try { const m = document.cookie.match(new RegExp("(?:^|; )" + n + "=([^;]*)")); return m ? decodeURIComponent(m[1]) : ""; } catch { return ""; } };
const gaClientId = () => { const m = getCookie("_ga").match(/^GA\d+\.\d+\.(.+)$/); return m ? m[1] : ""; };
// Clean ad attribution — capture first-touch + last-touch at entry, use at checkout
const captureAttribution = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const g = (k) => params.get(k) || "";
    const hit = {
      utm_source: g("utm_source"), utm_medium: g("utm_medium"), utm_campaign: g("utm_campaign"),
      utm_term: g("utm_term"), utm_content: g("utm_content"),
      gclid: g("gclid") || g("gbraid") || g("wbraid"), fbclid: g("fbclid"),
      referrer: document.referrer || "", landing_page: window.location.href, ts: new Date().toISOString(),
    };
    const hasParams = !!(hit.utm_source || hit.gclid || hit.fbclid);
    const hasExtRef = !!(hit.referrer && !hit.referrer.includes(window.location.host));
    if (!localStorage.getItem("lbb_first_touch") && (hasParams || hasExtRef)) {
      localStorage.setItem("lbb_first_touch", JSON.stringify(hit));
    }
    if (hasParams || hasExtRef) localStorage.setItem("lbb_last_touch", JSON.stringify(hit));
  } catch { /* noop */ }
};
const getAttribution = () => {
  const read = (k) => { try { return JSON.parse(localStorage.getItem(k) || "{}"); } catch { return {}; } };
  const first = read("lbb_first_touch"), last = read("lbb_last_touch");
  return {
    utm_source: last.utm_source || "", utm_medium: last.utm_medium || "", utm_campaign: last.utm_campaign || "",
    gclid: last.gclid || first.gclid || "", fbclid: last.fbclid || first.fbclid || "",
    referrer: last.referrer || first.referrer || "", landing_page: first.landing_page || "",
    first_touch: first || {},
  };
};
// Real Google reviews (verbatim, provided by the owner) — never fabricate
const GOOGLE_REVIEW_QUOTES = [
  { name: "Diana Pfitzer", text: "Wonderful customer service in store. Delivered and set up in my home professionally. My realtor recommended La Bodega and I’m so happy she did. Quality furniture, great service. You’re missing out if you haven’t tried this business!" },
  { name: "Chadwick Erickson", text: "This furniture shop really impressed me with its variety and quality. I found a beautiful wardrobe at a very reasonable price. The staff were helpful and guided me without pressure. The finishing and build quality are excellent. Delivery was fast and well-handled. A dependable place for stylish home furniture." },
  { name: "Arcelia Silva", text: "I received my custom order within a week, their crew came and put my living room together so quickly and professionally. I will definitely be ordering from them again!" },
  { name: "Ray Lovato", text: "Quality furniture for a bargan price. The sales reps are knowledgeable, up front and honest. Delivery was free !!! If your shopping for furniture, shop no further. This is the best furniture place in the county! bar none!" },
];

// ─── RECENTLY VIEWED ───
const RECENTLY_VIEWED_KEY = "lbb_recently_viewed";
const MAX_RECENTLY_VIEWED = 12;

function addToRecentlyViewed(product) {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || "[]");
    const filtered = stored.filter(p => p.id !== product.id);
    const compact = {
      id: product.id,
      name: product.name,
      slug: product.slug,
      brand: product.brand,
      price: product.price,
      original_price: product.original_price,
      category: product.category,
      images: product.images?.slice(0, 1) || [],
      shipping_days_min: product.shipping_days_min,
      shipping_days_max: product.shipping_days_max,
      is_new_arrival: product.is_new_arrival,
      stock_quantity: product.stock_quantity,
      is_deal_of_week: product.is_deal_of_week,
    };
    filtered.unshift(compact);
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(filtered.slice(0, MAX_RECENTLY_VIEWED)));
  } catch (e) { /* silent */ }
}

function getRecentlyViewed(excludeId) {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || "[]");
    return excludeId ? stored.filter(p => p.id !== excludeId) : stored;
  } catch { return []; }
}

// ─── RECENTLY VIEWED CAROUSEL COMPONENT ───
function RecentlyViewedCarousel({ excludeProductId }) {
  const [items, setItems] = useState([]);
  const [scrollPos, setScrollPos] = useState(0);
  const scrollRef = useCallback(node => { if (node) node.scrollLeft = scrollPos; }, [scrollPos]);

  useEffect(() => {
    setItems(getRecentlyViewed(excludeProductId));
  }, [excludeProductId]);

  if (items.length === 0) return null;

  const scroll = (dir) => {
    const container = document.getElementById("rv-scroll");
    if (!container) return;
    const amount = dir === "left" ? -320 : 320;
    container.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <section className="mt-16 pt-12 border-t border-gray-100" data-testid="recently-viewed-section">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#1A202C]">Recently Viewed</h2>
          <p className="text-gray-500 text-sm mt-1">Pick up where you left off</p>
        </div>
        {items.length > 4 && (
          <div className="flex gap-2">
            <button onClick={() => scroll("left")} className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors" data-testid="rv-scroll-left" aria-label="Scroll left">
              <ChevronRight size={18} className="rotate-180 text-gray-600" />
            </button>
            <button onClick={() => scroll("right")} className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors" data-testid="rv-scroll-right" aria-label="Scroll right">
              <ChevronRight size={18} className="text-gray-600" />
            </button>
          </div>
        )}
      </div>
      <div id="rv-scroll" className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
        {items.map((product) => (
          <RecentlyViewedCard key={product.id} product={product} />
        ))}
      </div>
    </section>
  );
}

function RecentlyViewedCard({ product }) {
  const navigate = useNavigate();
  const pickupPrice = product.price * 0.9;
  const savings = product.original_price > product.price ? product.original_price - product.price : 0;

  return (
    <div
      className="flex-shrink-0 w-[240px] snap-start group cursor-pointer"
      data-testid={`rv-card-${product.slug}`}
      onClick={() => navigate(`/product/${product.slug}`)}
    >
      <div className="relative overflow-hidden rounded-xl bg-[#F7FAFC] aspect-[4/3] mb-3">
        <img loading="lazy" decoding="async" onError={imgFallback} src={getCardImage(product.images?.[0]?.url)}
          alt={`${product.name} – ${product.brand || ""} ${product.subcategory || product.category || ""}`.trim()}
          className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
        />
        {product.is_new_arrival && (
          <span className="absolute top-2 left-2 bg-[#38A169] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">New</span>
        )}
        {savings > 0 && (
          <span className="absolute top-2 right-2 bg-[#DD6B20] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Save ${savings.toFixed(0)}</span>
        )}
      </div>
      <p className="text-[10px] font-semibold text-[#C05621] uppercase tracking-wider">{product.brand}</p>
      <h4 className="text-sm font-semibold text-[#1A202C] group-hover:text-[#C05621] transition-colors line-clamp-1 mt-0.5">{product.name}</h4>
      <div className="flex items-baseline gap-1.5 mt-1">
        <span className="text-base font-bold text-[#1A202C]">${product.price.toFixed(2)}</span>
        {savings > 0 && <span className="text-xs text-gray-400 line-through">${product.original_price.toFixed(2)}</span>}
      </div>
      <p className="text-[10px] text-[#38A169] font-medium flex items-center gap-0.5 mt-0.5">
        <Store size={10} /> Pickup: ${pickupPrice.toFixed(2)}
      </p>
    </div>
  );
}

// ─── HEADER ───
const BANNER_CACHE_KEY = "lbb_sale_banner_v1";
function SaleBanner() {
  // Cached copy paints on the very first frame (no layout shift on repeat visits); while the first
  // visit is still loading we reserve the bar's exact height instead of pushing the page down later.
  const [banner, setBanner] = useState(() => {
    try { const c = JSON.parse(localStorage.getItem(BANNER_CACHE_KEY) || "null"); return c === null ? undefined : c; } catch { return undefined; }
  });
  const { lang } = useTranslation();
  const linkPrefix = lang === "es" ? "/es" : "";

  useEffect(() => {
    axios.get(`${API}/storefront/sale-banner`).then(r => {
      const next = (r.data?.enabled && r.data?.text) ? r.data : null;
      setBanner(next);
      try { localStorage.setItem(BANNER_CACHE_KEY, JSON.stringify(next)); } catch { /* private mode */ }
    }).catch(() => setBanner(prev => (prev === undefined ? null : prev)));
  }, []);

  if (banner === null) return null;
  const loading = banner === undefined;
  const text = loading ? "\u00a0" : (lang === "es" ? (banner.text_es || banner.text) : banner.text);
  const cta = loading ? "" : (lang === "es" ? (banner.cta_es || banner.cta) : banner.cta);

  return (
    <Link to={`${linkPrefix}${(banner && banner.link) || "/categories"}`} className="block bg-[#B91C1C] hover:bg-[#991B1B] transition-colors" data-testid="sale-banner" aria-busy={loading}>
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-2 sm:gap-3 text-white">
        <Star size={13} fill="currentColor" className={`hidden sm:block text-amber-300 flex-shrink-0 ${loading ? "invisible" : ""}`} />
        <span className="font-bold text-xs sm:text-sm md:text-base tracking-wide uppercase text-center" data-testid="sale-banner-text">{text}</span>
        <Star size={13} fill="currentColor" className={`hidden sm:block text-amber-300 flex-shrink-0 ${loading ? "invisible" : ""}`} />
        {cta && (
          <span className="hidden md:inline-flex items-center gap-1 bg-white text-[#B91C1C] font-bold text-xs px-3 py-1 rounded-full uppercase tracking-wider flex-shrink-0" data-testid="sale-banner-cta">
            {cta} <ArrowRight size={12} />
          </span>
        )}
      </div>
    </Link>
  );
}

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [hoveredCat, setHoveredCat] = useState(null);
  const [subcats, setSubcats] = useState([]);
  const [showVisualSearch, setShowVisualSearch] = useState(false);
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, t } = useTranslation();
  const linkPrefix = lang === "es" ? "/es" : "";

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      track("search", { search_term: searchQuery.trim() });
      fbTrack("Search", { search_string: searchQuery.trim() });
      navigate(`${linkPrefix}/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
      setSearchSuggestions([]);
    }
  };

  const onSearchInput = async (val) => {
    setSearchQuery(val);
    if (val.length < 2) { setSearchSuggestions([]); return; }
    try {
      const r = await axios.get(`${API}/products/smart-search?q=${encodeURIComponent(val)}&limit=6`);
      setSearchSuggestions(r.data.products?.slice(0, 6) || []);
    } catch { setSearchSuggestions([]); }
  };

  const navLinks = [
    { label: t("header.nav.shopAll"), path: `${linkPrefix}/categories` },
    { label: t("header.nav.livingRoom"), path: `${linkPrefix}/category/Living Room` },
    { label: t("header.nav.bedroom"), path: `${linkPrefix}/category/Bedroom` },
    { label: t("header.nav.diningRoom"), path: `${linkPrefix}/category/Dining Room` },
    { label: t("header.nav.mattresses"), path: `${linkPrefix}/category/Mattress` },
    { label: t("header.nav.financing"), path: `${linkPrefix}/financing` },
  ];

  return (
    <>
      {/* Sitewide sale banner (admin-controlled) */}
      <SaleBanner />

      {/* Top bar */}
      <div className="bg-[#2D3748] text-white text-xs py-2" data-testid="top-bar">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="tel:2098274076" onClick={() => { try { window.gtag && window.gtag("event", "phone_click", { event_category: "engagement", phone_number: "2098274076" }); } catch {} }} className="flex items-center gap-1 hover:text-orange-300 transition-colors">
              <Phone size={12} /> (209) 827-4076
            </a>
            <span className="hidden sm:flex items-center gap-1">
              <MapPin size={12} /> {t("header.topbarHours")}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:flex items-center gap-1 text-orange-300">
              <Star size={12} fill="currentColor" /> 4.9/5 Google Rating
            </span>
            <span className="hidden lg:inline bg-[#C05621]/20 text-orange-300 font-bold px-3 py-1 rounded-full text-sm border border-orange-400/30"><Award size={14} className="inline mr-1" /> {t("header.topbarFamilyOwned")}</span>
            <LanguageToggle />
            <a href="https://www.facebook.com/LaBodegaBayFurniture/" target="_blank" rel="noreferrer" aria-label="Facebook"><Facebook size={14} /></a>
            <a href="https://www.instagram.com/labodegabayfurniture/" target="_blank" rel="noreferrer" aria-label="Instagram"><Instagram size={14} /></a>
          </div>
        </div>
      </div>

      {/* Promo bar */}
      <div className="bg-[#C05621] text-white text-center py-2 text-sm font-medium" data-testid="promo-bar">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-2">
          <Truck size={16} />
          <span>{t("header.promoDelivery")}</span>
          <span className="mx-2">|</span>
          <CreditCard size={16} />
          <span className="hidden sm:inline">{t("header.promoFinancing")}</span>
        </div>
      </div>

      {/* Main header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-[#E2E8F0] shadow-sm" data-testid="main-header">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16 md:h-20">
          <Link to={`${linkPrefix}/`} className="flex-shrink-0" data-testid="logo-link">
            <p className="font-heading text-xl md:text-2xl font-bold text-[#2D3748] leading-tight">
              La Bodega Bay<br className="md:hidden" />
              <span className="text-[#C05621]"> Furniture</span>
            </p>
          </Link>

          {/* Desktop nav with mega menu */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map((link) => {
              const isCategoryLink = link.path.startsWith("/category/");
              const catName = isCategoryLink ? decodeURIComponent(link.path.replace("/category/", "")) : "";
              return (
                <div key={link.path} className="relative"
                  onMouseEnter={async () => {
                    if (isCategoryLink) {
                      setHoveredCat(catName);
                      try {
                        const r = await axios.get(`${API}/products/category/${encodeURIComponent(catName)}/subcategories`);
                        setSubcats(r.data.subcategories || []);
                      } catch {}
                    }
                  }}
                  onMouseLeave={() => { setHoveredCat(null); setSubcats([]); }}
                >
                  <Link to={link.path} className="text-sm font-medium text-[#2D3748] hover:text-[#C05621] transition-colors relative group block py-2">
                    {link.label}
                    <span className="absolute -bottom-0 left-0 w-0 h-0.5 bg-[#C05621] transition-all group-hover:w-full" />
                  </Link>
                  {hoveredCat === catName && subcats.length > 0 && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 bg-white border border-gray-100 rounded-xl shadow-xl p-4 z-50 min-w-[220px] max-h-[400px] overflow-y-auto"
                      onMouseEnter={() => setHoveredCat(catName)} onMouseLeave={() => { setHoveredCat(null); setSubcats([]); }}>
                      <Link to={`/category/${encodeURIComponent(catName)}`} className="block text-xs font-bold text-[#C05621] mb-2 hover:underline">View All {catName}</Link>
                      {subcats.map(s => (
                        <Link key={s.name} to={`/shop?category=${encodeURIComponent(catName)}&subcategory=${encodeURIComponent(s.name)}`}
                          className="block py-1.5 px-2 text-xs text-gray-600 hover:bg-gray-50 hover:text-[#C05621] rounded transition-colors"
                          onClick={() => { setHoveredCat(null); setSubcats([]); }}
                        >
                          {s.name} <span className="text-gray-400 float-right">{s.count}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <Link to={`${linkPrefix}/about`} className="text-sm font-medium text-[#2D3748] hover:text-[#C05621] transition-colors">{t("header.nav.about")}</Link>
            <Link to={`${linkPrefix}/blog`} className="text-sm font-medium text-[#2D3748] hover:text-[#C05621] transition-colors">{t("header.nav.blog")}</Link>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button onClick={() => setSearchOpen(!searchOpen)} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-testid="search-toggle" aria-label="Search">
              <Search size={20} className="text-[#2D3748]" />
            </button>
            <Link to={`${linkPrefix}/cart`} className="p-2 hover:bg-gray-100 rounded-full transition-colors relative" data-testid="cart-link" aria-label="Cart">
              <ShoppingCart size={20} className="text-[#2D3748]" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#C05621] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Link>
            <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden p-2 hover:bg-gray-100 rounded-full" data-testid="mobile-menu-toggle" aria-label="Menu">
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Search bar */}
        <AnimatePresence>
          {searchOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="border-t border-gray-100 overflow-hidden">
              <form onSubmit={handleSearch} className="max-w-2xl mx-auto px-4 py-3 relative">
                <div className="flex gap-2">
                  <input
                    value={searchQuery}
                    onChange={(e) => onSearchInput(e.target.value)}
                    placeholder={lang === "es" ? "Buscar muebles, camas, sofás… o describe lo que necesitas" : "Search furniture, beds, sofas... or describe what you need"}
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30 focus:border-[#C05621]"
                    data-testid="search-input"
                    autoFocus
                  />
                  <button type="submit" className="px-6 py-2.5 bg-[#2D3748] text-white rounded-lg text-sm font-medium hover:bg-[#1A202C] transition-colors" data-testid="search-submit">Search</button>
                </div>
                {searchSuggestions.length > 0 && (
                  <div className="absolute left-4 right-4 top-full bg-white border border-gray-200 rounded-xl shadow-xl z-50 mt-1 overflow-hidden">
                    {searchSuggestions.map((p, i) => (
                      <Link key={i} to={`/product/${p.slug}`} onClick={() => { setSearchOpen(false); setSearchQuery(""); setSearchSuggestions([]); }}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0">
                        {p.images?.[0]?.url && <img src={getThumbnail(p.images[0].url)} onError={imgFallback} alt="" className="w-10 h-10 rounded object-cover" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1A202C] truncate">{p.name}</p>
                          <p className="text-xs text-gray-400">{p.brand} | {p.category}</p>
                        </div>
                        <p className="text-sm font-bold text-[#C05621]">${p.price?.toFixed(2)}</p>
                      </Link>
                    ))}
                    <Link to={`/shop?search=${encodeURIComponent(searchQuery)}`} onClick={() => { setSearchOpen(false); setSearchSuggestions([]); }}
                      className="block text-center py-2 text-xs font-bold text-[#C05621] hover:bg-gray-50">
                      View all results for "{searchQuery}"
                    </Link>
                  </div>
                )}
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="lg:hidden border-t border-gray-100 overflow-hidden bg-white">
              <nav className="px-4 py-4 space-y-1" data-testid="mobile-nav">
                {navLinks.map((link) => (
                  <Link key={link.path} to={link.path} onClick={() => setMenuOpen(false)} className="block py-2.5 px-3 text-sm font-medium text-[#2D3748] hover:bg-gray-50 rounded-lg">{link.label}</Link>
                ))}
                <Link to="/about" onClick={() => setMenuOpen(false)} className="block py-2.5 px-3 text-sm font-medium text-[#2D3748] hover:bg-gray-50 rounded-lg">About Us</Link>
                <Link to="/blog" onClick={() => setMenuOpen(false)} className="block py-2.5 px-3 text-sm font-medium text-[#2D3748] hover:bg-gray-50 rounded-lg">Blog</Link>
                <Link to="/financing" onClick={() => setMenuOpen(false)} className="block py-2.5 px-3 text-sm font-medium text-[#2D3748] hover:bg-gray-50 rounded-lg">Financing</Link>
                <Link to="/contact" onClick={() => setMenuOpen(false)} className="block py-2.5 px-3 text-sm font-medium text-[#2D3748] hover:bg-gray-50 rounded-lg">Contact</Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
      {showVisualSearch && <VisualSearchModal onClose={() => setShowVisualSearch(false)} />}
    </>
  );
}

// ─── FOOTER ───
function Footer() {
  const [storeInfo, setStoreInfo] = useState(null);
  const [navGroups, setNavGroups] = useState([]);
  const [footerCities, setFooterCities] = useState([]);
  const { lang } = useTranslation();
  const linkPrefix = lang === "es" ? "/es" : "";
  useEffect(() => { axios.get(`${API}/store-info`).then(r => setStoreInfo(r.data)).catch(() => {}); }, []);
  useEffect(() => { axios.get(`${API}/nav/subcategories`).then(r => setNavGroups(r.data?.groups || [])).catch(() => {}); }, []);
  useEffect(() => { axios.get(`${API}/cities`).then(r => setFooterCities(r.data || [])).catch(() => {}); }, []);
  const locations = storeInfo?.locations || [
    { name: "Los Banos", address: "2225 East Pacheco Blvd", phone: "(209) 827-4076", hours: "" },
    { name: "Newman", address: "1925 N Street", phone: "(209) 243-9020", hours: "" }
  ];
  // Localized strings
  const T = lang === "es" ? {
    about: "Empresa familiar desde 2000. Más de 25 años entregando muebles de calidad al Bay Area y Valle Central. Tu tienda local de confianza.",
    rating: "4.9/5 en Google",
    shop: "Comprar",
    livingRoom: "Sala",
    bedroom: "Recámara",
    dining: "Comedor",
    mattresses: "Colchones",
    homeOffice: "Oficina",
    outdoor: "Exterior",
    info: "Información",
    aboutUs: "Nosotros",
    contact: "Contacto",
    policies: "Políticas",
    privacy: "Política de Privacidad",
    priceGuarantee: "Garantía de Precio",
    financing: "Opciones de Financiamiento",
    whyLocal: "Por qué Comprar Local",
    mattressStore: "Tienda de Colchones",
    blog: "Blog",
    visitUs: "Visítanos",
    rights: "Todos los derechos reservados.",
    terms: "Términos de Servicio",
    secure: "Pago Seguro",
  } : {
    about: "Proudly family-owned since 2000. Over 25 years of delivering quality furniture to the Bay Area & Central Valley. Your trusted local furniture store.",
    rating: "4.9/5 on Google",
    shop: "Shop",
    livingRoom: "Living Room",
    bedroom: "Bedroom",
    dining: "Dining Room",
    mattresses: "Mattresses",
    homeOffice: "Home Office",
    outdoor: "Outdoor",
    info: "Info",
    aboutUs: "About Us",
    contact: "Contact",
    policies: "Company Policies",
    privacy: "Privacy Policy",
    priceGuarantee: "Price Guarantee",
    financing: "Financing Options",
    whyLocal: "Why Shop Local",
    mattressStore: "Mattress Store",
    blog: "Blog",
    visitUs: "Visit Us",
    rights: "All rights reserved.",
    terms: "Terms of Service",
    secure: "Secure Checkout",
  };
  return (
    <footer className="bg-[#2D3748] text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <h3 className="font-heading text-2xl font-bold mb-3">La Bodega Bay <span className="text-[#DD6B20]">Furniture</span></h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">{T.about}</p>
            <p className="text-gray-400 text-xs mb-4" data-testid="footer-county-line">
              <Link to={`${linkPrefix}/furniture/merced-county`} className="hover:text-white transition-colors underline decoration-white/20">
                {lang === "es" ? "Sirviendo Los Banos y todo el Condado de Merced" : "Serving Los Banos & all of Merced County"}
              </Link>
            </p>
            <div className="flex items-center gap-1 text-[#DD6B20] mb-3">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
              <span className="text-white text-sm ml-2">{T.rating}</span>
            </div>
            <div className="flex gap-3">
              <a href="https://www.facebook.com/LaBodegaBayFurniture/" target="_blank" rel="noreferrer" className="p-2 bg-white/10 rounded-full hover:bg-[#DD6B20] transition-colors"><Facebook size={18} /></a>
              <a href="https://www.instagram.com/labodegabayfurniture/" target="_blank" rel="noreferrer" className="p-2 bg-white/10 rounded-full hover:bg-[#DD6B20] transition-colors"><Instagram size={18} /></a>
            </div>
          </div>
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider mb-4 text-gray-400">{T.shop}</h4>
            <ul className="space-y-2.5 text-sm text-gray-300">
              <li><Link to={`${linkPrefix}/category/Living Room`} className="hover:text-white transition-colors">{T.livingRoom}</Link></li>
              <li><Link to={`${linkPrefix}/category/Bedroom`} className="hover:text-white transition-colors">{T.bedroom}</Link></li>
              <li><Link to={`${linkPrefix}/category/Dining Room`} className="hover:text-white transition-colors">{T.dining}</Link></li>
              <li><Link to={`${linkPrefix}/category/Mattress`} className="hover:text-white transition-colors">{T.mattresses}</Link></li>
              <li><Link to={`${linkPrefix}/category/Home Office`} className="hover:text-white transition-colors">{T.homeOffice}</Link></li>
              <li><Link to={`${linkPrefix}/shop?category=Outdoor`} className="hover:text-white transition-colors">{T.outdoor}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider mb-4 text-gray-400">{T.info}</h4>
            <ul className="space-y-2.5 text-sm text-gray-300">
              <li><Link to={`${linkPrefix}/about`} className="hover:text-white transition-colors">{T.aboutUs}</Link></li>
              <li><Link to={`${linkPrefix}/contact`} className="hover:text-white transition-colors">{T.contact}</Link></li>
              <li><Link to={`${linkPrefix}/policies`} className="hover:text-white transition-colors">{T.policies}</Link></li>
              <li><Link to={`${linkPrefix}/policies`} className="hover:text-white transition-colors">{T.privacy}</Link></li>
              <li><Link to={`${linkPrefix}/price-guarantee`} className="hover:text-white transition-colors">{T.priceGuarantee}</Link></li>
              <li><Link to={`${linkPrefix}/financing`} className="hover:text-white transition-colors">{T.financing}</Link></li>
              <li><Link to={`${linkPrefix}/why-shop-local`} className="hover:text-white transition-colors">{T.whyLocal}</Link></li>
              <li><Link to={`${linkPrefix}/mattress-store`} className="hover:text-white transition-colors">{T.mattressStore}</Link></li>
              <li><Link to={`${linkPrefix}/blog`} className="hover:text-white transition-colors">{T.blog}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider mb-4 text-gray-400">{T.visitUs}</h4>
            <div className="space-y-4 text-sm text-gray-300">
              {locations.map((loc, i) => (
                <div key={i}>
                  <p className="font-semibold text-white">{loc.name}</p>
                  <p>{loc.address?.split(',')[0]}</p>
                  <p>{loc.address?.includes(',') ? loc.address.split(',').slice(1).join(',').trim() : ''}</p>
                  <a href={`tel:${(loc.phone || '').replace(/\D/g,'')}`} className="text-[#DD6B20] hover:text-orange-300">{loc.phone}</a>
                  {loc.hours && <p className="text-xs text-gray-400 mt-1">{loc.hours}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
        {navGroups.length > 0 && (
          <nav className="border-t border-white/10 mt-12 pt-8" data-testid="footer-shop-by-category" aria-label={lang === "es" ? "Comprar por categoría" : "Shop by category"}>
            <h4 className="font-bold text-sm uppercase tracking-wider mb-5 text-gray-400">{lang === "es" ? "Comprar por Categoría" : "Shop by Category"}</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-6">
              {navGroups.map(g => (
                <div key={g.category}>
                  <Link to={`${linkPrefix}/category/${encodeURIComponent(g.category)}`} className="block text-sm font-semibold text-white mb-2 hover:text-[#DD6B20] transition-colors" data-testid={`footer-cat-${g.category.toLowerCase().replace(/\s+/g,'-')}`}>{g.category}</Link>
                  <ul className="space-y-1.5">
                    {g.subcategories.map(s => (
                      <li key={s.subcategory}>
                        <Link to={`${linkPrefix}${s.url}`} className="text-xs text-gray-400 hover:text-white transition-colors" data-testid={`footer-subcat-${s.subcategory.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>{s.subcategory}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </nav>
        )}
        {footerCities.length > 0 && (
          <nav className="border-t border-white/10 mt-12 pt-8" data-testid="footer-areas-we-serve" aria-label={lang === "es" ? "Áreas que servimos" : "Areas we serve"}>
            <h4 className="font-bold text-sm uppercase tracking-wider mb-2 text-gray-400">{lang === "es" ? "Áreas que Servimos" : "Areas We Serve"}</h4>
            <p className="text-xs text-gray-500 mb-5">{lang === "es" ? "Entrega gratis de muebles en todo el Valle Central" : "Free furniture delivery across the Central Valley"}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {footerCities.map(c => (
                <Link key={c.slug} to={`${linkPrefix}/furniture/${c.slug}`} className="text-xs text-gray-400 hover:text-white transition-colors" data-testid={`footer-city-${c.slug}`}>
                  {lang === "es" ? "Muebles" : "Furniture"} {c.name}
                </Link>
              ))}
            </div>
          </nav>
        )}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-400">&copy; {new Date().getFullYear()} La Bodega Bay Furniture. {T.rights}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-400">
            <Link to={`${linkPrefix}/policies?tab=privacy`} className="hover:text-white">{T.privacy}</Link>
            <Link to={`${linkPrefix}/policies?tab=terms`} className="hover:text-white">{T.terms}</Link>
            <button onClick={() => window.dispatchEvent(new Event("lbb-open-privacy"))} className="hover:text-white underline underline-offset-2" data-testid="footer-do-not-sell-link">
              {lang === "es" ? "No Vender ni Compartir Mi Información Personal" : "Do Not Sell or Share My Personal Information"}
            </button>
            <span className="flex items-center gap-1"><Shield size={12} /> {T.secure}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── PRODUCT CARD ───
function ProductCard({ product, eager = false }) {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { compareItems, toggleCompare } = useCompare();
  const { lang } = useTranslation();
  const linkPrefix = lang === "es" ? "/es" : "";
  const savings = product.original_price > product.price ? (product.original_price - product.price) : 0;
  const pickupPrice = product.price * 0.9;
  const [hovered, setHovered] = useState(false);
  const [showQuick, setShowQuick] = useState(false);
  const [showAffirm, setShowAffirm] = useState(false);
  const affirmPromo = useAffirmPromo(product.price);
  const img1 = getCardImage(product.images?.[0]?.url);
  const img2 = getCardImage(product.images?.[1]?.url || product.images?.[0]?.url);
  const isComparing = compareItems.some(c => c.id === product.id);

  return (
    <>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group cursor-pointer"
      data-testid={`product-card-${product.slug}`}
      onClick={() => { try { sessionStorage.setItem("lbb_scroll_anchor", product.slug); } catch { /* full */ } navigate(`${linkPrefix}/product/${product.slug}`); }}
    >
      <div className="relative overflow-hidden rounded-lg bg-[#F7FAFC] aspect-square mb-2"
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
        <img src={hovered ? img2 : img1} alt={`${product.name} – ${product.brand || ""} ${product.subcategory || product.category || ""}`.trim()}
          className="w-full h-full object-contain p-1"
          loading={eager ? "eager" : "lazy"} decoding="async" fetchPriority={eager ? "high" : "low"}
          onError={imgFallback}
        />
        {product.is_new_arrival && (
          <span className="absolute top-2 left-2 bg-[#38A169] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">New</span>
        )}
        {product.is_deal_of_week && (
          <span className="absolute top-2 left-2 bg-[#E53E3E] text-white text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-0.5"><Timer size={10} /> EXTRA {product.deal_discount_pct || 20}% OFF</span>
        )}
        {savings > 0 && (
          <span className="absolute top-2 right-2 bg-[#DD6B20] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Save ${savings.toFixed(0)}</span>
        )}
        {product.stock_quantity <= 5 && product.stock_quantity > 0 && (
          <span className="absolute bottom-2 left-2 bg-[#E53E3E]/90 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">Only {product.stock_quantity} left!</span>
        )}
        {/* Quick View + Compare overlay */}
        <div className={`absolute inset-x-0 bottom-0 p-2 flex gap-1.5 transition-all duration-200 ${hovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
          <button onClick={(e) => { e.stopPropagation(); setShowQuick(true); }}
            className="flex-1 py-1.5 bg-white/95 backdrop-blur text-[#1A202C] text-[10px] font-bold rounded-lg hover:bg-white transition-colors flex items-center justify-center gap-1"
            data-testid={`quick-view-${product.slug}`}>
            <Eye size={12} /> Quick View
          </button>
          <button onClick={(e) => { e.stopPropagation(); toggleCompare(product); }}
            className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-colors flex items-center gap-1 ${isComparing ? "bg-[#C05621] text-white" : "bg-white/95 backdrop-blur text-[#1A202C] hover:bg-white"}`}
            data-testid={`compare-${product.slug}`}>
            <Scale size={12} />
          </button>
        </div>
      </div>
      <div className="space-y-1">
        <p className="text-[10px] font-medium text-[#C05621] uppercase tracking-wider">{product.brand}</p>
        <h3 className="font-heading text-sm font-semibold text-[#1A202C] group-hover:text-[#C05621] transition-colors line-clamp-2 leading-tight">{product.name}</h3>
        <div className="flex items-baseline gap-1.5">
          <span className="text-base font-bold text-[#1A202C]">${product.price.toFixed(2)}</span>
          {savings > 0 && <span className="text-xs text-gray-400 line-through">${product.original_price.toFixed(2)}</span>}
        </div>
        {product.price >= 50 && (
          <p className="text-[10px] text-gray-600" data-testid={`affirm-teaser-${product.slug}`}>
            {affirmPromo?.monthly
              ? (lang === "es" ? <>{affirmPromo.zero_apr ? "0% APR — desde" : "Desde"} ${affirmPromo.monthly}/mes con Affirm. </> : <>{affirmPromo.zero_apr ? "0% APR — as low as" : "As low as"} ${affirmPromo.monthly}/mo with Affirm. </>)
              : (lang === "es" ? <>Financiamiento flexible disponible. </> : <>Flexible financing available. </>)}
            <button
              onClick={(e) => { e.stopPropagation(); setShowAffirm(true); }}
              className="text-[#0FA0EA] font-semibold underline underline-offset-2 hover:text-[#0C8AC9]"
              data-testid={`affirm-learn-how-${product.slug}`}
            >{lang === "es" ? "Aprende cómo*" : "Learn how*"}</button>
          </p>
        )}
        <p className="text-[10px] text-[#38A169] font-medium flex items-center gap-0.5">
          <Store size={10} /> Pickup & Save 10%: ${pickupPrice.toFixed(2)}
        </p>
        <p className="text-[10px] text-gray-500 flex items-center gap-0.5">
          <Truck size={10} /> <span className="text-[#38A169] font-semibold">FREE</span>&nbsp;Ships in {product.shipping_days_min}-{product.shipping_days_max} days
        </p>
      </div>
    </motion.div>
    {showQuick && <QuickViewModal product={product} onClose={() => setShowQuick(false)} />}
    {showAffirm && <AffirmCalculatorModal initialPrice={product.price} lang={lang} onClose={() => setShowAffirm(false)} getPrequalUrl={affirmPrequalUrl} />}
    </>
  );
}

// ─── HOME PAGE ───
// ─── POPULAR SEARCH TAGS (used on Shop page) ───
function PopularSearchTags({ currentSearch }) {
  const [tags, setTags] = useState([]);
  const { lang } = useTranslation();
  const linkPrefix = lang === "es" ? "/es" : "";
  useEffect(() => { axios.get(`${API}/popular-searches`).then(r => setTags(r.data)).catch(() => {}); }, []);
  if (tags.length === 0) return null;
  return (
    <div className="mb-6" data-testid="popular-search-tags">
      <div className="flex items-center gap-2 mb-3">
        <Search size={14} className="text-gray-400" />
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Popular Searches</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {tags.map(t => {
          const href = t.url || `/shop?search=${encodeURIComponent(t.query)}`;
          const isSearch = href.includes("?search=");
          return (
            <Link key={t.id} to={`${linkPrefix}${href}`} rel={isSearch ? "nofollow" : undefined}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${currentSearch === t.query ? "bg-[#C05621] text-white border-[#C05621]" : "bg-white text-[#2D3748] border-gray-200 hover:border-[#C05621] hover:text-[#C05621]"}`}
              data-testid={`search-tag-${t.query.toLowerCase().replace(/\s+/g, '-')}`}
            >{t.label}</Link>
          );
        })}
      </div>
    </div>
  );
}

// ─── POPULAR SEARCHES SECTION (Homepage) ───
function PopularSearchesSection() {
  const [tags, setTags] = useState([]);
  const [activeGroup, setActiveGroup] = useState("All");
  const { lang } = useTranslation();
  const linkPrefix = lang === "es" ? "/es" : "";
  useEffect(() => { axios.get(`${API}/popular-searches`).then(r => setTags(r.data)).catch(() => {}); }, []);
  if (tags.length === 0) return null;
  const groups = ["All", ...new Set(tags.map(t => t.group))];
  const filteredAll = activeGroup === "All" ? tags : tags.filter(t => t.group === activeGroup);
  // Slimmed: cap the "All" view so the homepage stays clean (full set lives in the footer)
  const filtered = activeGroup === "All" ? filteredAll.slice(0, 24) : filteredAll;
  return (
    <section className="max-w-7xl mx-auto px-4 py-12" data-testid="popular-searches-section">
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#1A202C]">Popular Searches</h2>
        <p className="text-gray-500 mt-2">Find exactly what you're looking for</p>
      </div>
      <div className="flex justify-center gap-2 mb-6 flex-wrap">
        {groups.map(g => (
          <button key={g} onClick={() => setActiveGroup(g)}
            className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${activeGroup === g ? "bg-[#1A202C] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            data-testid={`search-group-${g.toLowerCase().replace(/\s+/g, '-')}`}
          >{g}</button>
        ))}
      </div>
      <div className="flex flex-wrap justify-center gap-2.5">
        {filtered.map(t => {
          const href = t.url || `/shop?search=${encodeURIComponent(t.query)}`;
          const isSearch = href.includes("?search=");
          return (
            <Link key={t.id} to={`${linkPrefix}${href}`} rel={isSearch ? "nofollow" : undefined}
              className="group px-5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-[#2D3748] hover:border-[#C05621] hover:text-[#C05621] hover:shadow-md transition-all"
              data-testid={`home-search-${t.query.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {t.label}
              <ChevronRight size={14} className="inline ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}

// ─── LOCAL SEO SECTION (homepage: Merced County content + Areas We Serve + blog teaser) ───
function LocalSeoSection({ lang }) {
  const [cities, setCities] = useState([]);
  const [posts, setPosts] = useState([]);
  const linkPrefix = lang === "es" ? "/es" : "";
  useEffect(() => {
    axios.get(`${API}/cities`).then(r => setCities(r.data || [])).catch(() => {});
    axios.get(`${API}/blog?limit=3${lang === "es" ? "&lang=es" : ""}`).then(r => setPosts((r.data || []).slice(0, 3))).catch(() => {});
  }, [lang]);
  const es = lang === "es";
  return (
    <section className="bg-[#F7FAFC] border-t border-gray-100" data-testid="local-seo-section">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-10 items-start mb-12">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-[#1A202C] mb-4">
              {es ? "Tu Tienda de Muebles en Los Banos y el Condado de Merced" : "Your Furniture Store in Los Banos & Merced County"}
            </h2>
            <div className="text-sm text-gray-600 leading-relaxed space-y-3">
              {es ? (
                <>
                  <p>La Bodega Bay Furniture es una empresa familiar de Los Banos desde el año 2000. Durante más de 25 años hemos amueblado hogares en todo el <Link to={`${linkPrefix}/furniture/merced-county`} className="text-[#C05621] underline">Condado de Merced</Link> — desde Los Banos y Dos Palos hasta Merced, Atwater y Livingston — con envío gratis en todo el Valle Central y Bay Area.</p>
                  <p>Los veranos del Valle Central superan los 100°F, por eso tenemos telas resistentes a la decoloración, reclinables con ventilación y colchones frescos ideales para el clima local. Con más de 15,000 productos de Ashley, Homelegance y Coaster, y <Link to={`${linkPrefix}/financing`} className="text-[#C05621] underline">financiamiento con $0 de enganche</Link>, amueblar tu hogar nunca fue tan fácil.</p>
                  <p>Visita nuestras tiendas en Los Banos y Newman, o compra en línea con entrega gratis a tu puerta.</p>
                </>
              ) : (
                <>
                  <p>La Bodega Bay Furniture has been family-owned and operated in Los Banos since 2000. For over 25 years we've furnished homes across <Link to={`${linkPrefix}/furniture/merced-county`} className="text-[#C05621] underline">Merced County</Link> — from Los Banos and Dos Palos to Merced, Atwater, and Livingston — with free delivery throughout the Central Valley and Bay Area.</p>
                  <p>Central Valley summers regularly top 100°F, which is why we stock fade-resistant performance fabrics, breathable recliners, and cooling mattresses suited to local homes. With 15,000+ pieces from Ashley, Homelegance, and Coaster, plus <Link to={`${linkPrefix}/financing`} className="text-[#C05621] underline">$0-down financing with no credit needed</Link>, furnishing your home has never been easier.</p>
                  <p>Visit our showrooms in Los Banos and Newman, browse our <Link to={`${linkPrefix}/blog`} className="text-[#C05621] underline">buying guides</Link>, or shop online with free doorstep delivery.</p>
                </>
              )}
            </div>
          </div>
          <div>
            <h3 className="font-heading font-bold text-lg text-[#1A202C] mb-3">
              {es ? "Áreas que Servimos — Condado de Merced y Más Allá" : "Areas We Serve — Merced County & Beyond"}
            </h3>
            <div className="flex flex-wrap gap-2 mb-6" data-testid="areas-we-serve-links">
              {cities.map(c => (
                <Link key={c.slug} to={`${linkPrefix}/furniture/${c.slug}`} className="text-xs px-3 py-1.5 bg-white text-gray-700 rounded-full border border-gray-200 hover:border-[#C05621] hover:text-[#C05621] transition-colors">
                  {es ? "Muebles" : "Furniture"} {c.name}
                </Link>
              ))}
            </div>
            {posts.length > 0 && (
              <div data-testid="home-blog-teaser">
                <h3 className="font-heading font-bold text-lg text-[#1A202C] mb-3">{es ? "De Nuestras Guías de Compra" : "From Our Buying Guides"}</h3>
                <div className="space-y-2">
                  {posts.map(p => (
                    <Link key={p.id} to={`${linkPrefix}/blog/${p.slug || p.id}`} className="block text-sm text-gray-700 hover:text-[#C05621] transition-colors group">
                      <span className="underline decoration-gray-200 group-hover:decoration-[#C05621]">{p.title}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function HomePage() {
  const [homeData, setHomeData] = useState(null);
  const [flashSales, setFlashSales] = useState([]);
  const [trending, setTrending] = useState([]);
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { lang } = useTranslation();

  useEffect(() => {
    if (lang === "es") {
      updatePageSEO({
        title: "Tienda de Muebles en Los Banos, CA | La Bodega Bay Furniture",
        description: "Tienda de muebles familiar en Los Banos, CA desde 2000. Más de 15,000 sofás, camas, comedores y colchones con envío gratis al Valle Central y financiamiento $0 de enganche.",
        canonical: "https://labodegabayfurniture.com/es",
        ogTitle: "La Bodega Bay Furniture | Muebles de Calidad, Envío Gratis",
        ogDescription: "Más de 15,000 muebles de calidad con envío gratis al Valle Central y Bay Area. Financiamiento $0 enganche disponible. Empresa familiar desde 2000.",
        ogType: "website",
      });
    } else {
      updatePageSEO({
        title: "Furniture Store in Los Banos, CA | La Bodega Bay Furniture",
        description: "Family-owned furniture store in Los Banos, CA since 2000. 15,000+ sofas, beds, dining sets & mattresses with free Central Valley delivery and $0 down financing.",
        canonical: "https://labodegabayfurniture.com/",
        ogType: "website",
      });
    }
  }, [lang]);

  useEffect(() => {
    axios.get(`${API}/homepage-data`).then(r => {
      setHomeData(r.data);
      setFlashSales(r.data.flash_sales || []);
    }).catch(() => {});
    // Trending: pass recently viewed categories for personalization
    const rv = JSON.parse(localStorage.getItem("lbb_recently_viewed") || "[]");
    const rvCats = [...new Set(rv.map(p => p.category).filter(Boolean))].join(",");
    axios.get(`${API}/products/trending?recently_viewed=${encodeURIComponent(rvCats)}&limit=10`).then(r => setTrending(r.data)).catch(() => {});
    // Load homepage structured data for SEO
    axios.get(`${API}/homepage-schema`).then(r => {
      const schemas = r.data;
      const existing = document.getElementById("homepage-schema");
      if (existing) existing.remove();
      const script = document.createElement("script");
      script.id = "homepage-schema";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify([schemas.siteNavigation, schemas.organization, schemas.breadcrumb]);
      document.head.appendChild(script);
    }).catch(() => {});
  }, []);

  const cats = homeData?.category_heroes || {};
  const newArr = homeData?.new_arrivals || [];
  const hero = homeData?.hero;

  return (
    <div className="bg-[#FDFBF7]">
      {/* ═══ HERO ═══ */}
      <section className="relative bg-[#1A202C] overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-24 flex flex-col md:flex-row items-center gap-8">
          <div className="flex-1 text-center md:text-left z-10">
            <span className="inline-flex items-center gap-2 bg-[#C05621] text-white text-sm font-bold px-5 py-2.5 rounded-full mb-6 uppercase tracking-wider shadow-lg"><Award size={18} /> {lang === "es" ? "Empresa Familiar desde 2000" : "Family-Owned Since 2000"}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-4" data-testid="hero-h1">
              <span className="block text-sm md:text-base font-semibold tracking-[0.2em] uppercase text-[#F6AD55] mb-3">{lang === "es" ? "Tienda de Muebles en Los Banos" : "Los Banos Furniture Store"}</span>
              {lang === "es" ? <>Muebles que<br /><span className="text-[#DD6B20]">se Sienten Como Hogar</span></> : <>Furniture That<br /><span className="text-[#DD6B20]">Feels Like Home</span></>}
            </h1>
            <p className="text-lg text-gray-300 mb-8 max-w-lg">{lang === "es" ? "Más de 15,000 muebles de las mejores marcas. Envío gratis. Financiamiento $0 enganche. Sirviendo al Valle Central y Bay Area desde hace 25 años." : "Over 15,000 pieces from top brands. Free delivery. $0 down financing. Serving the Central Valley & Bay Area for 25 years."}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
              <Link to={lang === "es" ? "/es/categories" : "/categories"} className="px-8 py-4 bg-[#C05621] text-white font-bold rounded-xl hover:bg-[#DD6B20] transition-colors text-lg">{lang === "es" ? "Comprar ahora" : "Shop Now"}</Link>
              <Link to={lang === "es" ? "/es/category/Living Room" : "/category/Living Room"} className="px-8 py-4 border-2 border-white/30 text-white font-bold rounded-xl hover:bg-white/10 transition-colors text-lg">{lang === "es" ? "Ver Sala" : "Explore Living Room"}</Link>
            </div>
          </div>
          {hero?.images?.[0]?.url && <div className="flex-1 max-w-lg"><img src={getDetailImage(hero.images[0].url)} onError={imgFallback} alt={hero.name} className="w-full rounded-2xl shadow-2xl" loading="eager" /></div>}
        </div>
      </section>

      {/* ═══ TRUST BAR ═══ */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 py-5 grid grid-cols-2 md:grid-cols-5 gap-4">
          {(lang === "es" ? [
            { icon: <Award size={22} />, title: "Desde 2000", desc: "Empresa familiar 25 años" },
            { icon: <Truck size={22} />, title: "Envío Gratis", desc: "Valle Central y Bay Area" },
            { icon: <CreditCard size={22} />, title: "$0 Enganche", desc: "Sin crédito necesario" },
            { icon: <Shield size={22} />, title: "Garantía de Precio", desc: "Garantía + 5%" },
            { icon: <Store size={22} />, title: "Recoge y Ahorra", desc: "10% de descuento Los Banos" },
          ] : [
            { icon: <Award size={22} />, title: "Since 2000", desc: "Family-owned 25 years" },
            { icon: <Truck size={22} />, title: "Free Delivery", desc: "Central Valley & Bay Area" },
            { icon: <CreditCard size={22} />, title: "$0 Down", desc: "No credit needed" },
            { icon: <Shield size={22} />, title: "Price Match", desc: "Guarantee + 5%" },
            { icon: <Store size={22} />, title: "Pickup & Save", desc: "10% off Los Banos" },
          ]).map((b, i) => (
            <div key={i} className="flex items-center gap-3 py-1">
              <div className="text-[#C05621]">{b.icon}</div>
              <div><p className="text-xs font-bold text-[#1A202C]">{b.title}</p><p className="text-[10px] text-gray-500">{b.desc}</p></div>
            </div>
          ))}
        </div>
      </section>


      {/* ═══ POPULAR SEARCHES ═══ */}
      <PopularSearchesSection />

      {/* ═══ SHOP BY CATEGORY ═══ */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#1A202C]">{lang === "es" ? "Comprar por habitación" : "Shop by Room"}</h2>
          <p className="text-gray-500 mt-2">Find the perfect piece for every space</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { name: "Living Room", link: "/category/Living Room" },
            { name: "Bedroom", link: "/category/Bedroom" },
            { name: "Dining Room", link: "/category/Dining Room" },
            { name: "Mattress", link: "/category/Mattress" },
            { name: "Home Office", link: "/category/Home Office" },
            { name: "Outdoor", link: "/category/Outdoor" },
            { name: "Accents", link: "/category/Accents" },
            { name: "All Products", link: "/shop" },
          ].map((cat, i) => {
            const hero = cats[cat.name];
            return (
              <Link key={i} to={cat.link} className={`group relative overflow-hidden rounded-2xl ${i < 2 ? "col-span-2 h-72" : "h-56"} bg-[#2D3748]`}>
                {hero?.img && <img src={hero.img} alt={cat.name} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" loading="lazy" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="text-white font-heading font-bold text-xl">{cat.name}</h3>
                {hero?.from_price && <p className="text-white/70 text-xs mt-0.5">From ${Math.floor(hero.from_price)}</p>}
                  <span className="inline-block mt-2 text-white/90 text-xs font-bold border-b border-white/50 group-hover:border-white transition-colors">Shop Now <ChevronRight size={12} className="inline" /></span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ═══ FEATURED PRODUCTS ═══ */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div><h2 className="text-3xl font-heading font-bold text-[#1A202C]">{lang === "es" ? "Tendencias" : "Trending Now"}</h2><p className="text-gray-500 text-sm mt-1">{lang === "es" ? "Nuestros más populares" : "Our most popular picks"}</p></div>
            <Link to={lang === "es" ? "/es/shop" : "/shop"} className="text-[#C05621] font-bold text-sm hover:underline">{lang === "es" ? "Ver todo" : "View All"} <ChevronRight size={14} className="inline" /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {trending.slice(0, 10).map((p, i) => (
              <Link key={i} to={`${lang === "es" ? "/es" : ""}/product/${p.slug}`} className="group bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-square bg-gray-50 overflow-hidden">
                  <img src={getCardImage(p.images?.[0]?.url)} onError={imgFallback} alt={p.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                </div>
                <div className="p-3">
                  <p className="text-[10px] text-gray-400 uppercase">{p.brand}</p>
                  <p className="text-xs font-bold text-[#1A202C] line-clamp-2 mt-0.5">{p.name}</p>
                  <p className="text-sm font-bold text-[#C05621] mt-1">${p.price?.toFixed(2)}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FINANCING BANNER ═══ */}
      <section className="bg-[#1A202C] py-14" data-testid="financing-banner">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-2">{lang === "es" ? "Opciones de Financiamiento Flexibles" : "Flexible Financing Options"}</h2>
          <p className="text-gray-300 text-lg mb-10">{lang === "es" ? "Llévatelo a casa hoy — paga a plazos. Sin crédito necesario." : "Take it home today — pay over time. No credit needed."}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <a href="https://snapfinance.com/apply" target="_blank" rel="noreferrer" onClick={async (e) => {
              e.preventDefault();
              try {
                const r = await axios.get(`${API}/payments/snap/apply-url`);
                window.open(r.data?.url || "https://snapfinance.com/apply", "_blank", "noopener,noreferrer");
              } catch {
                window.open("https://snapfinance.com/apply", "_blank", "noopener,noreferrer");
              }
            }} className="bg-gradient-to-br from-[#38A169]/15 to-[#276749]/15 rounded-2xl p-10 text-center border border-[#38A169]/30 hover:border-[#38A169] transition-colors group" data-testid="snap-finance-card">
              <div className="mb-6">
                <span className="text-[#4ADE80] text-3xl font-black tracking-tight">snap</span>
                <span className="text-white text-3xl font-light tracking-tight ml-1">finance</span>
              </div>
              <p className="text-white font-bold text-2xl mb-2">{lang === "es" ? "$0 Enganche" : "$0 Down"}</p>
              <p className="text-gray-300">{lang === "es" ? "Sin crédito necesario. Solicita en 30 segundos." : "No credit needed. Apply in 30 seconds."}</p>
              <p className="text-gray-400 text-sm mt-2">{lang === "es" ? "100 días igual que en efectivo" : "100 days same as cash"}</p>
              <span className="inline-block mt-4 px-6 py-2.5 bg-[#38A169] text-white font-bold rounded-xl group-hover:bg-[#2F855A] transition-colors">{lang === "es" ? "Solicitar ahora" : "Apply Now"}</span>
            </a>
            <a href={affirmPrequalUrl()} target="_blank" rel="noreferrer" className="bg-gradient-to-br from-[#4C1D95]/15 to-[#7C3AED]/15 rounded-2xl p-10 text-center border border-[#7C3AED]/30 hover:border-[#7C3AED] transition-colors group" data-testid="affirm-card">
              <div className="mb-6">
                <span className="text-[#A78BFA] text-3xl font-black tracking-tight">affirm</span>
              </div>
              <p className="text-white font-bold text-2xl mb-2">{lang === "es" ? "Paga Mensual" : "Pay Monthly"}</p>
              <p className="text-gray-300">{lang === "es" ? "0% APR disponible para clientes que califican. Sin cargos ocultos." : "0% APR available for qualified customers. No hidden fees."}</p>
              <p className="text-gray-400 text-sm mt-2">{lang === "es" ? "Verifica tu tasa — sin afectar tu crédito" : "Check rate — no impact to credit score"}</p>
              <span className="inline-block mt-4 px-6 py-2.5 bg-[#7C3AED] text-white font-bold rounded-xl group-hover:bg-[#6D28D9] transition-colors">{lang === "es" ? "Pre-calificar" : "Pre-Qualify"}</span>
            </a>
          </div>
        </div>
      </section>

      {/* ═══ NEW ARRIVALS ═══ */}
      {newArr.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex items-center justify-between mb-8">
            <div><h2 className="text-3xl font-heading font-bold text-[#1A202C]">{lang === "es" ? "Nuevas llegadas" : "New Arrivals"}</h2><p className="text-gray-500 text-sm mt-1">{lang === "es" ? "Lo más nuevo recién llegado" : "Fresh finds just added"}</p></div>
            <Link to={lang === "es" ? "/es/shop?sort=newest" : "/shop?sort=newest"} className="text-[#C05621] font-bold text-sm hover:underline">{lang === "es" ? "Ver todo lo nuevo" : "See All New"} <ChevronRight size={14} className="inline" /></Link>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {newArr.map((p, i) => (
              <Link key={i} to={`${lang === "es" ? "/es" : ""}/product/${p.slug}`} className="flex-shrink-0 w-48 group">
                <div className="aspect-square bg-gray-50 rounded-xl overflow-hidden mb-2">
                  <img src={getCardImage(p.images?.[0]?.url)} onError={imgFallback} alt={p.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" loading="lazy" />
                </div>
                <p className="text-[10px] text-[#C05621] font-bold">{p.brand}</p>
                <p className="text-xs font-bold text-[#1A202C] line-clamp-2">{p.name}</p>
                <p className="text-sm font-bold text-[#C05621]">${p.price?.toFixed(2)}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ═══ SHOP BY BRAND ═══ */}
      <section className="bg-white py-12 border-t border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-heading font-bold text-center text-[#1A202C] mb-8">{lang === "es" ? "Comprar por marca" : "Shop by Brand"}</h2>
          <div className="flex flex-wrap justify-center gap-6 md:gap-12 items-center opacity-70">
            {["Ashley", "Homelegance", "Coaster"].map(brand => (
              <Link key={brand} to={`/brand/${brand.toLowerCase()}`} className="hover:opacity-100 transition-opacity" data-testid={`home-brand-${brand.toLowerCase()}`}>
                <p className="text-xl md:text-2xl font-heading font-bold text-[#2D3748]">{brand}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TRUST SECTION ═══ */}
      <section className="max-w-7xl mx-auto px-4 py-16 text-center">
        <h2 className="text-3xl font-heading font-bold text-[#1A202C] mb-2">Why Families Choose Us</h2>
        <p className="text-gray-500 mb-10">Proudly serving the Central Valley & Bay Area since 2000</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { num: "25+", label: "Years in Business" },
            { num: "15K+", label: "Products Available" },
            { num: "4.9★", label: "Google Rating" },
            { num: "2", label: "Store Locations" },
          ].map((s, i) => (
            <div key={i}>
              <p className="text-4xl font-heading font-bold text-[#C05621]">{s.num}</p>
              <p className="text-sm text-gray-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <LocalSeoSection lang={lang} />

      <RecentlyViewedCarousel />
    </div>
  );
}
// ─── DEAL OF THE WEEK ───
function DealOfWeek({ deal }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    const getNextSunday = () => {
      const now = new Date();
      const day = now.getDay();
      const daysUntilSunday = day === 0 ? 0 : 7 - day;
      const nextSunday = new Date(now);
      nextSunday.setDate(now.getDate() + daysUntilSunday);
      nextSunday.setHours(23, 59, 59, 999);
      return nextSunday;
    };

    const timer = setInterval(() => {
      const now = new Date();
      const end = getNextSunday();
      const diff = end - now;
      if (diff <= 0) return;
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        mins: Math.floor((diff / (1000 * 60)) % 60),
        secs: Math.floor((diff / 1000) % 60),
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="py-16 md:py-24 bg-[#1A202C] relative overflow-hidden" data-testid="deal-of-week-section">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#E53E3E]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#DD6B20]/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Prominent header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 bg-[#E53E3E] text-white text-sm font-bold px-5 py-2 rounded-full uppercase tracking-widest mb-4 animate-pulse">
            <Timer size={18} /> Deal of the Week
          </span>
          <h2 className="font-heading text-4xl md:text-6xl font-bold text-white">
            EXTRA <span className="text-[#DD6B20]">{deal.deal_discount_pct}% OFF</span>
          </h2>
          <p className="text-gray-400 mt-2 text-lg">Limited time only — offer ends Sunday night!</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
            <img loading="lazy" decoding="async" src={deal.images?.[0]?.url} alt={deal.name} className="w-full h-full object-contain" />
            <div className="absolute top-4 left-4 bg-[#E53E3E] text-white font-black px-5 py-3 rounded-xl text-lg shadow-lg">
              EXTRA {deal.deal_discount_pct}% OFF
            </div>
          </div>
          <div className="text-white">
            <p className="text-sm font-medium text-[#DD6B20] uppercase tracking-wider mb-2">{deal.brand}</p>
            <h3 className="font-heading text-3xl md:text-5xl font-bold mb-4">{deal.name}</h3>
            <p className="text-gray-300 leading-relaxed mb-6">{deal.short_description || deal.description?.substring(0, 150)}...</p>
            <div className="flex items-baseline gap-3 mb-2">
              <span className="text-5xl font-black text-[#DD6B20]">${deal.deal_price?.toFixed(2) || deal.price.toFixed(2)}</span>
              <span className="text-xl text-gray-500 line-through">${deal.original_price?.toFixed(2)}</span>
            </div>
            <p className="text-[#38A169] font-bold text-sm mb-6">You save ${((deal.original_price || 0) - (deal.deal_price || deal.price)).toFixed(2)}!</p>

            <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-3">Offer expires in:</p>
            <div className="flex gap-3 mb-8">
              {[
                { val: timeLeft.days, label: "Days" },
                { val: timeLeft.hours, label: "Hours" },
                { val: timeLeft.mins, label: "Mins" },
                { val: timeLeft.secs, label: "Secs" },
              ].map((t, i) => (
                <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 text-center min-w-[75px] border border-white/10">
                  <span className="text-3xl font-black block">{String(t.val).padStart(2, "0")}</span>
                  <span className="text-[10px] text-gray-400 uppercase font-semibold">{t.label}</span>
                </div>
              ))}
            </div>
            <button onClick={() => navigate(`/product/${deal.slug}`)} className="px-10 py-4 bg-[#DD6B20] text-white rounded-xl font-bold text-lg hover:bg-[#C05621] transition-all shadow-xl hover:shadow-2xl" data-testid="deal-shop-btn">
              Shop This Deal <ArrowRight size={20} className="inline ml-2" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── SHOP PAGE ───
// ─── SHOP FILTER BAR (Living Spaces-style sticky chips + quick dropdowns) ───
const CATEGORY_ORDER = ["Living Room", "Bedroom", "Dining Room", "Mattress", "Home Office", "Accents", "Outdoor", "Kids"];

const PRICE_BUCKETS = [
  { label: "Under $250", label_es: "Menos de $250", min: "", max: "250" },
  { label: "$250 – $500", label_es: "$250 – $500", min: "250", max: "500" },
  { label: "$500 – $1,000", label_es: "$500 – $1,000", min: "500", max: "1000" },
  { label: "$1,000 – $2,000", label_es: "$1,000 – $2,000", min: "1000", max: "2000" },
  { label: "$2,000+", label_es: "Más de $2,000", min: "2000", max: "" },
];

const COLOR_SWATCHES = { Brown: "#8B4513", Gray: "#808080", Black: "#1A1A1A", Beige: "#D4C5A9", White: "#F5F5F5", Blue: "#4169E1", Green: "#228B22", Red: "#B22222", Orange: "#D2691E", Yellow: "#DAA520", Pink: "#DB7093", Purple: "#6A0DAD" };

const sortSubcats = (list) => {
  const sizeOrder = { "Twin": 1, "Full": 2, "Queen": 3, "King": 4, "Cal King": 5, "Eastern King": 4 };
  const pieceOrder = { "3-Piece": 3, "4-Piece": 4, "5-Piece": 5, "6-Piece": 6, "7-Piece": 7, "8-Piece": 8, "9-Piece": 9, "10-Piece": 10 };
  const getSize = (s) => { for (const [k, v] of Object.entries(sizeOrder)) { if (s.startsWith(k)) return v; } return 50; };
  const getPiece = (s) => { for (const [k, v] of Object.entries(pieceOrder)) { if (s.startsWith(k)) return v; } return 50; };
  return [...list].sort((a, b) => {
    const aSize = getSize(a); const bSize = getSize(b);
    if (aSize !== 50 || bSize !== 50) return aSize - bSize;
    const aPiece = getPiece(a); const bPiece = getPiece(b);
    if (aPiece !== 50 || bPiece !== 50) return aPiece - bPiece;
    return a.localeCompare(b);
  });
};

function ActiveChip({ label, onRemove, testid }) {
  return (
    <span className="inline-flex items-center gap-1 bg-[#2D3748] text-white text-[11px] font-medium pl-2.5 pr-1 py-1 rounded-full" data-testid={testid}>
      {label}
      <button onClick={onRemove} className="w-4 h-4 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center transition-colors" aria-label={`Remove ${label}`}><X size={10} /></button>
    </span>
  );
}

function ShopFilterBar({ filters, category, subcategory, color, features, minPrice, maxPrice, sort, showCategories, selectCategory, updateParam, toggleFeature, setPriceBucket, clearQuickFilters, onOpenFullFilters }) {
  const [open, setOpen] = useState(null);
  const barRef = useRef(null);
  const { lang } = useTranslation();

  useEffect(() => {
    const close = (e) => { if (barRef.current && !barRef.current.contains(e.target)) setOpen(null); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const cats = [...new Set((filters?.categories || []).filter(Boolean))].sort((a, b) => {
    const ia = CATEGORY_ORDER.indexOf(a), ib = CATEGORY_ORDER.indexOf(b);
    return ((ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)) || a.localeCompare(b);
  });
  const subcats = category && filters?.subcategories ? sortSubcats(filters.subcategories.filter(Boolean)) : [];
  const activeFeatures = features ? features.split(",").filter(Boolean) : [];
  const activeBucket = PRICE_BUCKETS.find(b => b.min === minPrice && b.max === maxPrice && (b.min || b.max) && (minPrice || maxPrice));
  const hasActive = !!(color || activeBucket || activeFeatures.length > 0);
  const quickFeatures = [...(filters?.pieces || []), ...(filters?.features || [])];

  const pillCls = (isOpen, isActive) => `flex items-center gap-1 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors whitespace-nowrap flex-shrink-0 ${isActive ? "bg-[#2D3748] text-white border-[#2D3748]" : isOpen ? "border-[#C05621] text-[#C05621] bg-orange-50" : "border-gray-300 text-gray-700 bg-white hover:border-gray-400"}`;

  return (
    <div ref={barRef} className="sticky top-16 md:top-20 z-40 -mx-4 px-4 bg-white/95 backdrop-blur-md border-b border-gray-100 pt-2.5 pb-2 mb-6" data-testid="shop-filter-bar">
      {/* Row 1: main categories — always visible */}
      {showCategories && cats.length > 0 && (
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2" data-testid="category-chip-row">
          {cats.map(c => (
            <button key={c} onClick={() => { selectCategory(c); setOpen(null); }}
              className={`px-3.5 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap border transition-colors flex-shrink-0 ${category === c ? "bg-[#C05621] text-white border-[#C05621]" : "bg-white border-gray-300 text-gray-700 hover:border-[#C05621] hover:text-[#C05621]"}`}
              data-testid={`cat-chip-${c.toLowerCase().replace(/\s/g, "-")}`}>
              {c}
            </button>
          ))}
        </div>
      )}
      {/* Row 2: subcategories for the selected category */}
      {showCategories && subcats.length > 0 && (
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2" data-testid="subcategory-chip-row">
          {subcats.map(s => (
            <button key={s} onClick={() => { updateParam("subcategory", subcategory === s ? "" : s); setOpen(null); }}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap border transition-colors flex-shrink-0 ${subcategory === s ? "bg-[#2D3748] text-white border-[#2D3748]" : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-400"}`}
              data-testid={`subcat-chip-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {s}
            </button>
          ))}
        </div>
      )}
      {/* Row 3: quick filter dropdowns + sort */}
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide" data-testid="quick-filter-row">
        <button onClick={() => { onOpenFullFilters(); setOpen(null); }} className={pillCls(false, false)} data-testid="filter-toggle">
          <SlidersHorizontal size={13} /> {lang === "es" ? "Filtros" : "Filters"}
        </button>
        {filters?.colors?.length > 0 && (
          <button onClick={() => setOpen(open === "color" ? null : "color")} className={pillCls(open === "color", !!color)} data-testid="quick-color-btn">
            {color || "Color"} <ChevronDown size={13} className={`transition-transform ${open === "color" ? "rotate-180" : ""}`} />
          </button>
        )}
        <button onClick={() => setOpen(open === "price" ? null : "price")} className={pillCls(open === "price", !!activeBucket)} data-testid="quick-price-btn">
          {activeBucket ? (lang === "es" ? activeBucket.label_es : activeBucket.label) : (lang === "es" ? "Precio" : "Price")} <ChevronDown size={13} className={`transition-transform ${open === "price" ? "rotate-180" : ""}`} />
        </button>
        {quickFeatures.length > 0 && (
          <button onClick={() => setOpen(open === "features" ? null : "features")} className={pillCls(open === "features", activeFeatures.length > 0)} data-testid="quick-features-btn">
            {lang === "es" ? "Características" : "Features"}{activeFeatures.length > 0 ? ` (${activeFeatures.length})` : ""} <ChevronDown size={13} className={`transition-transform ${open === "features" ? "rotate-180" : ""}`} />
          </button>
        )}
        <select value={sort} onChange={(e) => updateParam("sort", e.target.value)} className="ml-auto px-3 py-1.5 border border-gray-300 rounded-full text-xs font-semibold bg-white text-gray-700 flex-shrink-0" data-testid="sort-select">
          <option value="newest">{lang === "es" ? "Más Nuevo" : "Newest"}</option>
          <option value="price_asc">{lang === "es" ? "Precio: Menor a Mayor" : "Price: Low to High"}</option>
          <option value="price_desc">{lang === "es" ? "Precio: Mayor a Menor" : "Price: High to Low"}</option>
          <option value="name_asc">{lang === "es" ? "Nombre: A-Z" : "Name: A-Z"}</option>
          <option value="popular">Popular</option>
        </select>
      </div>
      {/* Active filter chips */}
      {hasActive && (
        <div className="flex items-center gap-2 flex-wrap mt-2" data-testid="active-filter-chips">
          {color && <ActiveChip label={color} onRemove={() => updateParam("color", "")} testid="active-chip-color" />}
          {activeBucket && <ActiveChip label={lang === "es" ? activeBucket.label_es : activeBucket.label} onRemove={() => setPriceBucket(null)} testid="active-chip-price" />}
          {activeFeatures.map(f => (
            <ActiveChip key={f} label={f} onRemove={() => toggleFeature(f)} testid={`active-chip-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
          ))}
          <button onClick={() => { clearQuickFilters(); setOpen(null); }} className="text-xs text-[#C05621] font-semibold hover:underline" data-testid="clear-quick-filters">
            {lang === "es" ? "Borrar todo" : "Clear all"}
          </button>
        </div>
      )}
      {/* Dropdown panel */}
      {open && (
        <div className="absolute left-4 right-4 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl p-4 max-h-[55vh] overflow-y-auto z-50" data-testid={`quick-dropdown-${open}`}>
          {open === "color" && (
            <div className="flex flex-wrap gap-2">
              {filters.colors.map(c => {
                const isActive = color === c.name;
                return (
                  <button key={c.name} onClick={() => { updateParam("color", isActive ? "" : c.name); setOpen(null); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${isActive ? "bg-[#2D3748] text-white border-[#2D3748]" : "border-gray-200 text-gray-700 hover:border-gray-400"}`}
                    data-testid={`quick-color-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <span className="w-3.5 h-3.5 rounded-full border border-gray-300 flex-shrink-0" style={{ backgroundColor: COLOR_SWATCHES[c.name] || "#ccc" }} />
                    {c.name} <span className="opacity-50">({c.count})</span>
                  </button>
                );
              })}
            </div>
          )}
          {open === "price" && (
            <div className="space-y-1">
              {PRICE_BUCKETS.map(b => {
                const isActive = activeBucket === b;
                return (
                  <button key={b.label} onClick={() => { setPriceBucket(isActive ? null : b); setOpen(null); }}
                    className={`w-full flex items-center justify-between text-left text-sm px-3 py-2 rounded-lg transition-colors ${isActive ? "bg-[#2D3748] text-white" : "hover:bg-gray-50 text-gray-700"}`}
                    data-testid={`quick-price-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}>
                    {lang === "es" ? b.label_es : b.label}
                    {isActive && <Check size={15} />}
                  </button>
                );
              })}
            </div>
          )}
          {open === "features" && (
            <div>
              {filters?.pieces?.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1.5">{lang === "es" ? "Número de Piezas" : "Number of Pieces"}</p>
                  <div className="flex flex-wrap gap-2">
                    {filters.pieces.map(f => {
                      const isActive = activeFeatures.includes(f.name);
                      return (
                        <button key={f.name} onClick={() => toggleFeature(f.name)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${isActive ? "bg-[#2D3748] text-white border-[#2D3748]" : "border-gray-200 text-gray-700 hover:border-gray-400"}`}
                          data-testid={`quick-feature-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          {f.name} <span className="opacity-50">({f.count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {filters?.features?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {filters.features.map(f => {
                    const isActive = activeFeatures.includes(f.name);
                    return (
                      <button key={f.name} onClick={() => toggleFeature(f.name)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${isActive ? "bg-[#2D3748] text-white border-[#2D3748]" : "border-gray-200 text-gray-700 hover:border-gray-400"}`}
                        data-testid={`quick-feature-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {f.name} <span className="opacity-50">({f.count})</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <button onClick={() => setOpen(null)} className="mt-3 w-full py-2 bg-[#C05621] text-white rounded-lg text-sm font-bold hover:bg-[#DD6B20] transition-colors" data-testid="quick-features-done">
                {lang === "es" ? "Listo" : "Done"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const navigationType = useNavigationType();
  const { featureSlug, brandSlug, categorySlug } = useParams();
  const navigate = useNavigate();
  const [landing, setLanding] = useState(null);
  const isLanding = !!(featureSlug || brandSlug);

  const category = searchParams.get("category") || "";
  const brand = searchParams.get("brand") || "";
  const subcategory = searchParams.get("subcategory") || "";
  const color = searchParams.get("color") || "";
  const search = searchParams.get("search") || "";
  const collectionId = searchParams.get("collection_id") || "";
  const newArrivals = searchParams.get("new_arrivals") === "true";
  const features = searchParams.get("features") || "";
  const minPrice = searchParams.get("min_price") || "";
  const maxPrice = searchParams.get("max_price") || "";
  const sort = searchParams.get("sort") || (search ? "relevance" : "price_asc");

  // SEO landing page config (/shop/<feature-slug> or /brand/<brand>[/<category>])
  useEffect(() => {
    if (!featureSlug && !brandSlug) { setLanding(null); return; }
    setLanding(null);
    const url = featureSlug
      ? `${API}/feature-pages/${featureSlug}`
      : `${API}/brand-pages/${brandSlug}${categorySlug ? `/${categorySlug}` : ""}`;
    axios.get(url)
      .then(r => {
        setLanding(r.data);
        const canonicalPath = r.data.path || `/shop/${featureSlug}`;
        updatePageSEO({ title: r.data.title, description: r.data.description, canonical: `https://labodegabayfurniture.com${canonicalPath}` });
      })
      .catch(() => navigate("/shop", { replace: true }));
  }, [featureSlug, brandSlug, categorySlug, navigate]);

  // Effective filters — landing pages apply their base brand/category/features, URL params refine within them
  const effCategory = isLanding ? (landing?.category || category) : category;
  const effSubcategory = isLanding ? (landing?.subcategory || subcategory) : subcategory;
  const effBrand = isLanding ? (landing?.brand || brand) : brand;
  const effFeatures = isLanding
    ? [...(landing?.features || []), ...(features ? features.split(",") : [])].filter((v, i, a) => a.indexOf(v) === i).join(",")
    : features;

  // Cache key for scroll restoration
  const cacheKey = `shop_${featureSlug || ""}_${brandSlug || ""}_${categorySlug || ""}_${searchParams.toString()}`;
  const restoringScrollRef = useRef(false);
  // Capture the saved scroll target at RENDER time — before any effect or stray
  // scroll event (height collapse during nav, StrictMode double-effects) can
  // overwrite the sessionStorage value we need to restore to.
  const savedScrollRef = useRef({ key: null, y: 0 });
  if (savedScrollRef.current.key !== cacheKey) {
    savedScrollRef.current = { key: cacheKey, y: parseInt(sessionStorage.getItem(cacheKey + '_y') || '0') };
  }

  // Save scroll position continuously (debounced via passive listener)
  useEffect(() => {
    let ticking = false;
    const saveScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          sessionStorage.setItem(cacheKey + '_y', String(window.scrollY));
          ticking = false;
        });
      }
    };
    window.addEventListener('scroll', saveScroll, { passive: true });
    return () => window.removeEventListener('scroll', saveScroll);
  }, [cacheKey]);

  // Save products + page to sessionStorage when they change
  useEffect(() => {
    if (products.length > 0) {
      const payload = JSON.stringify({ products, page, total, hasMore });
      try {
        sessionStorage.setItem(cacheKey + '_data', payload);
      } catch {
        // Quota exceeded — purge older shop caches and retry once so back-navigation restore keeps working
        try {
          Object.keys(sessionStorage)
            .filter(k => k.startsWith('shop_') && k.endsWith('_data') && k !== cacheKey + '_data')
            .forEach(k => sessionStorage.removeItem(k));
          sessionStorage.setItem(cacheKey + '_data', payload);
        } catch { /* still full — give up silently */ }
      }
    }
  }, [products, page, total, hasMore, cacheKey]);

  useEffect(() => {
    if (isLanding) return; // landing pages set their own SEO meta
    const cat = category || search || "Shop All";
    const title = `${cat} Furniture | La Bodega Bay Furniture — Free Delivery`;
    const desc = category
      ? `Shop ${category} furniture at La Bodega Bay. Free delivery Central Valley & Bay Area. $0 down financing. 15,000+ pieces from Ashley, Homelegance & Coaster.`
      : search
        ? `Search results for "${search}" at La Bodega Bay Furniture. Free delivery & easy financing.`
        : "Shop 15,000+ pieces of furniture with free delivery. Living room, bedroom, dining, mattresses. Family-owned 25+ years.";
    updatePageSEO({ title, description: desc, canonical: `https://labodegabayfurniture.com/shop${category ? '?category=' + encodeURIComponent(category) : ''}` });
  }, [category, search]);

  const fetchProducts = useCallback(async (pageNum = 1, append = false) => {
    if (isLanding && !landing) return; // wait for landing config
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      if (effCategory) params.set("category", effCategory);
      if (effBrand) params.set("brand", effBrand);
      if (effSubcategory) params.set("subcategory", effSubcategory);
      if (collectionId) params.set("collection_id", collectionId);
      if (color) params.set("color", color);
      if (effFeatures) params.set("features", effFeatures);
      if (search) params.set("search", search);
      if (newArrivals) params.set("new_arrivals", "true");
      if (minPrice) params.set("min_price", minPrice);
      if (maxPrice) params.set("max_price", maxPrice);
      if (sort) params.set("sort", sort);
      params.set("page", pageNum);
      params.set("limit", "50");
      params.set("compact", "true");

      const r = await axios.get(`${API}/products?${params}`);
      if (append) {
        setProducts(prev => {
          // Dedupe by id — overlapping page fetches (scroll race) caused duplicate React keys
          const seen = new Set(prev.map(p => p.id));
          return [...prev, ...r.data.products.filter(p => !seen.has(p.id))];
        });
      } else {
        setProducts(r.data.products);
      }
      setTotal(r.data.total);
      setHasMore(pageNum < r.data.pages);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
    setLoadingMore(false);
  }, [effCategory, effBrand, effSubcategory, collectionId, color, effFeatures, search, newArrivals, minPrice, maxPrice, sort, isLanding, landing]);

  // Reset and fetch on filter change — restore from cache on back navigation
  useEffect(() => {
    // Progressive scroll restore: retries while page grows (lazy images / infinite scroll).
    // behavior:'instant' bypasses the global CSS scroll-behavior:smooth — restarting a
    // smooth animation every retry crawls and never reaches the target.
    // Restore the shopper's spot. Preferred: anchor to the exact card they clicked
    // (deterministic, immune to page-height drift). Fallback: raw Y with retries.
    const restoreScroll = () => {
      const anchor = sessionStorage.getItem("lbb_scroll_anchor") || "";
      const savedY = savedScrollRef.current.y;
      if (!anchor && savedY <= 0) return;
      restoringScrollRef.current = true;
      let attempts = 0;
      const tryScroll = () => {
        attempts++;
        const el = anchor ? document.querySelector(`[data-testid="product-card-${anchor}"]`) : null;
        if (el) {
          el.scrollIntoView({ block: "center", behavior: "instant" });
          sessionStorage.removeItem("lbb_scroll_anchor");
          sessionStorage.setItem(cacheKey + '_y', String(window.scrollY));
          setTimeout(() => { restoringScrollRef.current = false; }, 200);
          return;
        }
        if (!anchor && savedY > 0) {
          window.scrollTo({ top: savedY, left: 0, behavior: 'instant' });
          if (Math.abs(window.scrollY - savedY) <= 100) {
            sessionStorage.setItem(cacheKey + '_y', String(window.scrollY));
            setTimeout(() => { restoringScrollRef.current = false; }, 200);
            return;
          }
        } else if (anchor && savedY > 0) {
          // Anchor card not rendered yet — scroll toward savedY to trigger infinite scroll
          window.scrollTo({ top: savedY, left: 0, behavior: 'instant' });
        }
        if (attempts < 60) {
          setTimeout(tryScroll, 150);
        } else {
          setTimeout(() => { restoringScrollRef.current = false; }, 200);
        }
      };
      requestAnimationFrame(tryScroll);
    };

    if (navigationType === 'POP') {
      try {
        const cached = sessionStorage.getItem(cacheKey + '_data');
        if (cached) {
          const { products: cp, page: cpage, total: ctotal, hasMore: cmore } = JSON.parse(cached);
          setProducts(cp);
          setPage(cpage);
          setTotal(ctotal);
          setHasMore(cmore);
          setLoading(false);
          restoreScroll();
          return;
        }
      } catch { /* parse error — fall through to fresh fetch */ }
    }
    setPage(1);
    const fetched = fetchProducts(1, false);
    // Cache was missing/evicted but we still know the scroll spot — restore it after
    // the fresh fetch (scrolling down triggers infinite scroll to reload more pages)
    if (navigationType === 'POP' && fetched && typeof fetched.then === 'function') {
      fetched.then(restoreScroll);
    }
  }, [fetchProducts, navigationType, cacheKey]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 2500) {
        setPage(prev => {
          const next = prev + 1;
          fetchProducts(next, true);
          return next;
        });
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadingMore, hasMore, fetchProducts]);

  useEffect(() => {
    const fp = new URLSearchParams();
    if (effCategory) fp.set("category", effCategory);
    if (effSubcategory) fp.set("subcategory", effSubcategory);
    if (isLanding && effBrand) fp.set("brand", effBrand);
    const filterParams = fp.toString() ? `?${fp.toString()}` : "";
    axios.get(`${API}/products/filters${filterParams}`).then(r => setFilters(r.data)).catch(console.error);
  }, [effCategory, effSubcategory, effBrand, isLanding]);

  const updateParam = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    params.delete("page");
    setSearchParams(params);
    setPage(1);
  };

  const toggleFeature = (f) => {
    const cur = features ? features.split(",") : [];
    const next = cur.includes(f) ? cur.filter(x => x !== f) : [...cur, f];
    updateParam("features", next.join(","));
  };

  const selectCategory = (c) => {
    const params = new URLSearchParams(searchParams);
    if (category === c) { params.delete("category"); } else { params.set("category", c); }
    ["subcategory", "color", "min_price", "max_price", "features", "page", "collection_id", "search"].forEach(k => params.delete(k));
    setSearchParams(params);
    setPage(1);
  };

  const setPriceBucket = (b) => {
    const params = new URLSearchParams(searchParams);
    if (b) {
      if (b.min) params.set("min_price", b.min); else params.delete("min_price");
      if (b.max) params.set("max_price", b.max); else params.delete("max_price");
    } else {
      params.delete("min_price");
      params.delete("max_price");
    }
    params.delete("page");
    setSearchParams(params);
    setPage(1);
  };

  const clearQuickFilters = () => {
    const params = new URLSearchParams(searchParams);
    ["color", "min_price", "max_price", "features", "page"].forEach(k => params.delete(k));
    setSearchParams(params);
    setPage(1);
  };

  const collectionName = collectionId ? collectionId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : "";
  const pageTitle = landing ? (effSubcategory && !landing.subcategory ? `${landing.h1} — ${effSubcategory}` : landing.h1) : (newArrivals ? "New Arrivals" : collectionName || subcategory || category || brand || (search ? `Results for "${search}"` : "All Furniture"));

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 overflow-x-clip" data-testid="shop-page">
      {/* Sticky category / quick-filter bar */}
      <ShopFilterBar
        filters={filters}
        category={category}
        subcategory={subcategory}
        color={color}
        features={features}
        minPrice={minPrice}
        maxPrice={maxPrice}
        sort={sort}
        showCategories={!isLanding}
        selectCategory={selectCategory}
        updateParam={updateParam}
        toggleFeature={toggleFeature}
        setPriceBucket={setPriceBucket}
        clearQuickFilters={clearQuickFilters}
        onOpenFullFilters={() => setFiltersOpen(true)}
      />

      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-[#C05621]">Home</Link>
        <ChevronRight size={14} />
        <span className="text-[#1A202C] font-medium">{pageTitle}</span>
      </div>

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C]">{pageTitle}</h1>
          <p className="text-gray-500 mt-1">{total} products found</p>
          {landing && (
            <p className="text-gray-600 mt-2 max-w-2xl text-sm md:text-base" data-testid="landing-intro">{landing.intro}</p>
          )}
        </div>
      </div>

      {/* Related feature pages (internal SEO links) */}
      {landing && landing.related && landing.related.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="landing-related">
          <span className="text-xs uppercase font-semibold text-gray-400">Related:</span>
          {landing.related.map(r => (
            <Link key={r.path || r.slug} to={r.path || `/shop/${r.slug}`} className="px-3 py-1 rounded-full text-xs border border-gray-200 text-gray-600 hover:border-[#C05621] hover:text-[#C05621] transition-colors" data-testid={`related-page-${(r.path || r.slug).replace(/\//g, '-').replace(/^-/, '')}`}>{r.h1}</Link>
          ))}
        </div>
      )}

      {/* Popular Search Tags — hidden during an active search so results come first */}
      {!search && !category && !subcategory && !collectionId && !newArrivals && !isLanding && (
        <PopularSearchTags currentSearch={search} />
      )}

      <div className="flex gap-8">
        {/* Sidebar Filters */}
        <aside className={`${filtersOpen ? "fixed inset-0 z-50 bg-white p-6 overflow-y-auto" : "hidden"} lg:block lg:relative lg:w-64 lg:flex-shrink-0`} data-testid="filters-sidebar">
          {filtersOpen && (
            <button onClick={() => setFiltersOpen(false)} className="lg:hidden absolute top-4 right-4"><X size={24} /></button>
          )}
          <h3 className="font-bold text-lg mb-4">Filters</h3>
          {filters && (
            <div className="space-y-5">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Category</h4>
                <div className="space-y-0.5">
                  {filters.categories.map(c => (
                    <div key={c}>
                      <button onClick={() => { 
                        selectCategory(c);
                        setFiltersOpen(false);
                      }}
                        className={`block w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors font-medium ${category === c ? "bg-[#C05621] text-white" : "hover:bg-gray-100 text-gray-600"}`}
                        data-testid={`filter-category-${c.toLowerCase().replace(/\s/g,'-')}`}
                      >{c}</button>
                      {/* Show subcategories when category is selected */}
                      {category === c && filters.subcategories && (
                        <div className="ml-3 mt-0.5 space-y-0.5 border-l-2 border-gray-200 pl-2">
                          {sortSubcats(filters.subcategories.filter(s => s)).map(s => (
                            <button key={s} onClick={() => { updateParam("subcategory", searchParams.get("subcategory") === s ? "" : s); setFiltersOpen(false); }}
                              className={`block w-full text-left text-xs px-2 py-1 rounded transition-colors ${searchParams.get("subcategory") === s ? "bg-[#DD6B20] text-white" : "hover:bg-gray-50 text-gray-500"}`}
                            >{s}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {!brandSlug && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Brand</h4>
                <div className="space-y-0.5">
                  {filters.brands.map(b => (
                    <button key={b} onClick={() => { updateParam("brand", brand === b ? "" : b); setFiltersOpen(false); }}
                      className={`block w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors ${brand === b ? "bg-[#C05621] text-white" : "hover:bg-gray-100 text-gray-600"}`}
                      data-testid={`filter-brand-${b.toLowerCase()}`}
                    >{b}</button>
                  ))}
                </div>
              </div>
              )}
              {/* Color Filter */}
              {filters.colors && filters.colors.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Color</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {filters.colors.map(c => {
                      const isActive = color === c.name;
                      return (
                        <button key={c.name} onClick={() => updateParam("color", isActive ? "" : c.name)}
                          className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium border transition-colors ${isActive ? "bg-[#2D3748] text-white border-[#2D3748]" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
                        >
                          <span className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" style={{backgroundColor: COLOR_SWATCHES[c.name] || "#ccc"}} />
                          {c.name} <span className="opacity-50">({c.count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Features Filter (competitor-style facets) */}
              {((filters.features && filters.features.length > 0) || (filters.pieces && filters.pieces.length > 0)) && (
                <div data-testid="features-filter-section">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Features</h4>
                  {filters.pieces && filters.pieces.length > 0 && (
                    <div className="mb-2">
                      <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1">Number of Pieces</p>
                      <div className="flex flex-wrap gap-1.5">
                        {filters.pieces.map(f => {
                          const isActive = effFeatures.split(",").includes(f.name);
                          return (
                            <button key={f.name} onClick={() => { toggleFeature(f.name); setFiltersOpen(false); }}
                              className={`px-2 py-1 rounded-full text-[10px] font-medium border transition-colors ${isActive ? "bg-[#2D3748] text-white border-[#2D3748]" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
                              data-testid={`filter-feature-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            >{f.name} <span className="opacity-50">({f.count})</span></button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {filters.features && filters.features.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {filters.features.map(f => {
                        const isActive = effFeatures.split(",").includes(f.name);
                        return (
                          <button key={f.name} onClick={() => { toggleFeature(f.name); setFiltersOpen(false); }}
                            className={`px-2 py-1 rounded-full text-[10px] font-medium border transition-colors ${isActive ? "bg-[#2D3748] text-white border-[#2D3748]" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
                            data-testid={`filter-feature-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          >{f.name} <span className="opacity-50">({f.count})</span></button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Style</h4>
                <div className="space-y-0.5">
                  {filters.styles.map(s => (
                    <button key={s} onClick={() => updateParam("style", s)} className="block w-full text-left text-sm px-3 py-1.5 rounded-md hover:bg-gray-100 text-gray-600">{s}</button>
                  ))}
                </div>
              </div>
              {(category || brand || color || search || features || searchParams.get("subcategory")) && (
                <button onClick={() => { setSearchParams({}); setFiltersOpen(false); }} className="text-[#C05621] text-sm font-medium hover:underline" data-testid="clear-filters">Clear all filters</button>
              )}
            </div>
          )}
        </aside>

        {/* Product Grid */}
        <div className="flex-1 min-w-0 overflow-hidden">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 rounded-xl aspect-[4/3] mb-4" />
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
                  <div className="h-5 bg-gray-200 rounded w-2/3 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-1/4" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <Package size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No products found</h3>
              <p className="text-gray-500">Try adjusting your filters or search terms</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="product-grid">
                {products.map((p, i) => <ProductCard key={p.id} product={p} eager={i < 4} />)}
              </div>
              {loadingMore && (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-2 border-[#C05621] border-t-transparent rounded-full mx-auto" />
                  <p className="text-sm text-gray-500 mt-2">Loading more products...</p>
                </div>
              )}
              {!hasMore && products.length > 0 && (
                <p className="text-center text-sm text-gray-400 py-8">Showing all {products.length} of {total} products</p>
              )}
            </>
          )}
        </div>
      </div>

      {/* After a search, offer popular searches BELOW the results */}
      {search && !isLanding && (
        <div className="mt-10 pt-8 border-t border-gray-100">
          <PopularSearchTags currentSearch={search} />
        </div>
      )}
    </div>
  );
}

// ─── PRODUCT 3D VIEWER ───
function Product3DViewer({ productId, productSlug }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const check3DStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/products/${productId}/3d-status`);
      setStatus(r.data);
    } catch {}
  }, [productId]);

  useEffect(() => { check3DStatus(); }, [check3DStatus]);

  const generate3D = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/products/generate-3d`, { product_id: productId });
      setStatus(r.data);
      if (r.data.status === "processing") {
        toast.success("3D view is generating! Check back in 1-2 minutes.");
        // Start polling
        const poll = setInterval(async () => {
          const s = await axios.get(`${API}/products/${productId}/3d-status`);
          setStatus(s.data);
          if (s.data.status === "completed" || s.data.status === "failed") {
            clearInterval(poll);
            if (s.data.status === "completed") toast.success("3D view ready!");
          }
        }, 10000);
        setTimeout(() => clearInterval(poll), 120000);
      }
    } catch {
      toast.error("Failed to generate 3D view");
    }
    setLoading(false);
  };

  if (status?.status === "completed" && status?.video_url) {
    return (
      <div className="bg-gray-50 rounded-xl p-3" data-testid="product-3d-viewer">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} className="text-[#C05621]" />
          <span className="text-xs font-semibold text-[#1A202C]">3D Product View</span>
        </div>
        <video src={status.video_url} autoPlay loop muted playsInline className="w-full rounded-lg" />
      </div>
    );
  }

  return (
    <button
      onClick={generate3D}
      disabled={loading || status?.status === "processing"}
      className="w-full py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-sm font-medium text-gray-500 hover:border-[#C05621] hover:text-[#C05621] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
      data-testid="generate-3d-btn"
    >
      <Sparkles size={16} />
      {loading || status?.status === "processing" ? "Generating 3D View..." : "View in 3D"}
    </button>
  );
}

// ─── MATTRESS SIZE VARIANTS ───
function MattressSizeVariants({ productId, currentName }) {
  const [variants, setVariants] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!productId) return;
    axios.get(`${API}/products/${productId}/size-variants`).then(r => {
      if (r.data.variants?.length > 0) setVariants(r.data);
    }).catch(() => {});
  }, [productId]);

  if (!variants || variants.variants.length === 0) return null;

  return (
    <div className="border-t border-gray-100 pt-5" data-testid="mattress-size-variants">
      <h3 className="font-semibold text-sm text-[#1A202C] mb-3">Available Sizes for {variants.model_name}</h3>
      <p className="text-[10px] text-gray-400 mb-3 uppercase tracking-wider">Current: {variants.current_size}</p>
      <div className="grid grid-cols-2 gap-2">
        {variants.variants.map((v) => (
          <button key={v.id} onClick={() => navigate(`/product/${v.slug}`)}
            className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:border-[#C05621] hover:bg-[#FDFBF7] transition-all text-left group"
            data-testid={`size-variant-${v.size.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-[#1A202C] group-hover:text-[#C05621] transition-colors">{v.size}</p>
              <p className="text-sm font-semibold text-[#C05621]">${v.price.toFixed(2)}</p>
            </div>
            {!v.in_stock && <span className="text-[9px] text-red-500 font-medium">Out of stock</span>}
            <ChevronRight size={14} className="text-gray-300 group-hover:text-[#C05621] flex-shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── 3D VIEWER ───
function ThreeDViewer({ productId, productName }) {
  const [status, setStatus] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products/${productId}/3d-status`).then(r => setStatus(r.data)).catch(() => {});
  }, [productId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/products/generate-3d`, { product_id: productId });
      setStatus(r.data);
      if (r.data.status === "processing") {
        toast.success("3D view generating! Check back in 1-2 minutes.");
        const interval = setInterval(async () => {
          const s = await axios.get(`${API}/products/${productId}/3d-status`);
          setStatus(s.data);
          if (s.data.status === "completed" || s.data.status === "error" || s.data.status === "failed") clearInterval(interval);
        }, 10000);
        setTimeout(() => clearInterval(interval), 180000);
      }
    } catch (e) {
      toast.error("3D generation failed");
    }
    setGenerating(false);
  };

  if (status?.status === "completed" && status.video_url) {
    return (
      <div className="border-t border-gray-100 pt-5 mt-4" data-testid="3d-viewer">
        <h3 className="font-semibold text-sm text-[#1A202C] mb-2 flex items-center gap-1.5"><Eye size={16} className="text-[#C05621]" /> 3D View</h3>
        <video src={status.video_url} controls autoPlay loop muted className="w-full rounded-xl" style={{ maxHeight: "300px" }} />
      </div>
    );
  }

  return (
    <div className="border-t border-gray-100 pt-5 mt-4" data-testid="3d-viewer">
      <button onClick={generate} disabled={generating || status?.status === "processing"}
        className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm font-medium text-gray-500 hover:border-[#C05621] hover:text-[#C05621] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="generate-3d-btn">
        {status?.status === "processing" ? <><Clock size={16} className="animate-spin" /> Generating 3D view...</> :
         generating ? "Starting..." : <><Eye size={16} /> View in 3D</>}
      </button>
      {status?.status === "error" && <p className="text-[10px] text-red-500 mt-1 text-center">{status.message}</p>}
    </div>
  );
}

// ─── STYLE MATCHES ───
function StyleMatches({ productId }) {
  const [matches, setMatches] = useState([]);
  const [basedOn, setBasedOn] = useState({});
  const navigate = useNavigate();
  useEffect(() => {
    if (!productId) return;
    axios.get(`${API}/products/${productId}/style-matches`).then(r => { setMatches(r.data.matches || []); setBasedOn(r.data.based_on || {}); }).catch(() => {});
  }, [productId]);
  if (matches.length === 0) return null;
  return (
    <div className="border-t border-gray-100 pt-5 mt-4" data-testid="style-matches">
      <h3 className="font-semibold text-sm text-[#1A202C] mb-1">Matching Style</h3>
      <p className="text-[10px] text-gray-400 mb-3">Products that match {[basedOn.color, basedOn.style, basedOn.material].filter(Boolean).join(", ")}</p>
      <div className="grid grid-cols-3 gap-2">
        {matches.slice(0, 6).map(m => (
          <div key={m.id} className="cursor-pointer group" onClick={() => navigate(`/product/${m.slug}`)}>
            <div className="rounded-lg overflow-hidden bg-[#F7FAFC] aspect-square mb-1">
              <img loading="lazy" decoding="async" src={m.images?.[0]?.url || "https://placehold.co/120x120"} alt={m.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
            </div>
            <p className="text-[10px] text-[#C05621] font-medium">{m.brand}</p>
            <p className="text-[10px] font-semibold line-clamp-1">{m.name}</p>
            <p className="text-xs font-bold">${m.price?.toFixed(2)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── PICKUP AVAILABILITY ───
function PickupAvailability({ productId }) {
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    if (!productId) return;
    axios.get(`${API}/store/la-bodega-bay/locations`).then(async (r) => {
      const locs = r.data;
      const available = [];
      for (const loc of locs) {
        try {
          const inv = await axios.get(`${API}/store/la-bodega-bay/location-inventory/${loc.id}?search=`);
          const found = inv.data.items?.find(i => i.product_id === productId);
          if (found) available.push({ ...loc, qty: found.qty, floor_model: found.floor_model });
        } catch {}
      }
      setLocations(available);
    }).catch(() => {});
  }, [productId]);

  if (locations.length === 0) return null;
  return (
    <div className="border-t border-gray-100 pt-5 mt-4" data-testid="pickup-availability">
      <h3 className="font-semibold text-sm text-[#1A202C] mb-2 flex items-center gap-1.5"><Store size={16} className="text-[#38A169]" /> Available for Pickup</h3>
      <div className="space-y-1.5">
        {locations.map((loc, i) => (
          <div key={i} className="flex items-center justify-between bg-green-50 rounded-lg px-3 py-2">
            <div><p className="text-xs font-semibold text-[#1A202C]">{loc.name}</p>{loc.address && <p className="text-[10px] text-gray-500">{loc.address}</p>}</div>
            <div className="text-right"><span className="text-xs font-bold text-green-600">{loc.qty} in stock</span>{loc.floor_model && <span className="ml-1 text-[9px] text-blue-600 font-bold">Floor Model</span>}</div>
          </div>
        ))}
        <p className="text-[10px] text-gray-400">Save 10% when you pick up in store!</p>
      </div>
    </div>
  );
}

// ─── PRODUCT DETAIL ───
function ProductDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // 404 = truly gone (noindex). Anything else (403/5xx/network) is TRANSIENT: never tell crawlers "not found".
  const [fetchStatus, setFetchStatus] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPos, setPanPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [openProductFaq, setOpenProductFaq] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const ctaRef = useRef(null);
  const [showSticky, setShowSticky] = useState(false);
  const [saleCfg, setSaleCfg] = useState(null);
  const affirmPromo = useAffirmPromo(data?.product?.price || 0);
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const { lang, t } = useTranslation();

  useEffect(() => {
    axios.get(`${API}/storefront/sale-banner`).then(r => { if (r.data?.enabled) setSaleCfg(r.data); }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setFetchStatus(null);
    axios.get(`${API}/products/${slug}`).then(r => {
      setData(r.data);
      setSelectedImage(0);
      // GA4 view_item event
      try {
        const p = r?.data?.product;
        if (p && typeof window !== "undefined" && window.gtag) {
          window.gtag("event", "view_item", {
            currency: "USD",
            value: Number(p.price || 0),
            items: [{
              item_id: p.id || p.slug || slug,
              item_name: p.name || "",
              item_brand: p.brand || "",
              item_category: p.category || "",
              item_category2: p.subcategory || "",
              price: Number(p.price || 0),
            }],
          });
          // Google Ads dynamic remarketing — lets Google retarget with this exact product
          window.gtag("event", "view_item", {
            send_to: AW_ID,
            value: Number(p.price || 0),
            items: [{ id: p.id || p.slug || slug, google_business_vertical: "retail" }],
          });
        }
        if (p) fbTrack("ViewContent", { content_ids: [p.id || p.slug || slug], content_type: "product", content_name: p.name || "", content_category: p.category || "", value: Number(p.price || 0), currency: "USD" });
      } catch {}
    }).catch((err) => {
      const status = err?.response?.status || 0;
      setFetchStatus(status);
      console.error(err);
      // Transient failure (rate-limit, 5xx, network): retry once after a short pause
      if (status !== 404 && retryTick === 0) setTimeout(() => setRetryTick(1), 1500);
    }).finally(() => setLoading(false));
  }, [slug, retryTick]);

  // Inject JSON-LD structured data for SEO
  useEffect(() => {
    if (data?.product) {
      axios.get(`${API}/products/${slug}/structured-data`).then(r => {
        // Remove any previous structured data scripts
        document.querySelectorAll('script[data-seo="product-jsonld"]').forEach(s => s.remove());
        document.querySelectorAll('script[data-seo="local-business"]').forEach(s => s.remove());
        // Inject product JSON-LD
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo", "product-jsonld");
        script.textContent = JSON.stringify(r.data.product);
        document.head.appendChild(script);
        // Inject local business JSON-LD
        const bizScript = document.createElement("script");
        bizScript.type = "application/ld+json";
        bizScript.setAttribute("data-seo", "local-business");
        bizScript.textContent = JSON.stringify(r.data.localBusiness);
        document.head.appendChild(bizScript);
        // Inject AEO schemas (HowTo + Organization for E-E-A-T) from product detail response
        document.querySelectorAll('script[data-seo="aeo"]').forEach(s => s.remove());
        if (data.aeo_schemas && Array.isArray(data.aeo_schemas)) {
          data.aeo_schemas.forEach((schema, idx) => {
            const aeoScript = document.createElement("script");
            aeoScript.type = "application/ld+json";
            aeoScript.setAttribute("data-seo", "aeo");
            aeoScript.textContent = JSON.stringify(schema);
            document.head.appendChild(aeoScript);
          });
        }
        // Update page title and meta description (already localized by backend when ?lang=es)
        document.title = data.product.seo_title || `${data.product.name} - ${data.product.brand} | La Bodega Bay Furniture`;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.content = data.product.seo_description || data.product.description?.substring(0, 160) || "";
        // Dynamic OG + canonical for social sharing
        const canonicalUrl = lang === "es"
          ? `https://labodegabayfurniture.com/es/product/${slug}`
          : `https://labodegabayfurniture.com/product/${slug}`;
        const ogDesc = lang === "es"
          ? `${data.product.name} de ${data.product.brand} por $${data.product.price}. Envío gratis al Valle Central y Bay Area. Financiamiento $0 enganche disponible.`
          : `${data.product.name} by ${data.product.brand} for $${data.product.price}. Free delivery to Central Valley & Bay Area. $0 down financing available.`;
        updatePageSEO({
          title: data.product.seo_title || `${data.product.name} - ${data.product.brand} | La Bodega Bay Furniture`,
          description: data.product.seo_description || data.product.description?.substring(0, 160) || "",
          canonical: canonicalUrl,
          ogTitle: `${data.product.name} - $${data.product.price} | La Bodega Bay Furniture`,
          ogDescription: ogDesc,
          ogImage: data.product.images?.[0]?.url || "",
          ogType: "product",
        });
      }).catch(() => {});
    }
    return () => {
      document.querySelectorAll('script[data-seo="product-jsonld"]').forEach(s => s.remove());
      document.querySelectorAll('script[data-seo="local-business"]').forEach(s => s.remove());
      document.querySelectorAll('script[data-seo="aeo"]').forEach(s => s.remove());
    };
  }, [data, slug, lang]);

  // Track recently viewed
  useEffect(() => {
    if (data?.product) {
      addToRecentlyViewed(data.product);
    }
  }, [data]);

  // Sticky add-to-cart bar when the main CTA scrolls out of view
  useEffect(() => {
    const el = ctaRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(([entry]) => {
      setShowSticky(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    }, { threshold: 0 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [data]);

  if (loading) return (
    <div key="pdp-skeleton" className="max-w-7xl mx-auto px-4 py-8 min-h-screen" aria-busy="true" data-testid="product-skeleton">
      <div className="h-8 w-2/3 max-w-md bg-gray-100 rounded-full mb-6 animate-pulse" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 animate-pulse">
        <div className="bg-gray-200 rounded-2xl aspect-square" />
        <div className="space-y-4"><div className="h-6 bg-gray-200 w-1/4 rounded" /><div className="h-8 bg-gray-200 w-3/4 rounded" /><div className="h-6 bg-gray-200 w-1/3 rounded" /><div className="h-40 bg-gray-100 rounded-xl" /></div>
      </div>
    </div>
  );

  if (!data) {
    if (fetchStatus !== 404) {
      // Transient API failure: keep the page indexable and the title meaningful — NEVER emit noindex here.
      const guessedName = (slug || "").replace(/-[a-z0-9]{5,}$/i, "").replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
      if (typeof document !== "undefined") {
        const robotsMeta = document.querySelector('meta[name="robots"]');
        if (robotsMeta) robotsMeta.content = "index,follow";
        document.title = `${guessedName || "Product"} | La Bodega Bay Furniture`;
      }
      return (
        <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="product-temporarily-unavailable">
          <h1 className="text-3xl font-bold mb-3">{guessedName || "This product"}</h1>
          <p className="text-gray-600 mb-8">Product details are loading slowly right now. Please try again in a moment.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button onClick={() => setRetryTick(t => t + 1)} className="px-5 py-2.5 bg-[#C05621] text-white rounded-lg font-semibold" data-testid="product-retry-btn">Try again</button>
            <Link to="/shop" className="px-5 py-2.5 border border-gray-200 rounded-lg">Shop all furniture</Link>
          </div>
          <p className="text-xs text-gray-400 mt-10">Questions? Call us at <a className="underline" href="tel:+12098274076">(209) 827-4076</a>.</p>
        </div>
      );
    }
    // Soft-404 prevention: tell Google not to index this URL, and offer rescue links
    // so the user (and crawlers) can find their way back to real content.
    if (typeof document !== "undefined") {
      let robotsMeta = document.querySelector('meta[name="robots"]');
      if (!robotsMeta) { robotsMeta = document.createElement("meta"); robotsMeta.name = "robots"; document.head.appendChild(robotsMeta); }
      robotsMeta.content = "noindex,follow";
      document.title = "Product Not Found | La Bodega Bay Furniture";
    }
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="product-not-found">
        <h1 className="text-3xl font-bold mb-3">We couldn't find that product</h1>
        <p className="text-gray-600 mb-8">It may have sold out or been renamed. Try one of these:</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/shop" className="px-5 py-2.5 bg-[#C05621] text-white rounded-lg font-semibold">Shop all furniture</Link>
          <Link to="/category/Bedroom" className="px-5 py-2.5 border border-gray-200 rounded-lg">Bedroom</Link>
          <Link to="/category/Living%20Room" className="px-5 py-2.5 border border-gray-200 rounded-lg">Living Room</Link>
          <Link to="/category/Dining%20Room" className="px-5 py-2.5 border border-gray-200 rounded-lg">Dining</Link>
          <Link to="/mattress-store" className="px-5 py-2.5 border border-gray-200 rounded-lg">Mattresses</Link>
        </div>
        <p className="text-xs text-gray-400 mt-10">Need help finding something? Call us at <a className="underline" href="tel:+12098274076">(209) 827-4076</a> — we'll help you find an in-stock alternative.</p>
      </div>
    );
  }

  const { product, related, collection_products } = data;
  const product_faqs = data.product_faqs || [];
  const pickupPrice = product.price * 0.9;
  const savings = product.original_price > product.price ? product.original_price - product.price : 0;

  return (
    <div key="pdp-page" className="max-w-7xl mx-auto px-4 py-8" data-testid="product-detail-page">
      {/* Back button + Breadcrumb */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-gray-500 mb-6">
        <button
          onClick={() => {
            if (window.history.state && window.history.state.idx > 0) { navigate(-1); return; }
            const params = new URLSearchParams();
            if (product.category) params.set("category", product.category);
            if (product.subcategory) params.set("subcategory", product.subcategory);
            navigate(`/shop?${params.toString()}`);
          }}
          className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-full text-xs font-semibold text-[#1A202C] hover:border-[#C05621] hover:text-[#C05621] transition-colors"
          data-testid="product-back-btn"
        >
          <ChevronLeft size={14} /> {lang === "es" ? "Volver" : "Back"}
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <Link to="/" className="hover:text-[#C05621]">Home</Link>
          <ChevronRight size={14} className="flex-shrink-0" />
          <Link to={`/category/${encodeURIComponent(product.category)}`} className="hover:text-[#C05621]">{product.category}</Link>
          {product.subcategory && (
            <>
              <ChevronRight size={14} className="flex-shrink-0" />
              <Link to={`/shop?category=${encodeURIComponent(product.category)}&subcategory=${encodeURIComponent(product.subcategory)}`} className="hover:text-[#C05621]" data-testid="breadcrumb-subcategory">{product.subcategory}</Link>
            </>
          )}
          <ChevronRight size={14} className="flex-shrink-0" />
          <span className="text-[#1A202C] font-medium line-clamp-1">{product.name}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Images */}
        <div>
          <div className="relative rounded-2xl overflow-hidden bg-[#F7FAFC] aspect-square mb-4 cursor-pointer" data-testid="product-main-image"
            onClick={() => setLightboxOpen(true)}>
            <img src={getDetailImage(product.images?.[selectedImage]?.url)}
              alt={product.name}
              onError={imgFallback}
              className="w-full h-full object-contain transition-transform duration-500"
              loading={selectedImage === 0 ? "eager" : "lazy"} decoding="async" fetchPriority={selectedImage === 0 ? "high" : "auto"}
            />
            <div className="absolute bottom-4 right-4 bg-black/60 text-white text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1.5 opacity-0 group-hover:opacity-100 hover:!opacity-100 transition-opacity pointer-events-none" style={{opacity: 0.7}}>
              <Search size={12} /> Click to zoom
            </div>
            {product.is_deal_of_week && (
              <span className="absolute top-4 left-4 bg-[#E53E3E] text-white font-black px-5 py-2.5 rounded-xl text-base shadow-lg flex items-center gap-1.5"><Timer size={16} /> EXTRA {product.deal_discount_pct}% OFF</span>
            )}
            {product.stock_quantity <= 5 && product.stock_quantity > 0 && (
              <span className="absolute bottom-4 left-4 bg-[#E53E3E] text-white text-sm font-medium px-3 py-1.5 rounded-full flex items-center gap-1">
                <AlertCircle size={14} /> Only {product.stock_quantity} left in stock!
              </span>
            )}
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-3">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setSelectedImage(i)} className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${i === selectedImage ? "border-[#C05621]" : "border-transparent opacity-60 hover:opacity-100"}`} data-testid={`thumbnail-${i}`}>
                  <img loading="lazy" decoding="async" onError={imgFallback} src={getThumbnail(img.url)} alt={img.alt} className="w-full h-full object-contain bg-[#F7FAFC]" width="80" height="80" />
                </button>
              ))}
            </div>
          )}

          {/* Lightbox Modal */}
          {lightboxOpen && (
            <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center" data-testid="image-lightbox"
              onClick={(e) => { if (e.target === e.currentTarget) setLightboxOpen(false); }}>
              <button onClick={() => setLightboxOpen(false)} className="absolute top-4 right-4 z-10 text-white hover:text-gray-300 bg-black/50 rounded-full p-2" data-testid="lightbox-close">
                <X size={24} />
              </button>
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3">
                <button onClick={() => setZoomLevel(z => Math.max(1, z - 0.5))} className="text-white bg-black/50 rounded-full p-2 hover:bg-black/70" data-testid="lightbox-zoom-out">
                  <Minus size={20} />
                </button>
                <span className="text-white text-sm font-medium bg-black/50 px-3 py-1 rounded-full">{Math.round(zoomLevel * 100)}%</span>
                <button onClick={() => setZoomLevel(z => Math.min(4, z + 0.5))} className="text-white bg-black/50 rounded-full p-2 hover:bg-black/70" data-testid="lightbox-zoom-in">
                  <Plus size={20} />
                </button>
              </div>
              {product.images?.length > 1 && (
                <>
                  <button onClick={() => { setSelectedImage(i => i > 0 ? i - 1 : product.images.length - 1); setZoomLevel(1); setPanPos({x:0,y:0}); }}
                    className="absolute left-4 z-10 text-white bg-black/50 rounded-full p-3 hover:bg-black/70"><ChevronLeft size={28} /></button>
                  <button onClick={() => { setSelectedImage(i => i < product.images.length - 1 ? i + 1 : 0); setZoomLevel(1); setPanPos({x:0,y:0}); }}
                    className="absolute right-4 z-10 text-white bg-black/50 rounded-full p-3 hover:bg-black/70"><ChevronRight size={28} /></button>
                </>
              )}
              <div className="max-w-[90vw] max-h-[85vh] overflow-hidden cursor-grab active:cursor-grabbing"
                onMouseDown={(e) => { if (zoomLevel > 1) { e.preventDefault(); setDragging(true); setDragStart({x: e.clientX - panPos.x, y: e.clientY - panPos.y}); } }}
                onMouseMove={(e) => { if (dragging) { setPanPos({x: e.clientX - dragStart.x, y: e.clientY - dragStart.y}); } }}
                onMouseUp={() => setDragging(false)}
                onMouseLeave={() => setDragging(false)}
                onWheel={(e) => { e.preventDefault(); setZoomLevel(z => Math.max(1, Math.min(4, z + (e.deltaY > 0 ? -0.3 : 0.3)))); }}
                onDoubleClick={() => { if (zoomLevel > 1) { setZoomLevel(1); setPanPos({x:0,y:0}); } else { setZoomLevel(2.5); } }}
              >
                <img src={product.images?.[selectedImage]?.url} alt={product.name}
                  className="max-w-[90vw] max-h-[85vh] object-contain select-none"
                  style={{ transform: `scale(${zoomLevel}) translate(${panPos.x / zoomLevel}px, ${panPos.y / zoomLevel}px)`, transition: dragging ? 'none' : 'transform 0.2s ease' }}
                  draggable={false}
                />
              </div>
              {product.images?.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {product.images.map((_, i) => (
                    <button key={i} onClick={() => { setSelectedImage(i); setZoomLevel(1); setPanPos({x:0,y:0}); }}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${i === selectedImage ? "bg-white scale-125" : "bg-white/40 hover:bg-white/70"}`} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="space-y-5">
          <div>
            <p className="text-sm font-medium text-[#C05621] uppercase tracking-wider mb-1">{product.brand}</p>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-2" data-testid="product-name">{product.name}</h1>
            {product.collection_name && (
              <Link to={`/shop?collection_id=${product.collection_id}`}
                className="group inline-flex items-center gap-2 mt-1.5 px-3.5 py-2 bg-[#FDF6EE] border border-[#C05621]/30 rounded-full text-sm font-semibold text-[#C05621] hover:bg-[#C05621] hover:text-white transition-colors"
                data-testid="collection-link">
                <LayoutGrid size={15} />
                {lang === "es" ? `Ver toda la ${product.collection_name}` : `Shop the full ${product.collection_name}`}
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
            )}
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-sm group" data-testid="pdp-rating-badge">
              <span className="flex items-center gap-0.5 text-[#F6AD55]">{[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}</span>
              <span className="font-bold text-[#1A202C]">4.9</span>
              <span className="text-gray-500 group-hover:text-[#C05621] group-hover:underline">{lang === "es" ? "· 150+ reseñas en Google" : "· 150+ Google reviews"}</span>
            </a>
          </div>

          {/* Price */}
          <div className="bg-[#FDFBF7] rounded-xl p-5 space-y-3" data-testid="product-pricing">
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold text-[#1A202C]" data-testid="product-price">${product.price.toFixed(2)}</span>
              {savings > 0 && (
                <>
                  <span className="text-lg text-gray-400 line-through">${product.original_price.toFixed(2)}</span>
                  <span className="text-sm font-bold text-[#38A169] bg-green-50 px-2 py-0.5 rounded">Save ${savings.toFixed(2)}</span>
                </>
              )}
            </div>
            {/* Pickup discount */}
            <div className="flex items-center gap-2 bg-[#38A169]/10 text-[#38A169] px-4 py-2.5 rounded-lg" data-testid="pickup-discount">
              <Store size={18} />
              <span className="font-semibold">Pickup & Save 10%: ${pickupPrice.toFixed(2)}</span>
              <span className="text-xs ml-auto text-gray-500">at Los Banos location</span>
            </div>
            {/* Financing */}
            <div className="space-y-2 text-sm text-gray-600">
              <div className="bg-[#0FA0EA]/5 border border-[#0FA0EA]/25 px-4 py-3 rounded-lg space-y-2.5">
                <div className="flex items-center gap-2">
                  <CreditCard size={16} className="text-[#0FA0EA] flex-shrink-0" />
                  <span>{affirmAlaText(affirmPromo, lang) || (lang === "es" ? "Pagos mensuales flexibles con Affirm" : "Flexible monthly payments with Affirm")}</span>
                </div>
                <a
                  href={affirmPrequalUrl(product.price)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-1.5 w-full bg-[#0FA0EA] hover:bg-[#0C8AC9] text-white text-sm font-bold px-5 py-2.5 rounded-full shadow-sm transition-colors"
                  data-testid="affirm-prequalify-btn"
                >
                  {lang === "es" ? "Ve si calificas con Affirm" : "See if you qualify with Affirm"} →
                </a>
                <p className="text-[11px] text-gray-500 text-center">{lang === "es" ? "Verificar tu elegibilidad no afecta tu puntaje de crédito." : "Checking your eligibility won't affect your credit score."}</p>
              </div>
              <div className="bg-[#38A169]/5 border border-[#38A169]/25 px-4 py-3 rounded-lg space-y-2.5">
                <div className="flex items-center gap-2">
                  <Tag size={16} className="text-[#38A169] flex-shrink-0" />
                  <span className="text-[#2F855A] font-semibold text-xs" data-testid="snap-financing-teaser">{lang === "es" ? "Paga en 100 días con Snap Finance — $0 de enganche, ¡sin crédito necesario!" : "Pay in 100 days with Snap Finance — $0 down, no credit needed!"}</span>
                </div>
                <a
                  href="https://snapfinance.com/apply"
                  target="_blank"
                  rel="noreferrer"
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      const r = await axios.get(`${API}/payments/snap/apply-url`);
                      window.open(r.data?.url || "https://snapfinance.com/apply", "_blank", "noopener,noreferrer");
                    } catch {
                      window.open("https://snapfinance.com/apply", "_blank", "noopener,noreferrer");
                    }
                  }}
                  className="flex items-center justify-center gap-1.5 w-full bg-[#38A169] hover:bg-[#2F855A] text-white text-sm font-bold px-5 py-2.5 rounded-full shadow-sm transition-colors"
                  data-testid="snap-apply-btn"
                >
                  {lang === "es" ? "Solicitar con Snap Finance" : "Apply with Snap Finance"} →
                </a>
                <p className="text-[11px] text-gray-500 text-center">{lang === "es" ? "Aplicar no afecta tu puntaje FICO." : "Applying won't affect your FICO score."}</p>
              </div>
            </div>
          </div>

          {/* Shipping */}
          <div className="flex items-center gap-2 text-sm">
            <Truck size={18} className="text-[#C05621]" />
            <span className="text-gray-700">
              {product.in_stock ? (
                <><strong className="text-[#38A169]">In Stock</strong> - Ships <strong className="text-[#38A169]">FREE</strong> in {product.shipping_days_min}-{product.shipping_days_max} business days from {product.brand}</>
              ) : (
                <span className="text-gray-500">Check availability</span>
              )}
            </span>
          </div>

          {/* Bundle note */}
          {product.is_bundle && product.bundle_note && (
            <div className="flex items-start gap-2 text-sm bg-blue-50 text-blue-700 px-4 py-3 rounded-lg" data-testid="bundle-note">
              <Package size={18} className="mt-0.5 flex-shrink-0" />
              <span>{product.bundle_note}</span>
            </div>
          )}

          {/* Minimum Quantity Notice */}
          {product.min_quantity > 1 && (
            <div className="flex items-center gap-2 text-sm bg-amber-50 text-amber-700 px-4 py-2.5 rounded-lg" data-testid="min-qty-note">
              <Package size={16} className="flex-shrink-0" />
              <span className="font-medium">{product.quantity_note || `Minimum order: ${product.min_quantity} units`}</span>
            </div>
          )}

          {/* Low stock urgency */}
          {product.in_stock && product.stock_quantity > 0 && product.stock_quantity <= 5 && (
            <div className="flex items-center gap-2 text-sm text-[#E53E3E] font-semibold" data-testid="pdp-low-stock">
              <Timer size={16} /> {lang === "es" ? `¡Solo quedan ${product.stock_quantity}! Ordena pronto` : `Only ${product.stock_quantity} left — order soon`}
            </div>
          )}

          {/* Sale CTA — why buy from us (shown while a sale is active) */}
          {saleCfg && (
            <div className="border border-[#B91C1C]/25 bg-[#FEF2F2] rounded-xl p-4" data-testid="sale-cta-box">
              <div className="flex items-center gap-2 mb-2.5">
                <Tag size={15} className="text-[#B91C1C] flex-shrink-0" />
                <span className="font-heading font-bold text-[#B91C1C] text-sm uppercase tracking-wide" data-testid="sale-cta-title">
                  {lang === "es" ? (saleCfg.text_es || saleCfg.text) : saleCfg.text}
                </span>
              </div>
              <ul className="space-y-1.5 text-sm text-gray-700" data-testid="sale-cta-points">
                {(lang === "es" ? [
                  "Precios de Venta de Aniversario — respaldados por nuestra Garantía de Precio",
                  "$0 de enganche con Snap (sin crédito necesario) o pagos mensuales fáciles con Affirm",
                  "Envío GRATIS en el Valle Central, Bay Area y Fresno",
                  "Empresa familiar en Los Banos desde 2000 — gente real, servicio real",
                ] : [
                  "Anniversary Sale pricing — backed by our Price Match Guarantee",
                  "$0 down with Snap (no credit needed) or easy monthly payments with Affirm",
                  "FREE delivery across the Central Valley, Bay Area & Fresno",
                  "Family-owned in Los Banos since 2000 — real people, real service",
                ]).map((pt, i) => (
                  <li key={i} className="flex gap-2"><Check size={15} className="text-[#B91C1C] mt-0.5 flex-shrink-0" /> {pt}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Add to Cart */}
          <button
            ref={ctaRef}
            onClick={(e) => { e.stopPropagation(); addToCart(product.id, product.min_quantity || 1); }}
            className="w-full py-4 bg-[#C05621] text-white rounded-xl font-bold text-lg hover:bg-[#DD6B20] transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            data-testid="add-to-cart-btn"
          >
            <ShoppingCart size={20} /> {product.min_quantity > 1 
              ? (lang === "es" ? `Agregar ${product.min_quantity} al carrito` : `Add ${product.min_quantity} to Cart`) 
              : t("product.addToCart")}
          </button>

          {/* AI Assistant — same conversation as the floating chat widget */}
          <ProductChatBox productName={product.name} />

          {/* Share Button */}
          <div className="flex gap-2">
            <button onClick={(e) => { e.stopPropagation();
              if (navigator.share) {
                navigator.share({ title: product.name, text: lang === "es" ? `Mira ${product.name} - $${product.price.toFixed(2)} en La Bodega Bay Furniture!` : `Check out ${product.name} - $${product.price.toFixed(2)} at La Bodega Bay Furniture!`, url: window.location.href });
              } else {
                navigator.clipboard.writeText(window.location.href);
                toast.success(lang === "es" ? "¡Enlace copiado!" : "Link copied!");
              }
            }} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2" data-testid="share-btn">
              <Send size={16} /> {lang === "es" ? "Compartir" : "Share"}
            </button>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noreferrer" className="w-10 h-10 border border-gray-200 rounded-xl flex items-center justify-center text-gray-500 hover:text-[#1877F2] hover:border-[#1877F2] transition-colors" aria-label="Share on Facebook"><Facebook size={18} /></a>
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${product.name} - $${product.price.toFixed(2)} at La Bodega Bay Furniture!`)}&url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noreferrer" className="w-10 h-10 border border-gray-200 rounded-xl flex items-center justify-center text-gray-500 hover:text-[#1DA1F2] hover:border-[#1DA1F2] transition-colors" aria-label="Share on X"><ExternalLink size={18} /></a>
          </div>

          {/* 3D Viewer */}
          <Product3DViewer productId={product.id} productSlug={slug} />

          {/* Trust badges */}
          <div className="grid grid-cols-3 gap-3 text-center">
            {(lang === "es" ? [
              { icon: <Truck size={20} />, text: "Envío Gratis" },
              { icon: <Shield size={20} />, text: "Garantía de Precio" },
              { icon: <CreditCard size={20} />, text: "Snap: Sin Crédito" },
            ] : [
              { icon: <Truck size={20} />, text: "Free Delivery" },
              { icon: <Shield size={20} />, text: "Price Match" },
              { icon: <CreditCard size={20} />, text: "Snap: No Credit Needed" },
            ]).map((badge, i) => (
              <div key={i} className="bg-gray-50 rounded-lg py-3 text-xs text-gray-600">
                <div className="text-[#C05621] flex justify-center mb-1">{badge.icon}</div>
                {badge.text}
              </div>
            ))}
          </div>

          {/* Delivery, Assembly & Pickup details */}
          <div className="border border-gray-100 rounded-xl overflow-hidden" data-testid="delivery-info-accordion">
            <button type="button" onClick={() => setDeliveryOpen(!deliveryOpen)} className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors" data-testid="delivery-info-toggle">
              <span className="font-semibold text-sm flex items-center gap-2"><Truck size={16} className="text-[#C05621]" /> {lang === "es" ? "Entrega, Instalación y Recogida" : "Delivery, Assembly & Pickup"}</span>
              <ChevronDown size={18} className={`text-gray-400 transition-transform ${deliveryOpen ? "rotate-180" : ""}`} />
            </button>
            {deliveryOpen && (
              <ul className="px-4 pb-4 pt-1 text-sm text-gray-600 space-y-2">
                {(lang === "es" ? [
                  "Entrega gratis a domicilio en el Valle Central, Bay Area y Fresno — normalmente en pocos días",
                  "Instalación profesional opcional en tu casa por $99",
                  "Recoge en nuestra tienda de Los Banos y ahorra 10% automáticamente",
                  "Garantía de precio: igualamos cualquier precio más bajo + 5% de la diferencia",
                ] : [
                  "FREE drop-off delivery across the Central Valley, Bay Area & Fresno — usually within a few days",
                  "Optional professional in-home assembly for a flat $99",
                  "Pick up at our Los Banos store and save 10% automatically",
                  "Price Match Guarantee: we match any lower price + 5% of the difference",
                ]).map((line, i) => <li key={i} className="flex gap-2"><Check size={15} className="text-[#38A169] mt-0.5 flex-shrink-0" /> {line}</li>)}
              </ul>
            )}
          </div>

          {/* Description */}
          <div className="border-t border-gray-100 pt-5">
            <h3 className="font-semibold text-lg mb-3">{lang === "es" ? "Sobre este producto" : "About this product"}</h3>
            <p className="text-gray-600 leading-relaxed text-sm">{product.description}</p>
            {product.dimensions && <p className="text-sm text-gray-500 mt-3"><strong>{lang === "es" ? "Dimensiones:" : "Dimensions:"}</strong> {product.dimensions}</p>}
            {product.material && <p className="text-sm text-gray-500 mt-1"><strong>{lang === "es" ? "Material:" : "Material:"}</strong> {product.material}</p>}
            {product.color && <p className="text-sm text-gray-500 mt-1"><strong>{lang === "es" ? "Color:" : "Color:"}</strong> {product.color}</p>}
          </div>

          {/* Mattress Size Variants */}
          {product.category === "Mattress" && <MattressSizeVariants productId={product.id} currentName={product.name} />}

          {/* 3D Viewer */}
          <ThreeDViewer productId={product.id} productName={product.name} />

          {/* Pickup Availability */}
          <PickupAvailability productId={product.id} />
        </div>
      </div>

      {/* Real Google reviews — social proof at the decision point */}
      <section className="mt-12 pt-10 border-t border-gray-100" data-testid="pdp-reviews-section">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
          <div>
            <h2 className="font-heading text-2xl font-bold text-[#1A202C]">{lang === "es" ? "Lo Que Dicen Tus Vecinos" : "What Your Neighbors Say"}</h2>
            <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500">
              <span className="flex text-[#F6AD55]">{[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}</span>
              <strong className="text-[#1A202C]">4.9</strong> {lang === "es" ? "· 150+ reseñas en Google · 200+ en todas las plataformas" : "· 150+ Google reviews · 200+ across all platforms"}
            </div>
          </div>
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noreferrer" className="text-sm font-semibold text-[#C05621] hover:underline whitespace-nowrap" data-testid="read-all-reviews-link">
            {lang === "es" ? "Lee todas las reseñas en Google →" : "Read all reviews on Google →"}
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
          {GOOGLE_REVIEW_QUOTES.map((r, i) => (
            <figure key={i} className="bg-[#FDFBF7] rounded-xl p-5 border border-gray-100">
              <div className="flex text-[#F6AD55] mb-2">{[...Array(5)].map((_, j) => <Star key={j} size={13} fill="currentColor" />)}</div>
              <blockquote className="text-sm text-gray-700 leading-relaxed">“{r.text}”</blockquote>
              <figcaption className="mt-3 text-xs font-semibold text-[#1A202C]">{r.name} <span className="font-normal text-gray-400">· Google review</span></figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Collection Products */}
      {collection_products && collection_products.length > 0 && (
        <section className="mt-16 pt-12 border-t border-gray-100" data-testid="collection-products">
          <h2 className="font-heading text-2xl font-bold text-[#1A202C] mb-2">More from {product.collection_name}</h2>
          <p className="text-gray-500 mb-8 text-sm">Complete your set with matching pieces</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {collection_products.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Product FAQs — deterministic, grounded in product fields + store policies */}
      {product_faqs.length > 0 && (
        <section className="mt-16 pt-12 border-t border-gray-100" data-testid="product-faq-section">
          <h2 className="font-heading text-2xl font-bold text-[#1A202C] mb-6">Frequently Asked Questions</h2>
          <div className="space-y-3 max-w-3xl">
            {product_faqs.map((f, i) => (
              <div key={i} className="border border-gray-100 rounded-xl overflow-hidden bg-white">
                <button
                  data-testid={`product-faq-${i}`}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenProductFaq(openProductFaq === i ? null : i)}
                >
                  <h3 className="font-semibold text-sm text-[#1A202C] pr-4">{f.q}</h3>
                  <ChevronDown size={18} className={`text-gray-400 flex-shrink-0 transition-transform ${openProductFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openProductFaq === i && (
                  <div className="px-4 pb-4 text-sm text-gray-600 border-t border-gray-50 pt-3">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* You Might Also Like — similar products (same type, ±25% price) */}
      {related && related.length > 0 && (
        <section className="mt-16 pt-12 border-t border-gray-100" data-testid="similar-products">
          <h2 className="font-heading text-2xl font-bold text-[#1A202C] mb-2">{lang === "es" ? "También te puede gustar" : "You Might Also Like"}</h2>
          <p className="text-gray-500 text-sm mb-8">{lang === "es" ? "Opciones similares a un precio parecido" : "Similar options at a similar price"}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {related.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Recently Viewed */}
      <RecentlyViewedCarousel excludeProductId={product.id} />

      {/* Sticky add-to-cart bar (appears when the main CTA scrolls away) */}
      <div className={`fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] transition-transform duration-300 ${showSticky ? "translate-y-0" : "translate-y-full"}`} data-testid="sticky-add-to-cart">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
          <img src={getThumbnail(product.images?.[0]?.url)} onError={imgFallback} alt="" className="w-11 h-11 rounded-lg object-contain bg-[#F7FAFC] hidden sm:block" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#1A202C] truncate">{product.name}</p>
            <p className="text-sm font-bold text-[#C05621]">${product.price.toFixed(2)} <span className="text-[11px] font-normal text-gray-400">{affirmPromo?.monthly ? (lang === "es" ? `o desde $${affirmPromo.monthly}/mes con Affirm` : `or as low as $${affirmPromo.monthly}/mo with Affirm`) : (lang === "es" ? "o pagos flexibles con Affirm" : "or flexible payments with Affirm")}</span></p>
          </div>
          <button onClick={() => addToCart(product.id, product.min_quantity || 1)} className="px-5 py-2.5 bg-[#C05621] text-white rounded-lg font-bold text-sm hover:bg-[#DD6B20] transition-colors flex items-center gap-1.5 flex-shrink-0" data-testid="sticky-add-to-cart-btn">
            <ShoppingCart size={16} /> {t("product.addToCart")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CART PAGE ───
// ─── CART: FINANCING TEASER (live "as low as" pricing fetched from Affirm's promo API) ───
function CartFinancingTeaser({ total, lang }) {
  const promo = useAffirmPromo(total);
  if (!total || total < 100) return null;
  const es = lang === "es";
  return (
    <div className="mt-4 space-y-2" data-testid="cart-financing-teaser">
      <div className="bg-white border border-[#0FA0EA]/40 rounded-xl px-4 py-3 space-y-2.5" data-testid="cart-affirm-teaser">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#0FA0EA] rounded-lg flex items-center justify-center flex-shrink-0"><span className="text-white font-bold text-sm">A</span></div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#1A202C]">
              {promo?.monthly
                ? (es ? <>Pagos desde <span className="text-[#0FA0EA]">${promo.monthly}/mes</span> con Affirm</> : <>Payments as low as <span className="text-[#0FA0EA]">${promo.monthly}/mo</span> with Affirm</>)
                : (es ? <>Pagos mensuales flexibles con Affirm</> : <>Flexible monthly payments with Affirm</>)}
            </p>
            <p className="text-[11px] text-gray-500">
              {promo?.zero_apr
                ? (es ? "0% APR disponible para clientes que califican" : "0% APR available for qualified customers")
                : promo?.pay_in_4
                  ? (es ? "4 pagos sin interés también disponibles" : "4 interest-free payments also available")
                  : (es ? "Verifica tu elegibilidad en segundos" : "Check your eligibility in seconds")}
            </p>
          </div>
        </div>
        <a
          href={affirmPrequalUrl(total)}
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-center gap-1.5 w-full bg-[#0FA0EA] hover:bg-[#0C8AC9] text-white text-sm font-bold px-5 py-2.5 rounded-full shadow-sm transition-colors"
          data-testid="cart-affirm-prequalify-btn"
        >
          {es ? "Ve si calificas con Affirm" : "See if you qualify with Affirm"} →
        </a>
        <p className="text-[11px] text-gray-500 text-center">{es ? "Verificar tu elegibilidad no afecta tu puntaje de crédito." : "Checking your eligibility won't affect your credit score."}</p>
      </div>
      <div className="bg-white border border-[#38A169]/40 rounded-xl px-4 py-3 space-y-2.5" data-testid="cart-snap-teaser">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#38A169] rounded-lg flex items-center justify-center flex-shrink-0"><span className="text-white font-bold text-sm">S</span></div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#1A202C]">
              {es ? <>Snap Finance — <span className="text-[#38A169]">NO SE NECESITA CRÉDITO</span></> : <>Snap Finance — <span className="text-[#38A169]">NO CREDIT NEEDED</span></>}
            </p>
            <p className="text-[11px] text-gray-500">{es ? "$0 de enganche · 100 días igual que efectivo" : "$0 down · 100 days same as cash"}</p>
          </div>
        </div>
        <a
          href="https://snapfinance.com/apply"
          target="_blank"
          rel="noreferrer"
          onClick={async (e) => {
            e.preventDefault();
            try {
              const r = await axios.get(`${API}/payments/snap/apply-url`);
              window.open(r.data?.url || "https://snapfinance.com/apply", "_blank", "noopener,noreferrer");
            } catch {
              window.open("https://snapfinance.com/apply", "_blank", "noopener,noreferrer");
            }
          }}
          className="flex items-center justify-center gap-1.5 w-full bg-[#38A169] hover:bg-[#2F855A] text-white text-sm font-bold px-5 py-2.5 rounded-full shadow-sm transition-colors"
          data-testid="cart-snap-apply-btn"
        >
          {es ? "Solicitar con Snap Finance" : "Apply with Snap Finance"} →
        </a>
        <p className="text-[11px] text-gray-500 text-center">{es ? "Aplicar no afecta tu puntaje FICO." : "Applying won't affect your FICO score."}</p>
      </div>
      <p className="text-[10px] text-gray-400 leading-snug px-1">
        {es
          ? `*Estimación proporcionada por Affirm según tu total de $${total.toFixed(2)} para clientes que califiquen. Las tasas van de 0–36% APR según elegibilidad — Affirm te muestra tus términos exactos antes de confirmar.`
          : `*Estimate provided by Affirm based on your $${total.toFixed(2)} total for qualified customers. Rates range from 0–36% APR based on eligibility — Affirm shows your exact terms before you confirm.`}
      </p>
    </div>
  );
}

function CartPage() {
  const { lang, t } = useTranslation();
  useEffect(() => { document.title = lang === "es" ? "Carrito | La Bodega Bay Furniture" : "Shopping Cart | La Bodega Bay Furniture"; }, [lang]);
  const { cart, loading, updateQuantity, removeItem } = useCart();
  const navigate = useNavigate();
  const linkPrefix = lang === "es" ? "/es" : "";

  // GA4 view_cart — once per cart page visit
  const viewCartTracked = useRef(false);
  useEffect(() => {
    if (loading || viewCartTracked.current || !cart?.items?.length) return;
    viewCartTracked.current = true;
    track("view_cart", {
      currency: "USD",
      value: Number(cart.total || 0),
      items: cart.items.map(i => ({ item_id: i.product_id, item_name: i.name || "", price: Number(i.price || 0), quantity: Number(i.quantity || 1) })),
    });
  }, [loading, cart]);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-16 text-center"><div className="animate-spin w-8 h-8 border-2 border-[#C05621] border-t-transparent rounded-full mx-auto" /></div>;

  const items = cart?.items || [];
  const pickupTotal = (cart?.total || 0) * 0.9;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="cart-page">
      <h1 className="font-heading text-3xl font-bold text-[#1A202C] mb-8">{t("cart.title")}</h1>

      {items.length === 0 ? (
        <div className="text-center py-20">
          <ShoppingCart size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">{t("cart.empty")}</h3>
          <p className="text-gray-500 mb-6">{t("cart.emptySub")}</p>
          <button onClick={() => navigate(`${linkPrefix}/shop`)} className="px-6 py-3 bg-[#C05621] text-white rounded-lg font-semibold hover:bg-[#DD6B20] transition-colors" data-testid="continue-shopping-btn">
            {t("cart.continueShopping")}
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {items.map((item) => (
            <div key={item.product_id} className="flex gap-4 bg-white border border-gray-100 rounded-xl p-4 shadow-sm" data-testid={`cart-item-${item.product_id}`}>
              <img loading="lazy" decoding="async" src={item.product?.images?.[0]?.url || "https://placehold.co/120x120"}
                alt={item.product?.name}
                className="w-24 h-24 object-cover rounded-lg flex-shrink-0 cursor-pointer"
                onClick={() => navigate(`${linkPrefix}/product/${item.product?.slug}`)}
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-[#1A202C] line-clamp-1">{item.product?.name}</h3>
                <p className="text-xs text-gray-500">{item.product?.brand}</p>
                <p className="text-lg font-bold text-[#1A202C] mt-1">${item.product?.price?.toFixed(2)}</p>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button onClick={() => removeItem(item.product_id)} className="text-gray-400 hover:text-red-500 p-1" data-testid={`remove-item-${item.product_id}`} aria-label={t("cart.remove")}><Trash2 size={18} /></button>
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg">
                  <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="p-1.5 hover:bg-gray-200 rounded-l-lg" data-testid={`qty-minus-${item.product_id}`}><Minus size={16} /></button>
                  <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="p-1.5 hover:bg-gray-200 rounded-r-lg" data-testid={`qty-plus-${item.product_id}`}><Plus size={16} /></button>
                </div>
              </div>
            </div>
          ))}

          {/* Cart summary */}
          <div className="bg-[#FDFBF7] rounded-xl p-6 mt-6" data-testid="cart-summary">
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">{t("cart.subtotal")} ({cart.item_count} {cart.item_count === 1 ? t("cart.item") : t("cart.items")})</span><span className="font-semibold">${cart.total?.toFixed(2)}</span></div>
              <div className="flex justify-between text-[#38A169]"><span className="flex items-center gap-1"><Store size={14} /> {lang === "es" ? "Recoge en Los Banos: Ahorra 10%" : "Pickup at Los Banos: Save 10%"}</span><span className="font-semibold">${pickupTotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">{t("cart.shipping")}</span><span className="font-semibold text-[#38A169]">{t("cart.free").toUpperCase()}</span></div>
            </div>
            <div className="border-t border-gray-200 mt-4 pt-4 flex justify-between items-center">
              <span className="text-lg font-bold">{t("cart.total")}</span>
              <span className="text-2xl font-bold text-[#1A202C]">${cart.total?.toFixed(2)}</span>
            </div>
            <CartFinancingTeaser total={cart.total || 0} lang={lang} />
            <button onClick={() => navigate(`${linkPrefix}/checkout`)} className="w-full mt-4 py-3.5 bg-[#C05621] text-white rounded-xl font-bold text-lg hover:bg-[#DD6B20] transition-all shadow-lg" data-testid="checkout-btn">
              {t("cart.proceedToCheckout")}
            </button>
            <div className="mt-3 flex items-center justify-center gap-4 text-xs text-gray-500" data-testid="cart-trust-strip">
              <span className="flex items-center gap-1"><Star size={12} className="text-[#F6AD55]" fill="currentColor" /> {lang === "es" ? "Sirviendo familias por más de 25 años" : "Serving customers for over 25 years"}</span>
              <span className="flex items-center gap-1"><Shield size={12} /> {lang === "es" ? "Garantía de Precio" : "Price Match"}</span>
              <span className="flex items-center gap-1"><Truck size={12} /> {lang === "es" ? "Envío Gratis" : "Free Delivery"}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CHECKOUT PAGE ───
// ─── CHECKOUT: AFFIRM SAMPLE PAYMENT PANEL (live pricing from Affirm's promo API) ───
function AffirmSamplePanel({ total, lang }) {
  const promo = useAffirmPromo(total);
  if (!total || total < 100) return null;
  const es = lang === "es";
  return (
    <div className="mx-1 mb-1 -mt-1 bg-white rounded-lg border border-[#0FA0EA]/30 p-4" data-testid="affirm-sample-panel">
      <p className="text-xs font-bold text-[#1A202C] uppercase tracking-wide mb-2">
        {es ? `Tu pedido de $${total.toFixed(2)} podría verse así:` : `Your order of $${total.toFixed(2)} could look like:`}
      </p>
      <div className="flex gap-2 mb-3">
        {promo?.pay_in_4 && (
          <div className="flex-1 text-center border border-gray-100 rounded-lg py-2" data-testid="affirm-panel-pay4">
            <p className="text-base font-bold text-[#1A202C]">${Math.ceil(total / 4).toLocaleString()}<span className="text-xs font-normal"> ×4</span></p>
            <p className="text-[10px] text-gray-500">{es ? "4 pagos, 0% interés" : "4 payments, 0% interest"}</p>
          </div>
        )}
        <div className="relative flex-1 text-center border-2 border-[#0FA0EA] rounded-lg py-2 bg-blue-50/50" data-testid="affirm-panel-monthly">
          {promo?.zero_apr && (
            <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#0FA0EA] text-white text-[9px] font-bold px-2 rounded-full whitespace-nowrap">{es ? "0% APR DISPONIBLE*" : "0% APR AVAILABLE*"}</span>
          )}
          {promo?.monthly ? (
            <>
              <p className="text-base font-bold text-[#1A202C]">${promo.monthly}<span className="text-xs font-normal">/{es ? "mes" : "mo"}</span></p>
              <p className="text-[10px] text-gray-500">{es ? "estimación \u201Cdesde\u201D de Affirm*" : "Affirm's \u201Cas low as\u201D estimate*"}</p>
            </>
          ) : (
            <>
              <p className="text-base font-bold text-[#1A202C]">{es ? "Pagos mensuales" : "Monthly payments"}</p>
              <p className="text-[10px] text-gray-500">{es ? "Affirm te muestra tus opciones*" : "Affirm shows your options*"}</p>
            </>
          )}
        </div>
      </div>
      <div className="flex items-start gap-2 bg-green-50 rounded-lg p-2.5 mb-2">
        <Check size={14} className="text-green-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-gray-700">
          {es
            ? <><span className="font-semibold">No necesitas crédito perfecto.</span> Verificar tu elegibilidad toma ~30 segundos y <span className="font-semibold">no afecta tu puntaje de crédito</span>. Sin cargos ocultos, nunca.</>
            : <><span className="font-semibold">Perfect credit not required.</span> Checking your eligibility takes about 30 seconds and <span className="font-semibold">won't affect your credit score</span>. No hidden fees, ever.</>}
        </p>
      </div>
      <p className="text-[10px] text-gray-400 leading-snug">
        {es
          ? `*Estimación proporcionada por Affirm según el total de tu pedido para clientes que califiquen. Las tasas van de 0–36% APR según elegibilidad — Affirm te muestra tus términos exactos antes de confirmar. ¿Prefieres cero verificación de crédito? Prueba Snap Finance abajo.`
          : `*Estimate provided by Affirm based on your order total for qualified customers. Rates range from 0–36% APR based on eligibility — Affirm shows your exact terms before you confirm. Prefer no credit check at all? Try Snap Finance below.`}
      </p>
    </div>
  );
}

// ─── CHECKOUT: ROTATING GOOGLE REVIEWS (real quotes only — add more to GOOGLE_REVIEW_QUOTES) ───
function CheckoutReviewsRotator({ lang }) {
  const [idx, setIdx] = useState(0);
  const quotes = GOOGLE_REVIEW_QUOTES;
  useEffect(() => {
    if (quotes.length < 2) return;
    const t = setInterval(() => setIdx(i => (i + 1) % quotes.length), 6000);
    return () => clearInterval(t);
  }, [quotes.length]);
  const es = lang === "es";
  const q = quotes[idx];
  if (!q) return null;
  return (
    <div className="mt-4 pt-4 border-t border-gray-200" data-testid="checkout-reviews-rotator">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[#DD6B20] text-xs tracking-tight">★★★★★</span>
        <p className="text-xs font-bold text-[#1A202C]">{es ? "4.9/5 en Google · 150+ reseñas" : "4.9/5 on Google · 150+ reviews"}</p>
      </div>
      <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-2">{es ? "Por qué tus vecinos compran con nosotros" : "Why neighbors shop with us"}</p>
      <div className="bg-white rounded-lg border border-gray-100 p-3 min-h-[90px]" data-testid="review-quote">
        <p key={idx} className="text-xs text-gray-600 italic leading-relaxed animate-[fadeIn_0.5s_ease]">"{q.text}"</p>
        <p className="text-[10px] font-semibold text-[#1A202C] mt-1.5">— {q.name} <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noreferrer" className="text-gray-400 font-normal underline decoration-gray-200">· {es ? "reseña de Google" : "Google review"}</a></p>
      </div>
      {quotes.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2">
          {quotes.map((_, i) => (
            <button key={i} type="button" onClick={() => setIdx(i)} aria-label={`Review ${i + 1}`} data-testid={`review-dot-${i}`}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? "bg-[#C05621]" : "bg-gray-300"}`} />
          ))}
        </div>
      )}
      <div className="grid grid-cols-3 gap-2 text-center mt-3">
        <div className="bg-white rounded-lg border border-gray-100 py-2"><Truck size={14} className="mx-auto text-[#C05621] mb-0.5" /><p className="text-[9px] text-gray-500 font-semibold">{es ? "Envío Gratis" : "Free Delivery"}</p></div>
        <div className="bg-white rounded-lg border border-gray-100 py-2"><Shield size={14} className="mx-auto text-[#C05621] mb-0.5" /><p className="text-[9px] text-gray-500 font-semibold">{es ? "Igualamos Precios +5%" : "Price Match +5%"}</p></div>
        <div className="bg-white rounded-lg border border-gray-100 py-2"><Star size={14} className="mx-auto text-[#C05621] mb-0.5" fill="currentColor" /><p className="text-[9px] text-gray-500 font-semibold">{es ? "Familiar 25+ Años" : "Family-Owned 25+ Yrs"}</p></div>
      </div>
    </div>
  );
}

function CheckoutPage() {
  const { lang } = useTranslation();
  useEffect(() => { document.title = lang === "es" ? "Pagar | La Bodega Bay Furniture" : "Checkout | La Bodega Bay Furniture"; }, [lang]);
  const T = lang === "es" ? {
    title: "Pagar",
    deliveryMethod: "Método de Entrega",
    dropoffOnly: "Solo Entrega",
    dropoffSub: "GRATIS · Sin instalación",
    deliveryAssembly: "Entrega e Instalación",
    pickup: "Recoger",
    pickupSub: "¡Ahorra 10%!",
    pickupAt: "Recoger en:",
    contactInfo: "Información de contacto",
    deliveryAddress: "Dirección de entrega",
    billingAddress: "Dirección de facturación",
    paymentMethod: "Método de pago",
    placeOrder: "Hacer pedido",
    orderSummary: "Resumen del pedido",
    subtotal: "Subtotal",
    delivery: "Envío",
    assembly: "Instalación",
    tax: "Impuesto",
    total: "Total",
    free: "GRATIS",
    discount: "Descuento",
    placingOrder: "Procesando...",
    creditCard: "Tarjeta de crédito",
    affirm: "Affirm — paga en mensualidades",
    snap: "Snap Finance — sin crédito",
    firstName: "Nombre",
    lastName: "Apellido",
    middleName: "Segundo nombre",
    email: "Correo electrónico",
    phone: "Teléfono",
    streetAddress: "Dirección",
    cityLabel: "Ciudad",
    state: "Estado",
    zipCode: "Código postal",
    sameAsDelivery: "Igual que la dirección de entrega",
    promoCode: "Código de descuento",
    apply: "Aplicar",
    estimatedDelivery: "Entrega estimada",
    continueShopping: "Seguir comprando",
    completeSnap: "Completa tu solicitud de Snap Finance",
    completeSnapSub: "$0 enganche, sin crédito necesario. Recibirás los detalles para finalizar tu plan de financiamiento de 100 días.",
    confirmEmail: "Te enviaremos una confirmación a tu correo.",
    applySnapBtn: "Solicitar con Snap Finance",
    assemblyIncluded: "Incluye instalación profesional en tu casa ($99 fijos)",
  } : {
    title: "Checkout",
    deliveryMethod: "Delivery Method",
    dropoffOnly: "Drop-off Only",
    dropoffSub: "FREE · No assembly",
    deliveryAssembly: "Delivery & Assembly",
    pickup: "Pickup",
    pickupSub: "Save 10%!",
    pickupAt: "Pickup at:",
    contactInfo: "Contact Information",
    deliveryAddress: "Delivery Address",
    billingAddress: "Billing Address",
    paymentMethod: "Payment Method",
    placeOrder: "Place Order",
    orderSummary: "Order Summary",
    subtotal: "Subtotal",
    delivery: "Delivery",
    assembly: "Assembly",
    tax: "Tax",
    total: "Total",
    free: "FREE",
    discount: "Discount",
    placingOrder: "Placing Order...",
    creditCard: "Credit Card",
    affirm: "Affirm — Monthly Payments",
    snap: "Snap Finance — No Credit Needed",
    firstName: "First Name",
    lastName: "Last Name",
    middleName: "Middle Name",
    email: "Email",
    phone: "Phone",
    streetAddress: "Street Address",
    cityLabel: "City",
    state: "State",
    zipCode: "ZIP Code",
    sameAsDelivery: "Same as delivery address",
    promoCode: "Promo Code",
    apply: "Apply",
    estimatedDelivery: "Estimated Delivery",
    continueShopping: "Continue Shopping",
    completeSnap: "Complete your Snap Finance application",
    completeSnapSub: "$0 down, no credit needed. You'll receive details to finalize your 100-day financing plan.",
    confirmEmail: "We'll send a confirmation to your email.",
    applySnapBtn: "Apply with Snap Finance",
    assemblyIncluded: "Includes professional in-home assembly ($99 flat fee)",
  };
  const { cart, loading: cartLoading, fetchCart } = useCart();

  // begin_checkout (GA4) + InitiateCheckout (Meta) — once per checkout visit
  const checkoutTracked = useRef(false);
  useEffect(() => {
    if (checkoutTracked.current || !cart?.items?.length) return;
    checkoutTracked.current = true;
    const items = cart.items.map(i => ({ item_id: i.product_id, item_name: i.name || "", price: Number(i.price || 0), quantity: Number(i.quantity || 1) }));
    track("begin_checkout", { currency: "USD", value: Number(cart.total || 0), items });
    fbTrack("InitiateCheckout", { content_ids: cart.items.map(i => i.product_id), content_type: "product", contents: cart.items.map(i => ({ id: i.product_id, quantity: Number(i.quantity || 1) })), value: Number(cart.total || 0), currency: "USD", num_items: cart.items.length });
  }, [cart]);
  const [deliveryMethod, setDeliveryMethod] = useState("delivery");
  const [paymentMethod, setPaymentMethod] = useState("affirm");
  const CHECKOUT_DEFAULTS = { first_name: "", middle_name: "", last_name: "", customer_email: "", customer_phone: "", delivery_address: "", delivery_city: "", delivery_state: "CA", delivery_zip: "", billing_same_as_delivery: true, billing_address: "", billing_city: "", billing_state: "CA", billing_zip: "" };
  const [formData, setFormData] = useState(() => {
    // Restore saved checkout info so an abandoned/failed checkout doesn't force retyping
    try {
      const saved = JSON.parse(localStorage.getItem("lbb_checkout_info") || "{}");
      return { ...CHECKOUT_DEFAULTS, ...saved };
    } catch {
      return CHECKOUT_DEFAULTS;
    }
  });
  // Persist checkout info as the customer types (device-local only, no card data involved)
  useEffect(() => {
    try { localStorage.setItem("lbb_checkout_info", JSON.stringify(formData)); } catch { /* storage full/blocked */ }
  }, [formData]);
  const ph = lang === "es"
    ? { first: "Nombre", middle: "Segundo Nombre", last: "Apellido", email: "Correo Electrónico *", phone: "Número de Teléfono", street: "Dirección *", billStreet: "Dirección de Facturación *", city: "Ciudad *", state: "Estado", zip: "Código Postal *", firstL: "Nombre *", middleL: "Segundo Nombre", lastL: "Apellido *" }
    : { first: "First Name", middle: "Middle Name", last: "Last Name", email: "Email Address *", phone: "Phone Number", street: "Street Address *", billStreet: "Billing Street Address *", city: "City *", state: "State", zip: "Zip Code *", firstL: "First Name *", middleL: "Middle Name", lastL: "Last Name *" };
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  // Fallback modal: shown when Affirm denies a customer mid-checkout so they can
  // recover with Snap Finance (no credit needed) or Stripe — without re-filling
  // the form or losing their cart. Carries { order_id, total } so the fallback
  // can resume the existing order rather than creating a new one.
  const [affirmDenialFallback, setAffirmDenialFallback] = useState(null);
  const [fallbackSubmitting, setFallbackSubmitting] = useState(false);

  // Dev-only visual QA trigger: ?debug-modal=affirm-denial opens the fallback modal
  // with a stub order_id so you can verify the modal works on production WITHOUT
  // having to actually fail an Affirm transaction. Safe in prod — gated by URL.
  // Removes itself from history after firing so the modal doesn't re-open on every render.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("debug-modal") === "affirm-denial") {
      setAffirmDenialFallback({ order_id: "DEBUG-MODAL-TRIGGER", total: 0 });
      sp.delete("debug-modal");
      const newQs = sp.toString();
      window.history.replaceState({}, "", window.location.pathname + (newQs ? "?" + newQs : ""));
    }
  }, []);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [taxRate, setTaxRate] = useState(8.75);
  const [taxLoading, setTaxLoading] = useState(false);
  const navigate = useNavigate();
  const sessionId = getSessionId();

  const subtotal = cart?.total || 0;
  const discount = deliveryMethod === "pickup" ? subtotal * 0.10 : 0;
  const assemblyFee = deliveryMethod === "delivery_assembly" ? 99 : 0;
  const promoDiscount = promoApplied ? (promoApplied.discount_type === "percentage" ? subtotal * promoApplied.discount_value / 100 : Math.min(promoApplied.discount_value, subtotal)) : 0;
  const beforeTax = Math.max(0, subtotal - discount - promoDiscount + assemblyFee);
  const taxAmount = Math.round(beforeTax * taxRate / 100 * 100) / 100;
  const total = Math.round((beforeTax + taxAmount) * 100) / 100;

  // Estimate delivery time based on brands in cart
  const brandLeadTimes = { Ashley: [7, 14], Coaster: [3, 5], Homelegance: [1, 3] };
  const cartBrands = (cart?.items || []).map(i => i.product?.brand).filter(Boolean);
  const minLead = Math.max(1, ...cartBrands.map(b => (brandLeadTimes[b] || [7, 14])[0]));
  const maxLead = Math.max(1, ...cartBrands.map(b => (brandLeadTimes[b] || [7, 14])[1]));
  const addBusinessDays = (start, bdays) => {
    let d = new Date(start); let added = 0;
    while (added < bdays) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) added++; }
    return d;
  };
  const estStart = addBusinessDays(new Date(), minLead);
  const estEnd = addBusinessDays(new Date(), maxLead);
  const fmtDate = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
  const estimatedRange = `${fmtDate(estStart)}-${fmtDate(estEnd)}`;

  // Auto-fetch tax rate when zip code changes
  useEffect(() => {
    const zip = deliveryMethod === "pickup" ? "93635" : formData.delivery_zip;
    if (!zip || zip.length < 5) {
      if (deliveryMethod === "pickup") setTaxRate(8.75);
      return;
    }
    setTaxLoading(true);
    axios.get(`${API}/tax-rate/${zip}`).then(r => {
      setTaxRate(r.data.tax_rate);
    }).catch(() => {
      setTaxRate(8.75);
    }).finally(() => setTaxLoading(false));
  }, [formData.delivery_zip, deliveryMethod]);

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const r = await axios.post(`${API}/promo/validate`, { code: promoCode, session_id: sessionId });
      if (r.data.valid) {
        setPromoApplied(r.data);
        toast.success(r.data.message);
      } else {
        toast.error(r.data.message);
        setPromoApplied(null);
      }
    } catch {
      toast.error("Failed to validate promo code");
    }
    setPromoLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name.trim()) { toast.error("Please enter your first name"); return; }
    if (!formData.last_name.trim()) { toast.error("Please enter your last name"); return; }
    if (!formData.customer_email.trim()) { toast.error("Please enter your email"); return; }
    if (deliveryMethod !== "pickup") {
      if (!formData.delivery_address.trim()) { toast.error("Please enter your delivery address"); return; }
      if (!formData.delivery_city.trim()) { toast.error("Please enter your city"); return; }
      if (!formData.delivery_zip.trim() || formData.delivery_zip.length < 5) { toast.error("Please enter a valid 5-digit zip code"); return; }
      const zipNum = parseInt(formData.delivery_zip, 10);
      if (zipNum < 90001 || zipNum > 96162) { toast.error("We currently only serve California. Please select in-store pickup or enter a California zip code."); return; }
      if (!formData.billing_same_as_delivery) {
        if (!formData.billing_address.trim()) { toast.error("Please enter your billing address"); return; }
        if (!formData.billing_city.trim()) { toast.error("Please enter your billing city"); return; }
        if (!formData.billing_zip.trim() || formData.billing_zip.length < 5) { toast.error("Please enter a valid billing zip code"); return; }
        const bZipNum = parseInt(formData.billing_zip, 10);
        if (bZipNum < 90001 || bZipNum > 96162) { toast.error("Billing address must be within California."); return; }
      }
    }
    setSubmitting(true);
    try {
      const fullName = [formData.first_name, formData.middle_name, formData.last_name].filter(Boolean).join(" ");
      // add_payment_info (GA4) + AddPaymentInfo (Meta, with advanced matching)
      try {
        track("add_payment_info", { currency: "USD", value: Number(cart?.total || 0), payment_type: paymentMethod });
        fbIdentify(formData.customer_email, formData.customer_phone);
        fbTrack("AddPaymentInfo", { currency: "USD", value: Number(cart?.total || 0) });
      } catch {}
      // Step 1: Create the order
      const r = await axios.post(`${API}/checkout`, {
        session_id: sessionId,
        first_name: formData.first_name,
        middle_name: formData.middle_name,
        last_name: formData.last_name,
        customer_name: fullName,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        delivery_address: formData.delivery_address,
        delivery_city: formData.delivery_city,
        delivery_state: formData.delivery_state,
        delivery_zip: formData.delivery_zip,
        billing_same_as_delivery: formData.billing_same_as_delivery,
        billing_address: formData.billing_same_as_delivery ? formData.delivery_address : formData.billing_address,
        billing_city: formData.billing_same_as_delivery ? formData.delivery_city : formData.billing_city,
        billing_state: formData.billing_same_as_delivery ? formData.delivery_state : formData.billing_state,
        billing_zip: formData.billing_same_as_delivery ? formData.delivery_zip : formData.billing_zip,
        delivery_method: deliveryMethod,
        payment_method: paymentMethod,
        promo_code: promoApplied?.code || "",
        lang: lang || "en",
        ga_client_id: adsConsentAllowed() ? gaClientId() : "",
        fbp: adsConsentAllowed() ? getCookie("_fbp") : "",
        fbc: adsConsentAllowed() ? getCookie("_fbc") : "",
        ads_consent: adsConsentAllowed(),
        ...getAttribution()
      });

      if (paymentMethod === "stripe") {
        // Step 2: Create Stripe checkout session
        const payRes = await axios.post(`${API}/payments/create-session`, {
          order_id: r.data.order_id,
          origin_url: window.location.origin
        });
        // Step 3: Redirect to Stripe
        window.location.href = payRes.data.url;
        return;
      }

      if (paymentMethod === "affirm") {
        // Step 2: Load Affirm config and open Affirm checkout
        try {
          // Log Affirm initiation step
          axios.post(`${API}/checkout/${r.data.order_id}/log-step`, { step: "payment_initiated", details: { method: "affirm" } }).catch(() => {});
          // Check minimum amount - Affirm requires $50+
          if (r.data.total < 50) {
            toast.error("Affirm requires a minimum purchase of $50. Please choose another payment method.");
            setSubmitting(false);
            return;
          }
          const configRes = await axios.get(`${API}/payments/affirm-config`);
          const affirmConfig = configRes.data;

          // STEP 1: Initialize Affirm SDK using official IIFE bootstrap
          if (!window.affirm || !window.affirm.checkout) {
            window._affirm_config = {
              public_api_key: affirmConfig.public_key,
              script: affirmConfig.script_url,
              locale: "en_US",
              country_code: "USA",
            };
            // Official Affirm IIFE bootstrap — creates window.affirm with stub methods
            (function(m,g,n,d,a,e,h,c){var b=m[n]||{},k=document.createElement(e),p=document.getElementsByTagName(e)[0],l=function(a,b,c){return function(){a[b]._.push([c,arguments])}};b[d]=l(b,d,"set");var f=b[d];b[a]={};b[a]._=[];f._=[];b._=[];b[a][h]=l(b,a,h);b[c]=function(){b._.push([h,arguments])};a=0;for(c="set add save post open empty reset on off trigger ready setProduct".split(" ");a<c.length;a++)f[c[a]]=l(b,d,c[a]);a=0;for(c=["get","token","url","items"];a<c.length;a++)f[c[a]]=function(){};k.async=!0;k.src=g[e];p.parentNode.insertBefore(k,p);delete g[e];f(g);m[n]=b})(window,window._affirm_config,"affirm","checkout","ui","script","ready","jsReady");
            // Wait for Affirm SDK to be ready
            await new Promise((resolve, reject) => {
              const timeout = setTimeout(() => reject(new Error("Affirm SDK load timeout")), 10000);
              const checkReady = () => {
                if (window.affirm && window.affirm.checkout && typeof window.affirm.checkout === "function") {
                  clearTimeout(timeout);
                  resolve();
                } else {
                  setTimeout(checkReady, 200);
                }
              };
              checkReady();
            });
          }

          // Build addresses
          const shippingAddr = deliveryMethod === "pickup"
            ? { line1: "2225 East Pacheco Blvd", city: "Los Banos", state: "CA", zipcode: "93635", country: "US" }
            : { line1: formData.delivery_address, city: formData.delivery_city || "", state: formData.delivery_state || "CA", zipcode: formData.delivery_zip || "", country: "US" };

          const billingAddr = formData.billing_same_as_delivery || deliveryMethod === "pickup"
            ? shippingAddr
            : { line1: formData.billing_address, city: formData.billing_city || "", state: formData.billing_state || "CA", zipcode: formData.billing_zip || "", country: "US" };

          const fullNameAffirm = [formData.first_name, formData.middle_name, formData.last_name].filter(Boolean).join(" ");

          // Build Affirm checkout object (amounts in cents)
          const taxCents = Math.round((r.data.tax_amount || 0) * 100);
          const assemblyFeeCents = Math.round((r.data.assembly_fee || 0) * 100);
          const totalCents = Math.round(r.data.total * 100);

          // Build discounts object for Affirm
          const discounts = {};
          if (r.data.pickup_discount > 0) {
            discounts["PICKUP_DISCOUNT"] = { discount_amount: Math.round(r.data.pickup_discount * 100), discount_display_name: "Pickup Discount (10%)" };
          }
          if (r.data.promo_discount > 0) {
            discounts["PROMO"] = { discount_amount: Math.round(r.data.promo_discount * 100), discount_display_name: "Promo Code" };
          }

          const affirmCheckoutObj = {
            merchant: {
              user_confirmation_url: `${window.location.origin}/order-confirmation?order_id=${r.data.order_id}&payment=affirm&checkout_token={checkout_token}`,
              user_cancel_url: `${window.location.origin}/checkout`,
              user_confirmation_url_action: "GET",
              name: "La Bodega Bay Furniture",
              public_api_key: affirmConfig.public_key,
            },
            shipping: {
              name: { full: fullNameAffirm },
              address: shippingAddr,
              phone_number: formData.customer_phone,
              email: formData.customer_email,
            },
            billing: {
              name: { full: fullNameAffirm },
              address: billingAddr,
              phone_number: formData.customer_phone,
              email: formData.customer_email,
            },
            items: (cart?.items || []).map(item => ({
              display_name: item.product?.name || "Furniture Item",
              sku: item.product_id || "ITEM",
              unit_price: Math.round((item.product?.price || 0) * 100),
              qty: item.quantity,
              item_url: `${window.location.origin}/product/${item.product?.slug || item.product_id}`,
            })),
            metadata: { order_id: r.data.order_id, mode: "modal" },
            merchant_external_reference: r.data.order_id,
            order_id: r.data.order_id,
            discounts: Object.keys(discounts).length > 0 ? discounts : undefined,
            shipping_amount: assemblyFeeCents,
            tax_amount: taxCents,
            total: totalCents,
            currency: "USD",
          };

          // STEP 2: Open Affirm checkout
          window.affirm.checkout(affirmCheckoutObj);
          window.affirm.checkout.open({
            onFail: (e) => {
              console.error("Affirm onFail:", e);
              axios.post(`${API}/checkout/${r.data.order_id}/log-step`, { step: "payment_failed", details: { method: "affirm", reason: "Not approved / checkout canceled" } }).catch(() => {});
              // Surface the Snap/Stripe fallback inline instead of dead-ending the customer.
              setAffirmDenialFallback({ order_id: r.data.order_id, total: r.data.total });
              setSubmitting(false);
            },
            onSuccess: async (a) => {
              try {
                const authRes = await axios.post(`${API}/payments/affirm/authorize?checkout_token=${a.checkout_token}&order_id=${r.data.order_id}`);
                if (authRes.data.success) {
                  toast.success("Payment approved by Affirm!");
                  navigate(`/order-confirmation?order_id=${r.data.order_id}&payment=affirm&status=paid`);
                } else {
                  // Authorization step failed (Affirm SDK said "approved" but server-side capture didn't).
                  // Same recovery path: let the customer pick Snap or card without restarting.
                  setAffirmDenialFallback({ order_id: r.data.order_id, total: r.data.total });
                }
              } catch {
                setAffirmDenialFallback({ order_id: r.data.order_id, total: r.data.total });
              }
              setSubmitting(false);
            },
          });
        } catch (affirmErr) {
          console.error("Affirm SDK error:", affirmErr);
          toast.error("Could not load Affirm. Please try another payment method.");
          setSubmitting(false);
        }
        return;
      }

      if (paymentMethod === "snap") {
        // Step 2: Create Snap Finance application linked to this order
        try {
          axios.post(`${API}/checkout/${r.data.order_id}/log-step`, { step: "payment_initiated", details: { method: "snap" } }).catch(() => {});
          const snapRes = await axios.post(`${API}/payments/snap/create-application`, {
            order_id: r.data.order_id,
            origin_url: window.location.origin
          });
          // Open Snap Finance in a new tab so customer stays on our site
          window.open(snapRes.data.redirect_url, '_blank', 'noopener,noreferrer');
          // Navigate to order confirmation page which will poll for Snap approval
          navigate(`/order-confirmation?order_id=${r.data.order_id}&payment=snap`);
        } catch (snapErr) {
          const msg = snapErr?.response?.data?.detail || "Snap Finance is temporarily unavailable.";
          toast.error(msg);
          setSubmitting(false);
        }
        return;
      }

      setOrderResult(r.data);
      toast.success("Order placed successfully!");
    } catch (err) {
      toast.error("Failed to place order. Please try again.");
    }
    setSubmitting(false);
  };

  if (orderResult) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="order-confirmation">
        <div className="bg-[#38A169] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"><Check size={32} /></div>
        <h1 className="font-heading text-3xl font-bold text-[#1A202C] mb-3">{lang === "es" ? "¡Pedido Confirmado!" : "Order Confirmed!"}</h1>
        <p className="text-gray-600 mb-2">{lang === "es" ? "Pedido" : "Order"} #{orderResult.order_id?.substring(0, 8)}</p>
        <p className="text-2xl font-bold text-[#1A202C] mb-1">${orderResult.total.toFixed(2)}</p>
        {orderResult.tax_amount > 0 && <p className="text-gray-500 text-sm">{lang === "es" ? `Incluye $${orderResult.tax_amount.toFixed(2)} de impuesto (${orderResult.tax_rate}%)` : `Includes $${orderResult.tax_amount.toFixed(2)} tax (${orderResult.tax_rate}%)`}</p>}
        {orderResult.pickup_discount > 0 && <p className="text-[#38A169] font-medium">{lang === "es" ? `¡Ahorraste $${orderResult.pickup_discount.toFixed(2)} con recoger!` : `You saved $${orderResult.pickup_discount.toFixed(2)} with pickup!`}</p>}
        {orderResult.assembly_fee > 0 && <p className="text-gray-500 text-sm">{lang === "es" ? "Incluye $99 de costo de instalación" : "Includes $99 assembly fee"}</p>}
        {orderResult.estimated_delivery_range && (
          <div className="mt-4 bg-blue-50 text-blue-700 p-4 rounded-xl text-sm" data-testid="delivery-estimate-confirm">
            <p className="font-semibold mb-1">{deliveryMethod === "pickup" ? (lang === "es" ? "Listo para recoger aprox." : "Est. Ready for Pickup") : (lang === "es" ? "Entrega aprox." : "Est. Delivery")}</p>
            <p className="text-lg font-bold">{orderResult.estimated_delivery_range}</p>
            <p className="text-xs mt-1 text-blue-500">{orderResult.estimated_delivery_days} {lang === "es" ? "días hábiles" : "business days"}</p>
          </div>
        )}
        {orderResult.payment_note === "affirm" && (
          <div className="mt-4 bg-blue-50 text-blue-700 p-4 rounded-xl text-sm">
            <p className="font-semibold mb-1">{lang === "es" ? "Completa tu pago con Affirm" : "Complete your Affirm payment"}</p>
            <p>{lang === "es" ? "Recibirás un correo de Affirm para finalizar tu plan de compra ahora-paga-después." : "You'll receive an email from Affirm to finalize your buy-now-pay-later plan."}</p>
          </div>
        )}
        {orderResult.payment_note === "snap" && (
          <div className="mt-4 bg-orange-50 text-[#C05621] p-4 rounded-xl text-sm">
            <p className="font-semibold mb-1">{T.completeSnap}</p>
            <p>{T.completeSnapSub}</p>
            <a href="https://snapfinance.com/apply" target="_blank" rel="noreferrer" onClick={async (e) => {
              e.preventDefault();
              try {
                const r = await axios.get(`${API}/payments/snap/apply-url`);
                window.open(r.data?.url || "https://snapfinance.com/apply", "_blank", "noopener,noreferrer");
              } catch {
                window.open("https://snapfinance.com/apply", "_blank", "noopener,noreferrer");
              }
            }} className="inline-block mt-2 px-4 py-2 bg-[#C05621] text-white rounded-lg text-xs font-bold hover:bg-[#DD6B20]">{T.applySnapBtn}</a>
          </div>
        )}
        <p className="text-gray-500 text-sm mt-4 mb-8">{T.confirmEmail}</p>
        <button onClick={() => navigate(lang === "es" ? "/es" : "/")} className="px-8 py-3 bg-[#C05621] text-white rounded-lg font-semibold hover:bg-[#DD6B20] transition-colors">{T.continueShopping}</button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="checkout-page">
      {affirmDenialFallback && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="affirm-denial-fallback-modal" onClick={(e) => { if (e.target === e.currentTarget && !fallbackSubmitting) setAffirmDenialFallback(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 text-[#C05621] flex items-center justify-center flex-shrink-0"><AlertCircle size={20} /></div>
              <div>
                <h3 className="font-heading text-lg font-bold text-[#1A202C]">{lang === "es" ? "Affirm no pudo aprobarte" : "Affirm couldn't approve this purchase"}</h3>
                <p className="text-sm text-gray-500 mt-1">{lang === "es" ? "No te preocupes — tu carrito está guardado. Prueba una de estas opciones:" : "No worries — your cart is saved. Try one of these instead:"}</p>
              </div>
            </div>

            {/* Snap fallback — primary, no credit needed */}
            <button
              type="button"
              data-testid="fallback-try-snap-btn"
              disabled={fallbackSubmitting}
              onClick={async () => {
                setFallbackSubmitting(true);
                try {
                  axios.post(`${API}/checkout/${affirmDenialFallback.order_id}/log-step`, { step: "fallback_selected", details: { from: "affirm", to: "snap" } }).catch(() => {});
                  const snapRes = await axios.post(`${API}/payments/snap/create-application`, {
                    order_id: affirmDenialFallback.order_id,
                    origin_url: window.location.origin
                  });
                  window.open(snapRes.data.redirect_url, "_blank", "noopener,noreferrer");
                  navigate(`/order-confirmation?order_id=${affirmDenialFallback.order_id}&payment=snap`);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || (lang === "es" ? "Snap no está disponible. Intenta con tarjeta." : "Snap is unavailable right now. Try card instead."));
                  setFallbackSubmitting(false);
                }
              }}
              className="w-full flex items-center justify-between p-4 mb-2 rounded-xl border-2 border-[#38A169] bg-green-50 hover:bg-green-100 transition-all disabled:opacity-50 text-left"
            >
              <div>
                <p className="font-bold text-[#38A169]">{lang === "es" ? "Continuar con Snap Finance" : "Continue with Snap Finance"}</p>
                <p className="text-xs text-gray-600 mt-0.5">{lang === "es" ? "Sin crédito · $0 enganche · 100 días igual que efectivo" : "No credit check · $0 down · 100 days same as cash"}</p>
              </div>
              <ChevronRight size={20} className="text-[#38A169]" />
            </button>

            {/* Stripe / card fallback */}
            <button
              type="button"
              data-testid="fallback-try-card-btn"
              disabled={fallbackSubmitting}
              onClick={async () => {
                setFallbackSubmitting(true);
                try {
                  axios.post(`${API}/checkout/${affirmDenialFallback.order_id}/log-step`, { step: "fallback_selected", details: { from: "affirm", to: "stripe" } }).catch(() => {});
                  const sessionRes = await axios.post(`${API}/payments/create-session`, {
                    order_id: affirmDenialFallback.order_id,
                    origin_url: window.location.origin
                  });
                  // Redirect the current tab to Stripe Checkout
                  window.location.href = sessionRes.data.url;
                } catch (err) {
                  toast.error(err?.response?.data?.detail || (lang === "es" ? "El pago con tarjeta no está disponible. Llama al (209) 827-4076." : "Card payment unavailable. Call (209) 827-4076 for help."));
                  setFallbackSubmitting(false);
                }
              }}
              className="w-full flex items-center justify-between p-4 mb-3 rounded-xl border border-gray-200 hover:border-[#C05621] hover:bg-orange-50 transition-all disabled:opacity-50 text-left"
            >
              <div>
                <p className="font-semibold text-[#1A202C]">{lang === "es" ? "Pagar con tarjeta" : "Pay with credit / debit card"}</p>
                <p className="text-xs text-gray-500 mt-0.5">{lang === "es" ? "Pago seguro a través de Stripe" : "Secure checkout via Stripe"}</p>
              </div>
              <ChevronRight size={20} className="text-gray-400" />
            </button>

            <div className="text-center pt-2 border-t border-gray-100">
              <p className="text-xs text-gray-400 mb-2">{lang === "es" ? "¿Necesitas ayuda? Llámanos al" : "Need help? Call"} <a href="tel:+12098274076" className="text-[#C05621] underline font-semibold">(209) 827-4076</a></p>
              <button
                type="button"
                data-testid="fallback-close-btn"
                disabled={fallbackSubmitting}
                onClick={() => setAffirmDenialFallback(null)}
                className="text-xs text-gray-400 hover:text-gray-600 underline"
              >
                {lang === "es" ? "Cerrar y volver al checkout" : "Close and return to checkout"}
              </button>
            </div>
          </div>
        </div>
      )}

      <h1 className="font-heading text-3xl font-bold text-[#1A202C] mb-8">{T.title}</h1>
      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 space-y-6">
          {/* Delivery method */}
          <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm" data-testid="delivery-method-section">
            <h2 className="font-semibold text-lg mb-4">{T.deliveryMethod}</h2>
            <div className="grid grid-cols-3 gap-3">
              <button type="button" onClick={() => setDeliveryMethod("delivery")}
                className={`p-4 rounded-xl border-2 text-left transition-all ${deliveryMethod === "delivery" ? "border-[#C05621] bg-orange-50" : "border-gray-200"}`}
                data-testid="delivery-option"
              >
                <Truck size={22} className="text-[#C05621] mb-2" />
                <p className="font-semibold text-sm">{T.dropoffOnly}</p>
                <p className="text-xs text-[#38A169] font-medium mt-0.5">{T.dropoffSub}</p>
              </button>
              <button type="button" onClick={() => setDeliveryMethod("delivery_assembly")}
                className={`p-4 rounded-xl border-2 text-left transition-all ${deliveryMethod === "delivery_assembly" ? "border-[#C05621] bg-orange-50" : "border-gray-200"}`}
                data-testid="delivery-assembly-option"
              >
                <Package size={22} className="text-[#C05621] mb-2" />
                <p className="font-semibold text-sm">{T.deliveryAssembly}</p>
                <p className="text-xs text-gray-500 mt-0.5">+$99</p>
              </button>
              <button type="button" onClick={() => setDeliveryMethod("pickup")}
                className={`p-4 rounded-xl border-2 text-left transition-all relative ${deliveryMethod === "pickup" ? "border-[#38A169] bg-green-50" : "border-gray-200"}`}
                data-testid="pickup-option"
              >
                <Store size={22} className="text-[#38A169] mb-2" />
                <p className="font-semibold text-sm">{T.pickup}</p>
                <p className="text-xs text-[#38A169] font-medium mt-0.5">{T.pickupSub}</p>
                <span className="absolute -top-2 -right-2 bg-[#38A169] text-white text-xs font-bold px-2 py-0.5 rounded-full">-10%</span>
              </button>
            </div>
            {deliveryMethod !== "pickup" && (
              <div className="mt-4 bg-amber-50 border border-amber-200 text-amber-900 p-3 rounded-lg text-sm flex items-start gap-2" data-testid="california-only-notice">
                <MapPin size={16} className="mt-0.5 shrink-0" />
                <span>
                  <strong>{lang === "es" ? "Solo entregamos dentro de California." : "California delivery only."}</strong>{" "}
                  {lang === "es"
                    ? "No enviamos fuera de California. Si vives fuera del estado, elige Recoger en Tienda."
                    : "We do not ship outside of California. If you live out of state, please choose In-Store Pickup."}
                </span>
              </div>
            )}
            {deliveryMethod === "pickup" && (
              <div className="mt-4 bg-green-50 text-[#38A169] p-3 rounded-lg text-sm flex items-center gap-2" data-testid="pickup-info">
                <MapPin size={16} /> {T.pickupAt} 2225 East Pacheco Blvd, Los Banos, CA 93635
              </div>
            )}
            {deliveryMethod === "delivery_assembly" && (
              <div className="mt-4 bg-orange-50 text-[#C05621] p-3 rounded-lg text-sm flex items-center gap-2" data-testid="assembly-info">
                <Package size={16} /> {T.assemblyIncluded}
              </div>
            )}
          </div>

          {/* Contact info */}
          <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
            <h2 className="font-semibold text-lg mb-4">{T.contactInfo}</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{ph.firstL}</label>
                  <input required value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} placeholder={ph.first} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30" data-testid="checkout-first-name" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{ph.middleL}</label>
                  <input value={formData.middle_name} onChange={e => setFormData({...formData, middle_name: e.target.value})} placeholder={ph.middle} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30" data-testid="checkout-middle-name" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{ph.lastL}</label>
                  <input required value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} placeholder={ph.last} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30" data-testid="checkout-last-name" />
                </div>
              </div>
              <input required type="email" value={formData.customer_email} onChange={e => setFormData({...formData, customer_email: e.target.value})} placeholder={ph.email} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30" data-testid="checkout-email" />
              <input value={formData.customer_phone} onChange={e => setFormData({...formData, customer_phone: e.target.value})} placeholder={ph.phone} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30" data-testid="checkout-phone" />
            </div>
          </div>

          {/* Delivery Address */}
          {deliveryMethod !== "pickup" && (
            <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
              <h2 className="font-semibold text-lg mb-4">{deliveryMethod === "delivery_assembly" ? "Delivery & Assembly Address" : "Delivery Address"}</h2>
              <div className="space-y-4">
                <input required value={formData.delivery_address} onChange={e => setFormData({...formData, delivery_address: e.target.value})} placeholder={ph.street} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30" data-testid="checkout-street" />
                <div className="grid grid-cols-3 gap-3">
                  <input required value={formData.delivery_city} onChange={e => setFormData({...formData, delivery_city: e.target.value})} placeholder={ph.city} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30" data-testid="checkout-city" />
                  <input value={formData.delivery_state} onChange={e => setFormData({...formData, delivery_state: e.target.value})} placeholder={ph.state} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30 bg-gray-50" data-testid="checkout-state" />
                  <input required value={formData.delivery_zip} onChange={e => setFormData({...formData, delivery_zip: e.target.value.replace(/\D/g, '').slice(0, 5)})} placeholder={ph.zip} maxLength={5} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30" data-testid="checkout-zip" />
                </div>
                {formData.delivery_zip.length === 5 && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500" data-testid="tax-rate-display">
                    <MapPin size={12} /> Sales tax for {formData.delivery_zip}: {taxLoading ? "..." : `${taxRate}%`}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Billing Address */}
          {deliveryMethod !== "pickup" && (
            <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm" data-testid="billing-address-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-lg">Billing Address</h2>
              </div>
              <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer" data-testid="billing-same-checkbox">
                <input type="checkbox" checked={formData.billing_same_as_delivery} onChange={e => setFormData({...formData, billing_same_as_delivery: e.target.checked})} className="w-4 h-4 rounded border-gray-300 text-[#C05621] focus:ring-[#C05621]" />
                <span className="text-gray-600">Same as delivery address</span>
              </label>
              {!formData.billing_same_as_delivery && (
                <div className="space-y-4">
                  <input required value={formData.billing_address} onChange={e => setFormData({...formData, billing_address: e.target.value})} placeholder={ph.billStreet} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30" data-testid="billing-street" />
                  <div className="grid grid-cols-3 gap-3">
                    <input required value={formData.billing_city} onChange={e => setFormData({...formData, billing_city: e.target.value})} placeholder={ph.city} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30" data-testid="billing-city" />
                    <input value={formData.billing_state} onChange={e => setFormData({...formData, billing_state: e.target.value})} placeholder={ph.state} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30 bg-gray-50" data-testid="billing-state" />
                    <input required value={formData.billing_zip} onChange={e => setFormData({...formData, billing_zip: e.target.value.replace(/\D/g, '').slice(0, 5)})} placeholder={ph.zip} maxLength={5} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30" data-testid="billing-zip" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Payment Method Selection */}
          <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm" data-testid="payment-method-section">
            <h2 className="font-semibold text-lg mb-4">{T.paymentMethod}</h2>
            <div className="space-y-3">
              <button type="button" onClick={() => setPaymentMethod("stripe")}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${paymentMethod === "stripe" ? "border-[#C05621] bg-orange-50" : "border-gray-200 hover:border-gray-300"}`}
                data-testid="payment-stripe"
              >
                <div className="w-12 h-12 bg-[#635BFF] rounded-lg flex items-center justify-center flex-shrink-0">
                  <CreditCard size={22} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm text-[#1A202C]">Credit / Debit Card</p>
                  <p className="text-xs text-gray-500">Visa, Mastercard, Amex — powered by Stripe</p>
                </div>
                {paymentMethod === "stripe" && <Check size={20} className="text-[#C05621]" />}
              </button>

              <button type="button" onClick={() => setPaymentMethod("affirm")}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${paymentMethod === "affirm" ? "border-[#C05621] bg-orange-50" : "border-gray-200 hover:border-gray-300"}`}
                data-testid="payment-affirm"
              >
                <div className="w-12 h-12 bg-[#0FA0EA] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">A</span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm text-[#1A202C]">Affirm — Buy Now, Pay Later</p>
                  <p className="text-xs text-gray-500">Split into monthly payments. 0% interest available.</p>
                </div>
                {paymentMethod === "affirm" && <Check size={20} className="text-[#C05621]" />}
              </button>
              {paymentMethod === "affirm" && <AffirmSamplePanel total={total} lang={lang} />}

              <button type="button" onClick={() => setPaymentMethod("snap")}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${paymentMethod === "snap" ? "border-[#38A169] bg-green-50" : "border-gray-200 hover:border-gray-300"}`}
                data-testid="payment-snap"
              >
                <div className="w-12 h-12 bg-[#38A169] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">S</span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm text-[#1A202C]">Snap Finance — No Credit Needed</p>
                  <p className="text-xs text-gray-500">$0 down, 100 days same as cash. No credit check required.</p>
                </div>
                {paymentMethod === "snap" && <Check size={20} className="text-[#38A169]" />}
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
              <Shield size={12} /> All payments are secure and encrypted
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-2">
          <div className="bg-[#FDFBF7] rounded-xl p-6 sticky top-28" data-testid="order-summary">
            <h2 className="font-semibold text-lg mb-4">{T.orderSummary}</h2>
            <div className="space-y-3 text-sm max-h-48 overflow-y-auto">
              {(cart?.items || []).map(item => (
                <div key={item.product_id} className="flex justify-between"><span className="text-gray-600 line-clamp-1 flex-1">{item.product?.name} x{item.quantity}</span><span className="font-medium ml-2">${item.item_total?.toFixed(2)}</span></div>
              ))}
            </div>

            {/* Promo Code */}
            <div className="border-t border-gray-200 mt-4 pt-4 mb-3" data-testid="promo-code-section">
              <div className="flex gap-2">
                <input
                  value={promoCode} onChange={e => setPromoCode(e.target.value)}
                  placeholder="Promo code"
                  className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C05621]/30"
                  data-testid="promo-code-input"
                  disabled={!!promoApplied}
                />
                {promoApplied ? (
                  <button type="button" onClick={() => { setPromoApplied(null); setPromoCode(""); }} className="px-3 py-2 text-xs font-medium text-red-500 border border-red-200 rounded-lg hover:bg-red-50" data-testid="remove-promo-btn">Remove</button>
                ) : (
                  <button type="button" onClick={applyPromo} disabled={promoLoading || !promoCode.trim()} className="px-4 py-2 text-xs font-bold bg-[#2D3748] text-white rounded-lg hover:bg-[#1A202C] disabled:opacity-40" data-testid="apply-promo-btn">{promoLoading ? "..." : "Apply"}</button>
                )}
              </div>
              {promoApplied && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-[#38A169] font-medium" data-testid="promo-applied-msg">
                  <Check size={12} /> {promoApplied.description || promoApplied.code} applied — saving ${promoDiscount.toFixed(2)}
                </div>
              )}
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">{T.subtotal}</span><span>${subtotal.toFixed(2)}</span></div>
              {discount > 0 && <div className="flex justify-between text-[#38A169]"><span>{lang === "es" ? "Descuento por recoger (10%)" : "Pickup Discount (10%)"}</span><span>-${discount.toFixed(2)}</span></div>}
              {promoDiscount > 0 && <div className="flex justify-between text-[#C05621]"><span>{lang === "es" ? "Promo" : "Promo"} ({promoApplied?.code})</span><span>-${promoDiscount.toFixed(2)}</span></div>}
              {assemblyFee > 0 && <div className="flex justify-between"><span className="text-gray-600">{lang === "es" ? "Costo de instalación" : "Assembly Fee"}</span><span>+$99.00</span></div>}
              <div className="flex justify-between"><span className="text-gray-600">{T.delivery}</span><span className="text-[#38A169] font-medium">{T.free}</span></div>
              <div className="flex justify-between" data-testid="tax-line-item"><span className="text-gray-600">{T.tax} ({taxRate}%)</span><span>${taxAmount.toFixed(2)}</span></div>
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200"><span>{T.total}</span><span data-testid="checkout-total">${total.toFixed(2)}</span></div>
            </div>
            {/* Estimated Delivery */}
            {cart?.items?.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg" data-testid="delivery-estimate-checkout">
                <div className="flex items-center gap-2 text-sm">
                  <Truck size={14} className="text-blue-600" />
                  <span className="font-medium text-blue-700">{deliveryMethod === "pickup" ? (lang === "es" ? "Listo aprox." : "Est. Ready") : (lang === "es" ? "Entrega aprox." : "Est. Delivery")}: {estimatedRange}</span>
                </div>
                <p className="text-[10px] text-blue-500 mt-1 ml-6">{minLead}-{maxLead} {lang === "es" ? "días hábiles · Basado en el tiempo más largo de tu carrito" : "business days · Based on longest lead time in your cart"}</p>
              </div>
            )}
            <button type="submit" disabled={submitting || !cart?.items?.length} className={`w-full mt-4 py-3.5 text-white rounded-xl font-bold transition-all shadow-lg disabled:opacity-50 ${paymentMethod === "snap" ? "bg-[#38A169] hover:bg-[#2F855A]" : "bg-[#C05621] hover:bg-[#DD6B20]"}`} data-testid="place-order-btn">
              {submitting ? T.placingOrder : paymentMethod === "stripe" ? (lang === "es" ? `Pagar $${total.toFixed(2)} con Tarjeta` : `Pay $${total.toFixed(2)} with Card`) : paymentMethod === "affirm" ? (lang === "es" ? `Pagar con Affirm - $${total.toFixed(2)}` : `Pay with Affirm - $${total.toFixed(2)}`) : (lang === "es" ? `Solicitar con Snap - $${total.toFixed(2)}` : `Apply with Snap - $${total.toFixed(2)}`)}
            </button>
            {paymentMethod === "snap" && (
              <p className="text-[10px] text-center text-gray-400 mt-2">{lang === "es" ? "$0 enganche, sin crédito necesario, 100 días igual que en efectivo" : "$0 down, no credit needed, 100 days same as cash"}</p>
            )}
            <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-[11px] text-gray-400" data-testid="checkout-trust-strip">
              <Star size={12} className="text-[#F6AD55]" fill="currentColor" /> <strong className="text-gray-600">4.9</strong> {lang === "es" ? "en Google (150+ reseñas)" : "Google rating (150+ reviews)"} · {lang === "es" ? "Negocio familiar, 25+ años" : "Family-owned 25+ yrs"} · <Shield size={12} /> {lang === "es" ? "Pago seguro" : "Secure checkout"}
            </div>
            <CheckoutReviewsRotator lang={lang} />
          </div>
        </div>
      </form>
    </div>
  );
}

// ─── ORDER CONFIRMATION (Stripe redirect) ───
function OrderConfirmationPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const navigate = useNavigate();

  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");
  const paymentType = searchParams.get("payment");
  const paymentStatus = searchParams.get("status");

  useEffect(() => {
    const checkoutToken = searchParams.get("checkout_token");
    // Detect Affirm: explicit payment=affirm OR checkout_token present in URL (Affirm redirect strips payment param)
    const isAffirm = paymentType === "affirm" || (checkoutToken && checkoutToken !== "{checkout_token}");

    // Affirm payment confirmation
    if (isAffirm && orderId) {
      if (checkoutToken && checkoutToken !== "{checkout_token}") {
        // Affirm redirect flow — auto-authorize with the checkout_token
        setStatus("processing");
        axios.post(`${API}/payments/affirm/authorize?checkout_token=${checkoutToken}&order_id=${orderId}`)
          .then(r => {
            if (r.data.success) {
              setStatus("paid");
              setPaymentData({ payment_method: "affirm" });
            } else {
              setStatus("error");
              setPaymentData({ payment_method: "affirm" });
            }
          })
          .catch(() => {
            setStatus("error");
            setPaymentData({ payment_method: "affirm" });
          });
        return;
      }
      if (paymentStatus === "paid") {
        setStatus("paid");
        setPaymentData({ payment_method: "affirm" });
      } else {
        setStatus("pending");
        setPaymentData({ payment_method: "affirm" });
      }
      return;
    }

    // Snap Finance payment confirmation (polls order status)
    if (paymentType === "snap" && orderId) {
      setStatus("processing");
      let snapAttempts = 0;
      const maxSnapAttempts = 10;
      // Capture any applicationId Snap may append on redirect — gives us the
      // REAL applicationId before the webhook arrives. checkoutId tries are a
      // safety net (Snap sometimes uses different param names).
      const snapAppId =
        searchParams.get("applicationId") ||
        searchParams.get("application_id") ||
        searchParams.get("appId") ||
        searchParams.get("snap_application_id") ||
        "";
      const claimSnapAppId = async () => {
        if (!snapAppId) return;
        try {
          await axios.post(`${API}/payments/snap/claim-redirect`, {
            order_id: orderId,
            application_id: snapAppId,
          });
        } catch (e) {
          // Non-fatal — polling will still finalize the order shortly.
        }
      };
      const pollSnap = async () => {
        try {
          const r = await axios.get(`${API}/payments/snap/status/${orderId}`);
          setPaymentData({ payment_method: "snap", amount_total: (r.data.total || 0) * 100 });
          if (r.data.payment_status === "paid") {
            setStatus("paid");
            return;
          }
          snapAttempts++;
          if (snapAttempts < maxSnapAttempts) {
            setTimeout(pollSnap, 3000);
          } else {
            setStatus("snap_pending");
          }
        } catch {
          setStatus("snap_pending");
        }
      };
      claimSnapAppId().finally(pollSnap);
      return;
    }

    // Fallback: order_id present but no payment type identified (Affirm/Snap may strip URL params on redirect)
    if (orderId && !paymentType && !sessionId) {
      setStatus("processing");
      axios.get(`${API}/orders/${orderId}/payment-check`)
        .then(async r => {
          const method = r.data.payment_method;
          if (method === "affirm") {
            if (r.data.status === "paid" || r.data.payment_confirmed) {
              setStatus("paid");
            } else {
              setStatus("pending");
            }
            setPaymentData({ payment_method: "affirm" });
          } else if (method === "snap") {
            setPaymentData({ payment_method: "snap" });
            // Same Layer-3 trick — capture any applicationId from Snap's redirect.
            const snapAppId =
              searchParams.get("applicationId") ||
              searchParams.get("application_id") ||
              searchParams.get("appId") ||
              searchParams.get("snap_application_id") ||
              "";
            if (snapAppId) {
              try {
                await axios.post(`${API}/payments/snap/claim-redirect`, {
                  order_id: orderId,
                  application_id: snapAppId,
                });
              } catch (e) { /* non-fatal */ }
            }
            let snapAttempts = 0;
            const pollSnap = async () => {
              try {
                const sr = await axios.get(`${API}/payments/snap/status/${orderId}`);
                setPaymentData({ payment_method: "snap", amount_total: (sr.data.total || 0) * 100 });
                if (sr.data.payment_status === "paid") { setStatus("paid"); return; }
                snapAttempts++;
                if (snapAttempts < 10) { setTimeout(pollSnap, 3000); }
                else { setStatus("snap_pending"); }
              } catch { setStatus("snap_pending"); }
            };
            pollSnap();
          } else {
            setStatus("error");
          }
        })
        .catch(() => { setStatus("error"); });
      return;
    }

    // Stripe payment confirmation (polls session status)
    if (!sessionId) { setStatus("error"); return; }

    let attempts = 0;
    const maxAttempts = 5;

    const pollStatus = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        setPaymentData(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (r.data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          setStatus("pending");
        }
      } catch {
        setStatus("error");
      }
    };

    pollStatus();
  }, [sessionId, paymentType, orderId, paymentStatus, searchParams]);

  // GA4 purchase event — fire ONCE when payment is confirmed
  const [purchaseTracked, setPurchaseTracked] = useState(false);
  useEffect(() => {
    if (status !== "paid" || purchaseTracked) return;
    if (typeof window === "undefined" || !window.gtag) return;
    (async () => {
      try {
        // Pull the order details so we can populate items + total
        let order = null;
        if (orderId) {
          const r = await axios.get(`${API}/orders/${orderId}`).catch(() => null);
          order = r?.data || null;
        }
        const total = order?.total
          ?? (paymentData?.amount_total ? paymentData.amount_total / 100 : 0);
        const items = (order?.items || []).map(it => ({
          item_id: it.product_id || it.id || "",
          item_name: it.name || it.product_name || "",
          item_brand: it.brand || "",
          price: Number(it.price || 0),
          quantity: Number(it.quantity || 1),
        }));
        // Enhanced Conversions — Google hashes these client-side for better ad attribution
        if (order?.customer_email) {
          window.gtag("set", "user_data", {
            email: order.customer_email,
            phone_number: order.customer_phone || undefined,
            address: {
              first_name: order.first_name || undefined,
              last_name: order.last_name || undefined,
              postal_code: order.delivery_zip || undefined,
              country: "US",
            },
          });
        }
        window.gtag("event", "purchase", {
          transaction_id: orderId || sessionId || "",
          value: Number(total || 0),
          currency: "USD",
          payment_type: paymentData?.payment_method || paymentType || "stripe",
          items,
        });
        // Google Ads remarketing purchase
        window.gtag("event", "purchase", {
          send_to: AW_ID,
          transaction_id: orderId || sessionId || "",
          value: Number(total || 0),
          currency: "USD",
          items: items.map(it => ({ id: it.item_id, google_business_vertical: "retail" })),
        });
        // Meta Purchase — eventID = order id dedupes with the server-side Conversions API
        if (order?.customer_email) fbIdentify(order.customer_email, order.customer_phone);
        fbTrack("Purchase", {
          content_ids: items.map(it => it.item_id),
          content_type: "product",
          contents: items.map(it => ({ id: it.item_id, quantity: Number(it.quantity || 1) })),
          value: Number(total || 0),
          currency: "USD",
          num_items: items.length,
          delivery_category: order?.delivery_method === "pickup" ? "in_store" : "home_delivery",
        }, orderId || sessionId || "");
        setPurchaseTracked(true);
      } catch { /* noop */ }
    })();
  }, [status, orderId, sessionId, paymentType, paymentData, purchaseTracked]);

  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="stripe-confirmation">
      {status === "loading" && (
        <div>
          <div className="animate-spin w-12 h-12 border-3 border-[#C05621] border-t-transparent rounded-full mx-auto mb-6" />
          <h1 className="font-heading text-2xl font-bold text-[#1A202C] mb-2">Verifying your payment...</h1>
          <p className="text-gray-500">Please wait while we confirm your payment.</p>
        </div>
      )}
      {status === "paid" && (
        <div>
          <div className="bg-[#38A169] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"><Check size={32} /></div>
          <h1 className="font-heading text-3xl font-bold text-[#1A202C] mb-3">Payment Successful!</h1>
          {orderId && <p className="text-gray-600 mb-2">Order #{orderId.substring(0, 8)}</p>}
          {paymentData && <p className="text-2xl font-bold text-[#1A202C] mb-1">${(paymentData.amount_total / 100).toFixed(2)}</p>}
          <p className="text-gray-500 text-sm mt-4 mb-8">Your order is confirmed. We'll send a receipt to your email.</p>
          <button onClick={() => navigate("/")} className="px-8 py-3 bg-[#C05621] text-white rounded-lg font-semibold hover:bg-[#DD6B20] transition-colors" data-testid="continue-shopping-after-payment">Continue Shopping</button>
        </div>
      )}
      {status === "pending" && (
        <div>
          <div className="bg-[#D69E2E] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"><Clock size={32} /></div>
          <h1 className="font-heading text-2xl font-bold text-[#1A202C] mb-3">Payment Processing</h1>
          <p className="text-gray-500 mb-8">Your payment is being processed. You'll receive a confirmation email shortly.</p>
          <button onClick={() => navigate("/")} className="px-8 py-3 bg-[#C05621] text-white rounded-lg font-semibold hover:bg-[#DD6B20] transition-colors">Go Home</button>
        </div>
      )}
      {status === "processing" && (
        <div>
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#C05621] mx-auto mb-6" />
          <h1 className="font-heading text-2xl font-bold text-[#1A202C] mb-3">Processing Payment...</h1>
          <p className="text-gray-500 mb-3">Please wait while we confirm your payment. This may take a moment.</p>
        </div>
      )}
      {(status === "error" || status === "expired") && (
        <div>
          <div className="bg-[#E53E3E] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"><AlertCircle size={32} /></div>
          <h1 className="font-heading text-2xl font-bold text-[#1A202C] mb-3">{status === "expired" ? "Session Expired" : "Payment Error"}</h1>
          <p className="text-gray-500 mb-8">{status === "expired" ? "Your payment session expired. Please try again." : "Something went wrong. Please try again or contact us."}</p>
          <button onClick={() => navigate("/checkout")} className="px-8 py-3 bg-[#C05621] text-white rounded-lg font-semibold hover:bg-[#DD6B20] transition-colors">Try Again</button>
        </div>
      )}
      {status === "snap_pending" && (
        <div>
          <div className="bg-[#D69E2E] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"><Clock size={32} /></div>
          <h1 className="font-heading text-2xl font-bold text-[#1A202C] mb-3">Snap Finance Application Submitted</h1>
          <p className="text-gray-500 mb-3">Your Snap Finance application is being reviewed. You'll receive a text/email from Snap Finance with your approval status.</p>
          <p className="text-gray-500 text-sm mb-8">Once approved, your order will be confirmed automatically. If you have questions, call us at <a href="tel:+12098274076" className="text-[#C05621] font-semibold">(209) 827-4076</a>.</p>
          <button onClick={() => navigate("/")} className="px-8 py-3 bg-[#C05621] text-white rounded-lg font-semibold hover:bg-[#DD6B20] transition-colors">Continue Shopping</button>
        </div>
      )}
    </div>
  );
}

// ─── ALL CATEGORIES PAGE ───
function AllCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const { lang } = useTranslation();

  useEffect(() => {
    axios.get(`${API}/products/categories`).then(r => setCategories(r.data)).catch(console.error);
    const canonical = lang === "es"
      ? "https://labodegabayfurniture.com/es/categories"
      : "https://labodegabayfurniture.com/categories";
    updatePageSEO({
      title: lang === "es"
        ? "Categorías de Muebles | Recámara, Sala, Comedor, Colchones | La Bodega Bay"
        : "Shop Furniture by Category | Bedroom, Living Room, Dining, Mattress | La Bodega Bay",
      description: lang === "es"
        ? "Explora todas las categorías de muebles: recámara, sala, comedor, oficina en casa, colchones, exterior y accesorios. Más de 15,000 productos con entrega gratis."
        : "Browse every furniture category: bedroom, living room, dining, home office, mattress, outdoor, and accents. Over 15,000 products with free delivery to the Central Valley.",
      canonical,
      ogType: "website",
    });
  }, [lang]);

  const categoryData = {
    "Bedroom": { image: "https://ashleyfurniture.scene7.com/is/image/AshleyFurniture/B947-31-36-46-56-58-93%282%29-97-SW-P1-KO?wid=800&hei=800", desc: "Beds, dressers, nightstands, and complete bedroom sets in every size" },
    "Living Room": { image: "https://ashleyfurniture.scene7.com/is/image/AshleyFurniture/30901-64-46%284%29-77-65-SW-P1-KO?wid=800&hei=800", desc: "Sofas, sectionals, recliners, accent chairs, and tables" },
    "Dining Room": { image: "https://ashleyfurniture.scene7.com/is/image/AshleyFurniture/D546-35-01%288%29-60-SW-P1-KO?wid=800&hei=800", desc: "Dining sets by piece count, tables, chairs, bar furniture" },
    "Home Office": { image: "https://ashleyfurniture.scene7.com/is/image/AshleyFurniture/H743-34-34R-70%284%29-134-SW-P1-KO?wid=800&hei=800", desc: "Desks, office chairs, bookcases, and storage" },
    "Mattress": { image: "https://ashleyfurniture.scene7.com/is/image/AshleyFurniture/M52431-ANGLE-SW-P1-KO?wid=800&hei=800", desc: "Mattresses in Twin, Full, Queen, King, and Cal King" },
    "Outdoor": { image: "https://ashleyfurniture.scene7.com/is/image/AshleyFurniture/P791-854-851-846%283%29-SW-P1-KO?wid=800&hei=800", desc: "Weather-resistant outdoor furniture for your patio" },
    "Accents": { image: "https://assets.coastercenter.com/nextgenimages//950318/950318_03.jpg", desc: "Lamps, rugs, pillows, wall art, bar cabinets, and decor" },
  };

  return (
    <div data-testid="all-categories-page">
      <section className="bg-[#2D3748] py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-3">Shop by Category</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">Browse our collection of over 15,000 products from top brands. Pick a category to start shopping.</p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((cat) => {
            const data = categoryData[cat.name] || { image: "https://placehold.co/600x400/F7FAFC/718096?text=" + cat.name, desc: `Browse ${cat.name.toLowerCase()} furniture` };
            return (
              <Link key={cat.name} to={`/category/${encodeURIComponent(cat.name)}`}
                className="group relative overflow-hidden rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300"
                data-testid={`cat-card-${cat.name.toLowerCase().replace(/\s/g,'-')}`}
              >
                <div className="aspect-[3/2] overflow-hidden">
                  <img loading="lazy" decoding="async" src={data.image} alt={cat.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-1">{cat.name}</h2>
                  <p className="text-gray-300 text-sm mb-2">{data.desc}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-white/70 text-sm">{cat.count.toLocaleString()} products</span>
                    <span className="text-[#DD6B20] font-semibold text-sm flex items-center gap-1">Shop Now <ArrowRight size={14} /></span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// ─── CATEGORY LANDING PAGE ───
function CategoryLandingPage() {
  const { categoryName } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchStatus, setFetchStatus] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  const navigate = useNavigate();

  const category = decodeURIComponent(categoryName || "").replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  useEffect(() => {
    setLoading(true);
    setFetchStatus(null);
    axios.get(`${API}/products/category/${encodeURIComponent(category)}/subcategories`)
      .then(r => setData(r.data))
      .catch((err) => {
        const status = err?.response?.status || 0;
        setFetchStatus(status);
        console.error(err);
        if (status !== 404 && retryTick === 0) setTimeout(() => setRetryTick(1), 1500);
      })
      .finally(() => setLoading(false));
    // SEO meta tags for category page
    const desc = categoryDescriptions[category] || `Shop ${category} furniture at La Bodega Bay Furniture. Free delivery to Central Valley & Bay Area.`;
    updatePageSEO({
      title: `${category} Furniture | Free Delivery | La Bodega Bay Furniture`,
      description: `${desc.substring(0, 140)} Free delivery. $0 down financing. Family-owned 25+ years.`,
      canonical: `https://labodegabayfurniture.com/category/${encodeURIComponent(category)}`,
      ogType: "website",
    });
  }, [category, retryTick]);

  const categoryDescriptions = {
    "Bedroom": "Create your dream sanctuary. From complete bedroom sets to individual nightstands, dressers, and beds in every size — find the perfect pieces to transform your bedroom.",
    "Living Room": "Make your living space unforgettable. Shop sofas, sectionals, recliners, coffee tables, TV stands, and accent pieces to design the perfect gathering spot.",
    "Dining Room": "Gather around the table. Explore dining sets by piece count, individual tables and chairs, counter height options, bar furniture, and storage pieces.",
    "Home Office": "Work smarter, work in style. Find desks, office chairs, bookcases, and storage solutions for a productive and organized home office.",
    "Mattress": "Sleep better tonight. Shop mattresses by size — Twin, Full, Queen, King, and Cal King — from top brands with fast delivery.",
    "Outdoor": "Enjoy the outdoors in style. Weather-resistant furniture for your patio, deck, or garden.",
    "Accents": "Complete your space with the perfect finishing touches. Lamps, rugs, accent pillows, wall art, bar cabinets, and home decor.",
  };

  const categoryImages = {
    "Bedroom": "https://ashleyfurniture.scene7.com/is/image/AshleyFurniture/B947-31-36-46-56-58-93%282%29-97-SW-P1-KO?wid=1200&hei=1200",
    "Living Room": "https://ashleyfurniture.scene7.com/is/image/AshleyFurniture/30901-64-46%284%29-77-65-SW-P1-KO?wid=1200&hei=1200",
    "Dining Room": "https://ashleyfurniture.scene7.com/is/image/AshleyFurniture/D546-35-01%288%29-60-SW-P1-KO?wid=1200&hei=1200",
    "Home Office": "https://ashleyfurniture.scene7.com/is/image/AshleyFurniture/H743-34-34R-70%284%29-134-SW-P1-KO?wid=1200&hei=1200",
    "Mattress": "https://ashleyfurniture.scene7.com/is/image/AshleyFurniture/M52431-ANGLE-SW-P1-KO?wid=1200&hei=1200",
    "Outdoor": "https://ashleyfurniture.scene7.com/is/image/AshleyFurniture/P791-854-851-846%283%29-SW-P1-KO?wid=1200&hei=1200",
    "Accents": "https://assets.coastercenter.com/nextgenimages//950318/950318_03.jpg",
  };

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <div className="animate-pulse space-y-8">
        <div className="h-48 bg-gray-200 rounded-2xl" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="h-48 bg-gray-200 rounded-xl" />)}
        </div>
      </div>
    </div>
  );

  if (!data) {
    if (fetchStatus !== 404) {
      // Transient API failure — stay indexable, never render "Category Not Found" for crawlers
      return (
        <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="category-temporarily-unavailable">
          <h1 className="text-3xl font-bold mb-3">{category} Furniture</h1>
          <p className="text-gray-600 mb-8">This category is loading slowly right now. Please try again in a moment.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button onClick={() => setRetryTick(t => t + 1)} className="px-5 py-2.5 bg-[#C05621] text-white rounded-lg font-semibold" data-testid="category-retry-btn">Try again</button>
            <Link to="/categories" className="px-5 py-2.5 border border-gray-200 rounded-lg">View all categories</Link>
          </div>
        </div>
      );
    }
    if (typeof document !== "undefined") {
      let robotsMeta = document.querySelector('meta[name="robots"]');
      if (!robotsMeta) { robotsMeta = document.createElement("meta"); robotsMeta.name = "robots"; document.head.appendChild(robotsMeta); }
      robotsMeta.content = "noindex,follow";
      document.title = "Category Not Found | La Bodega Bay Furniture";
    }
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="category-not-found">
        <h1 className="text-3xl font-bold mb-3">Category not found</h1>
        <p className="text-gray-600 mb-8">Browse all our furniture categories instead:</p>
        <Link to="/categories" className="px-5 py-2.5 bg-[#C05621] text-white rounded-lg font-semibold">View all categories</Link>
      </div>
    );
  }

  return (
    <div data-testid="category-landing-page">
      {/* Hero Banner */}
      <section className="relative h-[35vh] md:h-[45vh] overflow-hidden">
        <img loading="lazy" decoding="async" src={categoryImages[category] || "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1200"} alt={category} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-sm text-gray-300 mb-2">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight size={14} />
            <span className="text-white font-medium">{category}</span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-3">{category}</h1>
          <p className="text-gray-200 text-lg max-w-2xl leading-relaxed">{categoryDescriptions[category] || `Browse our complete ${category.toLowerCase()} collection.`}</p>
          <p className="text-gray-400 text-sm mt-2">{data.total_products.toLocaleString()} products across {data.subcategories.length} categories</p>
        </div>
      </section>

      {/* Subcategory Grid */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A202C]">Shop {category} by Category</h2>
          <button onClick={() => navigate(`/shop?category=${encodeURIComponent(category)}`)} className="text-[#C05621] font-semibold text-sm hover:underline flex items-center gap-1" data-testid="view-all-link">
            View All {data.total_products.toLocaleString()} Products <ArrowRight size={16} />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {data.subcategories.map((sub) => (
            <Link
              key={sub.name}
              to={`/shop?category=${encodeURIComponent(category)}&subcategory=${encodeURIComponent(sub.name)}`}
              className="group relative overflow-hidden rounded-xl bg-[#F7FAFC] border border-gray-100 hover:shadow-lg transition-all duration-300"
              data-testid={`subcat-${sub.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="aspect-square overflow-hidden">
                <img loading="lazy" decoding="async" src={sub.image || "https://placehold.co/300x300/F7FAFC/718096?text=" + encodeURIComponent(sub.name)}
                  alt={sub.name}
                  className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4">
                <h3 className="font-heading text-sm md:text-base font-bold text-[#1A202C] group-hover:text-[#C05621] transition-colors">{sub.name}</h3>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-gray-500">{sub.count} products</p>
                  {sub.min_price > 0 && <p className="text-xs text-[#38A169] font-medium">From ${sub.min_price.toFixed(0)}</p>}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* SEO Content */}
        <div className="mt-16 pt-12 border-t border-gray-100 max-w-3xl">
          <h2 className="font-heading text-xl font-bold text-[#1A202C] mb-4">{category} Furniture at La Bodega Bay</h2>
          <p className="text-gray-600 leading-relaxed text-sm mb-4">
            {category === "Bedroom" && "Transform your bedroom into a personal retreat with our curated selection of bedroom furniture. Whether you're looking for a complete bedroom set in twin, full, queen, king, or California king size, or individual pieces like nightstands, dressers, chests, and mirrors — we have everything you need. Shop from top brands like Coaster and Homelegance with free delivery in the Bay Area and Central Valley."}
            {category === "Living Room" && "Design the perfect living room with our extensive collection of sofas, sectionals, recliners, and accent furniture. Choose from power reclining options, sleeper sofas, and stylish accent chairs. Complete your space with coffee tables, end tables, TV stands, and decorative lighting. All backed by our price match guarantee and easy financing options."}
            {category === "Dining Room" && "Create the perfect dining experience with our selection of dining sets from 3-piece to 10-piece configurations. Find individual dining tables, chairs, counter height options, bar furniture, and elegant servers and buffets. Whether casual or formal, we have the right dining furniture for every home and budget."}
            {category === "Home Office" && "Build a productive workspace with our home office furniture collection. From executive desks to space-saving solutions, ergonomic office chairs, and bookcases for storage and display — everything you need for a comfortable and efficient home office."}
            {category === "Mattress" && "Find your perfect sleep with our mattress collection organized by size. Shop twin, full, queen, king, and California king mattresses from trusted brands. All mattresses include free delivery and our price match guarantee. Easy financing available with Affirm and Snap Finance — no credit needed."}
          </p>
          <div className="flex flex-wrap gap-3">
            <span className="flex items-center gap-1 text-xs text-gray-500"><Truck size={12} /> Free Delivery</span>
            <span className="flex items-center gap-1 text-xs text-gray-500"><Shield size={12} /> Price Match Guarantee</span>
            <span className="flex items-center gap-1 text-xs text-gray-500"><CreditCard size={12} /> Easy Financing</span>
            <span className="flex items-center gap-1 text-xs text-gray-500"><Store size={12} /> Pickup & Save 10%</span>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─── ABOUT PAGE ───
function AboutPage() {
  const { lang } = useTranslation();
  useEffect(() => {
    const canonical = lang === "es"
      ? "https://labodegabayfurniture.com/es/about"
      : "https://labodegabayfurniture.com/about";
    updatePageSEO({
      title: lang === "es"
        ? "Nosotros | La Bodega Bay Furniture — Empresa Familiar desde 2000"
        : "About Us | La Bodega Bay Furniture — Family-Owned Since 2000 in Los Banos, CA",
      description: lang === "es"
        ? "Tienda de muebles familiar en Los Banos y Newman, CA desde el año 2000. Más de 25 años sirviendo al Valle Central con muebles de calidad, entrega gratis y financiamiento sin crédito."
        : "Family-owned furniture store in Los Banos and Newman, CA since 2000. Over 25 years serving the Central Valley with quality furniture, free delivery, and no-credit financing.",
      canonical,
      ogType: "website",
    });
  }, [lang]);
  const linkPrefix = lang === "es" ? "/es" : "";
  return (
    <div data-testid="about-page">
      <section className="relative h-[50vh] overflow-hidden">
        <img loading="lazy" decoding="async" src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1400" alt={lang === "es" ? "Sala de exhibición de muebles" : "Furniture showroom"} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="font-heading text-4xl md:text-6xl font-bold mb-4">{lang === "es" ? "Nuestra Historia" : "Our Story"}</h1>
            <p className="text-xl text-gray-200">{lang === "es" ? "Empresa Familiar desde 2000 — Sirviéndote por más de 25 años" : "Proudly Family-Owned Since 2000 — Serving You for Over 25 Years"}</p>
          </div>
        </div>
      </section>
      <section className="max-w-4xl mx-auto px-4 py-16 space-y-8">
        <div className="text-center">
          <h2 className="font-heading text-3xl font-bold text-[#1A202C] mb-4">{lang === "es" ? "Entregando Muebles de Calidad al Bay Area desde el 2000" : "Delivering Quality Furniture to the Bay Area Since 2000"}</h2>
          <p className="text-gray-600 leading-relaxed text-lg">
            {lang === "es"
              ? <>Desde el 2000, La Bodega Bay Furniture ha sido una empresa familiar y pilar de Los Banos. Orgullosamente la tienda de muebles más antigua del pueblo, nuestro compromiso con <strong>Calidad sobre Cantidad</strong> define nuestro espíritu y es la razón por la que nuestros clientes regresan una y otra vez a la mueblería de confianza de la comunidad.</>
              : <>Since 2000, La Bodega Bay Furniture has been a family-owned and operated cornerstone in Los Banos. Proudly holding the title of the longest-standing furniture store in town, our commitment to <strong>Quality over Quantity</strong> defines our ethos and drives why customers keep returning to our trusted furniture outlet.</>}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 py-8">
          {(lang === "es" ? [
            { icon: <Users size={32} />, title: "Valores Familiares", desc: "Tratamos a cada cliente como familia. Nuestro equipo está dedicado a ayudarte a encontrar el mueble perfecto." },
            { icon: <Award size={32} />, title: "25 Años Fuertes", desc: "Un cuarto de siglo sirviendo al Valle Central con muebles de calidad a precios accesibles." },
            { icon: <Star size={32} />, title: "Calificación 4.9/5", desc: "Confianza de cientos de familias. Lee nuestras 150+ reseñas en Google y descubre por qué nos aman." },
          ] : [
            { icon: <Users size={32} />, title: "Family Values", desc: "We treat every customer like family. Our team is dedicated to helping you find the perfect furniture." },
            { icon: <Award size={32} />, title: "25 Years Strong", desc: "A quarter century of serving the Central Valley with quality furniture at affordable prices." },
            { icon: <Star size={32} />, title: "4.9/5 Rating", desc: "Trusted by hundreds of families. Read our 150+ Google reviews to see why customers love us." },
          ]).map((item, i) => (
            <div key={i} className="text-center p-6">
              <div className="text-[#C05621] flex justify-center mb-4">{item.icon}</div>
              <h3 className="font-bold text-lg mb-2">{item.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
        <div className="bg-[#FDFBF7] rounded-2xl p-8">
          <h3 className="font-heading text-2xl font-bold mb-4">{lang === "es" ? "Nuestras Marcas" : "Our Brands"}</h3>
          <p className="text-gray-600 mb-4">{lang === "es" ? "Trabajamos con los fabricantes de muebles más confiables de la industria:" : "We partner with the most trusted furniture manufacturers in the industry:"}</p>
          <div className="grid grid-cols-3 gap-6">
            {["Ashley Furniture", "Homelegance", "Coaster"].map(brand => (
              <Link key={brand} to={`${linkPrefix}/shop?brand=${brand.split(' ')[0]}`} className="bg-white rounded-xl p-6 text-center border border-gray-100 hover:border-[#C05621] hover:shadow-md transition-all">
                <p className="font-bold text-[#2D3748]">{brand}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// ─── CONTACT PAGE ───
function ContactPage() {
  const { lang } = useTranslation();
  useEffect(() => {
    const canonical = lang === "es"
      ? "https://labodegabayfurniture.com/es/contact"
      : "https://labodegabayfurniture.com/contact";
    updatePageSEO({
      title: lang === "es" ? "Contacto | La Bodega Bay Furniture — Los Banos, CA (209) 827-4076" : "Contact Us | La Bodega Bay Furniture — Los Banos, CA (209) 827-4076",
      description: lang === "es"
        ? "Contáctanos en Los Banos (209) 827-4076 o Newman (209) 243-9020. Tienda de muebles familiar abierta Lun-Sáb 10am-7pm, Dom 10am-5pm."
        : "Contact La Bodega Bay Furniture in Los Banos (209) 827-4076 or Newman (209) 243-9020. Family-owned furniture store. Mon-Sat 10am-7pm, Sun 10am-5pm.",
      canonical,
      ogType: "website",
    });
  }, [lang]);
  return (
    <div className="max-w-4xl mx-auto px-4 py-16" data-testid="contact-page">
      <h1 className="font-heading text-4xl font-bold text-[#1A202C] mb-8 text-center">{lang === "es" ? "Contáctanos" : "Contact Us"}</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {[
          { name: "Los Banos", address: "2225 East Pacheco Blvd, Los Banos, CA 93635", phone: "(209) 827-4076" },
          { name: "Newman", address: "1925 N Street, Newman, CA 95360", phone: "(209) 243-9020" },
        ].map((loc, i) => (
          <div key={i} className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm">
            <h2 className="font-heading text-2xl font-bold mb-4">{loc.name}</h2>
            <div className="space-y-3 text-gray-600">
              <p className="flex items-start gap-2"><MapPin size={18} className="text-[#C05621] mt-0.5" /> {loc.address}</p>
              <p className="flex items-center gap-2"><Phone size={18} className="text-[#C05621]" /> <a href={`tel:${loc.phone.replace(/\D/g,'')}`} className="hover:text-[#C05621]">{loc.phone}</a></p>
              <p className="flex items-center gap-2"><Clock size={18} className="text-[#C05621]" /> {lang === "es" ? "Lun-Sáb: 10am-7pm | Dom: 10am-5pm" : "Mon-Sat: 10am-7pm | Sun: 10am-5pm"}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SEO PAGES ───
function MattressStorePage() {
  const navigate = useNavigate();
  const { lang } = useTranslation();
  const linkPrefix = lang === "es" ? "/es" : "";
  const [openFaq, setOpenFaq] = useState(null);

  // FAQ data (visible accordion + JSON-LD FAQPage schema injected for AEO/Google rich results)
  const FAQ_EN = [
    { q: "Where is the best mattress store in Los Banos, CA?", a: "La Bodega Bay Furniture at 2225 East Pacheco Blvd, Los Banos, CA 93635 is a family-owned mattress store serving Los Banos and the Central Valley since 2000. We carry hybrid, memory foam, and innerspring mattresses from Homelegance, Coaster, and Ashley with $0 down financing and free local delivery." },
    { q: "Do you offer no-credit-needed mattress financing?", a: "Yes. Snap Finance approves most applicants with no credit check, $0 down, and 100 days same as cash. Affirm offers 0% APR plans for qualified buyers with no impact to credit score for pre-qualification. Apply in 30 seconds in store or online." },
    { q: "How fast is mattress delivery to Los Banos?", a: "Most in-stock mattresses are delivered to Los Banos within 1–3 business days. We deliver free across the Central Valley including Newman, Dos Palos, Firebaugh, Santa Nella, Gustine, Merced, Atwater, and Turlock. Optional setup and old-mattress haul-away are $99." },
    { q: "What types of mattresses do you sell?", a: "We carry queen, king, California king, full, and twin mattresses in three constructions: hybrid (coil + foam, best all-around), memory foam (pressure relief, motion isolation), and innerspring (firm, cool sleeping). All major sizes available with adjustable bases." },
    { q: "Can I try the mattress before I buy it?", a: "Yes — visit our Los Banos showroom at 2225 East Pacheco Blvd or our Newman location at 1925 N Street. Online-only mattress retailers can't offer this. Take 15 minutes to test firmness in person; our staff will guide you to the right firmness for your sleep position." },
    { q: "Do you price match other mattress stores?", a: "Yes. If you find an identical mattress (same brand, model, size) at a lower advertised price from an authorized retailer, we match it AND give you 5% off the difference. Pickup in-store saves an additional 10%." },
    { q: "What's the difference between a local mattress store and a chain?", a: "Local stores like ours offer same-day pickup, in-person firmness testing, no-credit financing approved on the spot, and the ability to walk in and talk to a real person. Chains like Mattress Firm have more locations but slower delivery, less flexible financing, and centralized customer service." },
  ];
  const FAQ_ES = [
    { q: "¿Cuál es la mejor tienda de colchones en Los Banos, CA?", a: "La Bodega Bay Furniture en 2225 East Pacheco Blvd, Los Banos, CA 93635 es una tienda de colchones familiar que sirve a Los Banos y el Valle Central desde el 2000. Tenemos colchones híbridos, de memory foam y de resortes de Homelegance, Coaster y Ashley con financiamiento $0 enganche y entrega local gratis." },
    { q: "¿Ofrecen financiamiento de colchones sin crédito?", a: "Sí. Snap Finance aprueba a la mayoría sin chequeo de crédito, $0 enganche, y 100 días igual que en efectivo. Affirm ofrece planes de 0% APR para compradores calificados sin afectar tu crédito para la pre-calificación. Aplica en 30 segundos en tienda o en línea." },
    { q: "¿Qué tan rápida es la entrega de colchones a Los Banos?", a: "La mayoría de colchones en stock se entregan a Los Banos en 1 a 3 días hábiles. Entregamos gratis en todo el Valle Central incluyendo Newman, Dos Palos, Firebaugh, Santa Nella, Gustine, Merced, Atwater y Turlock. Instalación y retiro del colchón viejo opcional por $99." },
    { q: "¿Qué tipos de colchones venden?", a: "Tenemos colchones queen, king, California king, full y twin en tres construcciones: híbrido (resortes + espuma, mejor para todo), memory foam (alivio de presión, aislamiento de movimiento) e innerspring (firme, fresco al dormir). Todos los tamaños principales con bases ajustables." },
    { q: "¿Puedo probar el colchón antes de comprar?", a: "Sí — visita nuestra tienda en Los Banos en 2225 East Pacheco Blvd o nuestra ubicación de Newman en 1925 N Street. Las tiendas en línea no ofrecen esto. Toma 15 minutos para probar la firmeza en persona; nuestro personal te guiará a la firmeza correcta para tu posición de sueño." },
    { q: "¿Igualan precios de otras tiendas de colchones?", a: "Sí. Si encuentras un colchón idéntico (misma marca, modelo, tamaño) a un precio anunciado más bajo de un vendedor autorizado, lo igualamos Y te damos 5% de descuento sobre la diferencia. Recoger en tienda ahorra 10% adicional." },
    { q: "¿Cuál es la diferencia entre una tienda local de colchones y una cadena?", a: "Las tiendas locales como la nuestra ofrecen recogida el mismo día, prueba de firmeza en persona, financiamiento sin crédito aprobado en el momento, y la capacidad de entrar y hablar con una persona real. Las cadenas como Mattress Firm tienen más ubicaciones pero entregas más lentas, financiamiento menos flexible y servicio al cliente centralizado." },
  ];
  const faqs = lang === "es" ? FAQ_ES : FAQ_EN;

  useEffect(() => {
    const canonical = lang === "es"
      ? "https://labodegabayfurniture.com/es/mattress-store"
      : "https://labodegabayfurniture.com/mattress-store";
    const title = lang === "es"
      ? "Tienda de Colchones en Los Banos, CA | Hybrid, Memory Foam, Resortes | Sin Crédito"
      : "Mattress Store Los Banos, CA | Hybrid, Memory Foam & Innerspring | $0 Down";
    const description = lang === "es"
      ? "Tienda de colchones familiar en Los Banos, CA. Colchones queen, king, twin de Homelegance, Coaster, Ashley. $0 enganche, sin crédito, entrega gratis al Valle Central. Garantía del mejor precio."
      : "Family-owned mattress store in Los Banos, CA. Queen, king, twin mattresses from Homelegance, Coaster & Ashley. $0 down financing, no credit needed, free Central Valley delivery. Price match guarantee.";

    updatePageSEO({
      title,
      description,
      canonical,
      ogType: "website",
      jsonLd: [
        // FAQPage schema — surfaces in Google rich results AND extracted by ChatGPT/Perplexity for direct quoting.
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQ_EN.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        },
        // LocalBusiness schema specifically positioning us as a mattress store in Los Banos.
        {
          "@context": "https://schema.org",
          "@type": "FurnitureStore",
          "@id": "https://labodegabayfurniture.com/mattress-store#mattressstore",
          "name": "La Bodega Bay Furniture - Mattress Store",
          "url": canonical,
          "telephone": "+12098274076",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "2225 East Pacheco Blvd",
            "addressLocality": "Los Banos",
            "addressRegion": "CA",
            "postalCode": "93635",
            "addressCountry": "US"
          },
          "geo": { "@type": "GeoCoordinates", "latitude": 37.0583, "longitude": -120.8497 },
          "areaServed": [
            { "@type": "City", "name": "Los Banos" },
            { "@type": "City", "name": "Newman" },
            { "@type": "City", "name": "Dos Palos" },
            { "@type": "City", "name": "Firebaugh" },
            { "@type": "City", "name": "Santa Nella" },
            { "@type": "City", "name": "Gustine" },
            { "@type": "City", "name": "Merced" },
            { "@type": "City", "name": "Atwater" },
            { "@type": "City", "name": "Turlock" },
            { "@type": "City", "name": "Patterson" }
          ],
          "makesOffer": [
            { "@type": "Offer", "name": "Hybrid Mattress" },
            { "@type": "Offer", "name": "Memory Foam Mattress" },
            { "@type": "Offer", "name": "Innerspring Mattress" },
            { "@type": "Offer", "name": "Adjustable Base" }
          ],
          "priceRange": "$$",
          "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "150", "bestRating": "5" }
        }
      ]
    });
  }, [lang]);

  return (
    <div data-testid="mattress-store-page">
      <section className="bg-[#2D3748] py-16"><div className="max-w-4xl mx-auto px-4 text-center text-white">
        <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">{lang === "es" ? "La Mejor Tienda de Colchones en Los Banos, CA" : "Mattress Store in Los Banos, CA — Family-Owned Since 2000"}</h1>
        <p className="text-gray-300 text-lg mb-6">{lang === "es" ? "Compra colchones híbridos, de memory foam y de resortes con financiamiento $0 enganche" : "Hybrid, Memory Foam & Innerspring Mattresses · $0 Down Financing · Free Central Valley Delivery"}</p>
        <button onClick={() => navigate(`${linkPrefix}/category/Mattress`)} data-testid="mattress-shop-all-btn" className="px-8 py-3 bg-[#C05621] text-white rounded-lg font-semibold hover:bg-[#DD6B20]">{lang === "es" ? "Ver todos los colchones" : "Shop All Mattresses"}</button>
      </div></section>

      <section className="max-w-4xl mx-auto px-4 py-12 space-y-10">
        <div><h2 className="font-heading text-2xl font-bold mb-4">{lang === "es" ? "Las Mejores Marcas de Colchones Cerca de Ti" : "Top Mattress Brands Near You"}</h2>
          <p className="text-gray-600 leading-relaxed">{lang === "es"
            ? <>Somos tu destino local para <strong>colchones de calidad en Los Banos</strong>, con marcas como <strong>Homelegance, Coaster y Ashley</strong>. Ya sea que necesites una base ajustable para dolor de espalda o un colchón de memory foam con tecnología refrescante para los veranos del Valle Central, tenemos lo que buscas. Nuestros expertos en colchones en nuestra tienda de Los Banos te ayudarán a encontrar la solución perfecta para dormir.</>
            : <>We are your local destination for <strong>quality mattresses in Los Banos, CA</strong>, with brands like <strong>Homelegance, Coaster, and Ashley</strong>. Whether you need an adjustable base for back pain or a cooling memory foam mattress for those Central Valley summers, we have it. Our mattress experts at our Los Banos showroom will help you find the perfect sleep solution.</>}</p>
        </div>

        {/* Mattress type buying guide — improves AEO + on-page word count + dwell time */}
        <div>
          <h2 className="font-heading text-2xl font-bold mb-4">{lang === "es" ? "Tipos de Colchones — Guía Rápida" : "Mattress Types — Quick Buying Guide"}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-gray-100 rounded-xl p-5"><h3 className="font-bold text-base mb-2">{lang === "es" ? "Híbrido" : "Hybrid"}</h3><p className="text-xs text-gray-600 leading-relaxed">{lang === "es" ? "Resortes embolsados + capas de espuma. Mejor opción para parejas y para dormir de lado o boca arriba. Buena ventilación." : "Pocketed coils + foam layers. Best all-around pick for couples and side/back sleepers. Strong airflow keeps you cool."}</p></div>
            <div className="border border-gray-100 rounded-xl p-5"><h3 className="font-bold text-base mb-2">{lang === "es" ? "Memory Foam" : "Memory Foam"}</h3><p className="text-xs text-gray-600 leading-relaxed">{lang === "es" ? "Alivio de presión y aislamiento de movimiento. Ideal si tu pareja se mueve mucho o si tienes dolor articular." : "Pressure relief & motion isolation. Ideal if your partner moves a lot or you have joint pain. Choose gel-infused for hot Central Valley summers."}</p></div>
            <div className="border border-gray-100 rounded-xl p-5"><h3 className="font-bold text-base mb-2">{lang === "es" ? "Resortes (Innerspring)" : "Innerspring"}</h3><p className="text-xs text-gray-600 leading-relaxed">{lang === "es" ? "Firme y fresco. Mejor para personas que duermen calientes o que prefieren un colchón tradicional." : "Firm, bouncy, breathable. Best for hot sleepers or anyone who wants a traditional firm feel under $700."}</p></div>
          </div>
        </div>

        <div><h2 className="font-heading text-2xl font-bold mb-4">{lang === "es" ? "Sin Crédito y Financiamiento $0 Enganche para Colchones" : "No Credit Needed & $0 Down Mattress Financing"}</h2>
          <p className="text-gray-600 mb-4">{lang === "es" ? "Creemos que todos merecen un colchón de calidad. Por eso ofrecemos financiamiento sin crédito para colchones en Los Banos:" : "We believe everyone deserves a quality mattress. That's why we offer no credit check mattress financing in Los Banos:"}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#38A169]/10 rounded-xl p-5"><h3 className="font-bold text-[#38A169] mb-2">Snap Finance</h3><ul className="text-sm text-gray-600 space-y-1">{(lang === "es" ? ["$0 de enganche","Hasta $5,000 de crédito","100 Días Igual que en Efectivo","Sin crédito necesario — alta aprobación"] : ["$0 Down Payment","Up to $5,000 in credit","100 Days Same as Cash","No credit needed — high approval rate"]).map((s,i)=> <li key={i}>{s}</li>)}</ul></div>
            <div className="bg-blue-50 rounded-xl p-5"><h3 className="font-bold text-blue-600 mb-2">Affirm</h3><ul className="text-sm text-gray-600 space-y-1">{(lang === "es" ? ["Pagos mensuales transparentes","Opciones de 0% APR","Sin cargos ocultos","Elige el plan que más te convenga"] : ["Transparent monthly payments","0% APR options available","No hidden fees","Pick the plan that fits your budget"]).map((s,i)=> <li key={i}>{s}</li>)}</ul></div>
          </div></div>

        <div><h2 className="font-heading text-2xl font-bold mb-4">{lang === "es" ? "Entrega Rápida de Colchones en Los Banos y Áreas Cercanas" : "Fast Mattress Delivery to Los Banos & Surrounding Areas"}</h2>
          <p className="text-gray-600 leading-relaxed">{lang === "es" ? <>No te preocupes por cargar una caja pesada. Ofrecemos <strong>entrega rápida de colchones</strong> a Los Banos, Newman, Dos Palos, Firebaugh, Santa Nella, Gustine y todo el Valle Central. La mayoría de colchones en stock pueden estar en tu recámara en pocos días. Instalación disponible por $99.</> : <>Don't worry about hauling a heavy box. We provide <strong>fast mattress delivery</strong> to Los Banos, Newman, Dos Palos, Firebaugh, Santa Nella, Gustine, Merced, Atwater, Turlock and the entire Central Valley. Most in-stock mattresses can be in your bedroom within just a few days. Optional setup and old mattress haul-away available for $99.</>}</p></div>

        {/* Local vs Chain comparison — directly targets queries comparing us to Mattress Firm */}
        <div>
          <h2 className="font-heading text-2xl font-bold mb-4">{lang === "es" ? "Tienda Local vs. Cadena Nacional — ¿Cuál Conviene?" : "Local Mattress Store vs. National Chain — Which Is Better?"}</h2>
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50"><th className="p-3 text-left">{lang === "es" ? "Característica" : "Feature"}</th><th className="p-3 text-center">{lang === "es" ? "Cadena Nacional" : "National Chain"}</th><th className="p-3 text-center text-[#C05621]">La Bodega Bay</th></tr></thead>
              <tbody className="divide-y">
                {(lang === "es" ? [
                  ["Prueba en Persona", "Sí (corto)", "Sí — sin presión"],
                  ["Recogida el Mismo Día", "Raramente", "Sí, en stock"],
                  ["Sin Crédito Aprobado", "Limitado", "Sí, Snap"],
                  ["Entrega Local Gratis", "$$$", "Gratis"],
                  ["Garantía del Mejor Precio", "Variable", "Sí + 5% extra"],
                  ["Hablas con un Local", "Centro de llamadas", "Familia local"],
                ] : [
                  ["Try Before You Buy", "Yes (rushed)", "Yes — no pressure"],
                  ["Same-Day Pickup", "Rarely", "Yes on in-stock"],
                  ["No-Credit Approval", "Limited", "Yes, Snap Finance"],
                  ["Free Local Delivery", "$$$", "Free"],
                  ["Price Match Guarantee", "Varies", "Yes + 5% off diff"],
                  ["Talk to a Local", "Call center", "Owner & family"],
                ]).map(([f, big, us], i) => (
                  <tr key={i}><td className="p-3">{f}</td><td className="p-3 text-center text-red-400">{big}</td><td className="p-3 text-center text-green-600 font-bold">{us}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ accordion — visible to users AND emits FAQPage schema */}
        <div>
          <h2 className="font-heading text-2xl font-bold mb-4" data-testid="mattress-faq-heading">{lang === "es" ? "Preguntas Frecuentes — Colchones en Los Banos" : "Frequently Asked Questions — Mattresses in Los Banos"}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                <button data-testid={`mattress-faq-${i}`} className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <h3 className="font-semibold text-sm text-[#1A202C] pr-4">{f.q}</h3>
                  <ChevronDown size={18} className={`text-gray-400 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-4 pb-4 text-sm text-gray-600 border-t border-gray-50 pt-3">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#FDFBF7] rounded-2xl p-8 text-center">
          <h3 className="font-heading text-xl font-bold mb-2">{lang === "es" ? "Visita nuestra tienda en Los Banos" : "Visit Our Los Banos Showroom"}</h3>
          <p className="text-gray-600">2225 East Pacheco Blvd, Los Banos, CA 93635</p>
          <p className="text-[#C05621] font-bold">(209) 827-4076</p>
          <p className="text-sm text-gray-500 mt-1">{lang === "es" ? "Lun-Sáb: 10am-7pm | Dom: 10am-5pm" : "Mon-Sat: 10am-7pm | Sun: 10am-5pm"}</p>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            <Link to={`${linkPrefix}/financing`} className="text-xs text-[#C05621] underline">{lang === "es" ? "Ver opciones de financiamiento" : "See financing options"}</Link>
            <span className="text-gray-300">·</span>
            <Link to={`${linkPrefix}/furniture/los-banos`} className="text-xs text-[#C05621] underline">{lang === "es" ? "Tienda de muebles en Los Banos" : "Furniture store in Los Banos"}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function PriceGuaranteePage() {
  const { lang } = useTranslation();
  useEffect(() => {
    const canonical = lang === "es"
      ? "https://labodegabayfurniture.com/es/price-guarantee"
      : "https://labodegabayfurniture.com/price-guarantee";
    updatePageSEO({
      title: lang === "es"
        ? "Garantía del Mejor Precio en Muebles | La Bodega Bay Furniture Los Banos"
        : "Furniture Price Match Guarantee | La Bodega Bay Furniture Los Banos, CA",
      description: lang === "es"
        ? "Igualamos cualquier precio anunciado más bajo de un vendedor autorizado y te damos 5% extra de descuento. Compra muebles en Los Banos y Newman con confianza."
        : "We match any competitor's lower advertised price on identical furniture and give you an extra 5% off the difference. Shop with confidence in Los Banos & Newman, CA.",
      canonical,
      ogType: "website",
    });
  }, [lang]);
  return (
    <div data-testid="price-guarantee-page">
      <section className="bg-[#2D3748] py-16"><div className="max-w-4xl mx-auto px-4 text-center text-white">
        <Shield size={48} className="mx-auto mb-4 text-[#DD6B20]" />
        <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">{lang === "es" ? "Nuestra Garantía del Mejor Precio" : "Our Best Price Guarantee"}</h1>
        <p className="text-gray-300 text-lg">{lang === "es" ? "Compra con confianza en Los Banos y Newman. No nos ganan en precio." : "Shop with confidence in Los Banos and Newman. We won't be undersold."}</p>
      </div></section>
      <section className="max-w-4xl mx-auto px-4 py-12 space-y-10">
        <div><h2 className="font-heading text-2xl font-bold mb-4">{lang === "es" ? "Compra Local, Ahorra Mucho" : "Shop Local, Save Big"}</h2>
          <p className="text-gray-600 leading-relaxed">{lang === "es"
            ? <>En <strong>La Bodega Bay Furniture</strong>, nos enorgullecemos de ofrecer los mejores precios en el Valle Central. Si encuentras un precio anunciado más bajo en un artículo idéntico en stock de un vendedor autorizado, <strong>lo igualamos y te damos 5% extra de descuento sobre la diferencia</strong>. Obtén los precios bajos de una tienda grande con el servicio personal, entrega rápida y cuidado de un negocio familiar local.</>
            : <>At <strong>La Bodega Bay Furniture</strong>, we pride ourselves on offering the best values in the Central Valley. If you find a lower advertised price on an identical in-stock item from an authorized retailer, <strong>we will match it plus give you an extra 5% off the difference</strong>. Get the low prices of a big-box retailer with the personal service, fast delivery, and care of a local family-owned business.</>}</p></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-gray-100 rounded-xl p-6 text-center shadow-sm"><div className="text-3xl mb-2">1</div><h3 className="font-bold mb-2">Find a Deal</h3><p className="text-sm text-gray-600">Find an identical product (same brand and model) advertised for less by an authorized retailer.</p></div>
          <div className="bg-white border border-gray-100 rounded-xl p-6 text-center shadow-sm"><div className="text-3xl mb-2">2</div><h3 className="font-bold mb-2">Show Us</h3><p className="text-sm text-gray-600">Bring in the ad, a photo, or a link to the product page. Contact us within 30 days of purchase.</p></div>
          <div className="bg-white border border-gray-100 rounded-xl p-6 text-center shadow-sm"><div className="text-3xl mb-2">3</div><h3 className="font-bold mb-2">We Match + 5%</h3><p className="text-sm text-gray-600">We'll match the price AND give you 5% off the difference. You still get $0 down financing!</p></div>
        </div>
        <div className="bg-[#FDFBF7] rounded-xl p-6"><h3 className="font-bold mb-2">Still Need to Pay Over Time?</h3><p className="text-gray-600 text-sm">Price matching doesn't mean you have to pay all at once. Use <strong>Snap Finance (No Credit Needed)</strong> or <strong>Affirm</strong> on your price-matched items!</p></div>
        <p className="text-xs text-gray-400">Terms: Item must be identical in brand, model, and condition. Must be from an authorized retailer. Excludes clearance, closeouts, damaged items, and limited-time doorbusters. Contact us within 30 days of purchase.</p>
      </section>
    </div>
  );
}

function WhyShopLocalPage() {
  const { lang } = useTranslation();
  const linkPrefix = lang === "es" ? "/es" : "";
  useEffect(() => {
    const canonical = lang === "es"
      ? "https://labodegabayfurniture.com/es/why-shop-local"
      : "https://labodegabayfurniture.com/why-shop-local";
    updatePageSEO({
      title: lang === "es"
        ? "Por Qué Comprar Muebles Local en Los Banos | La Bodega Bay Furniture"
        : "Why Shop Local for Furniture in Los Banos, CA | La Bodega Bay Furniture",
      description: lang === "es"
        ? "Compra muebles en una tienda familiar local en Los Banos en lugar de cadenas grandes. Entrega gratis, financiamiento sin crédito, igualación de precio y soporte personal."
        : "Shop furniture from a local family-owned store in Los Banos instead of big online retailers. Free delivery, no-credit financing, price match, and personal support that chains can't offer.",
      canonical,
      ogType: "website",
    });
  }, [lang]);
  return (
    <div data-testid="why-shop-local-page">
      <section className="bg-[#2D3748] py-16"><div className="max-w-4xl mx-auto px-4 text-center text-white">
        <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">{lang === "es" ? "Apoya lo Local. Vive Mejor." : "Support Local. Live Better."}</h1>
        <p className="text-gray-300 text-lg">{lang === "es" ? "Descubre la diferencia de comprar con un negocio familiar que se preocupa por el Valle Central tanto como tú." : "Discover the difference of shopping with a family-owned business that cares about the Central Valley as much as you do."}</p>
      </div></section>
      <section className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="text-center p-6"><Truck size={40} className="mx-auto text-[#C05621] mb-3" /><h3 className="font-bold text-lg mb-2">{lang === "es" ? "Entrega Local Rápida" : "Fast Local Delivery"}</h3><p className="text-sm text-gray-600">{lang === "es" ? "¡Vivimos aquí! Entrega rápida a Los Banos, Newman y pueblos cercanos — usualmente en días, no meses. Entrega gratis o instalación por $99." : "We live here! Rapid delivery to Los Banos, Newman, and surrounding towns — often within days, not months. Free drop-off or $99 assembly."}</p></div>
          <div className="text-center p-6"><Users size={40} className="mx-auto text-[#C05621] mb-3" /><h3 className="font-bold text-lg mb-2">{lang === "es" ? "Personas Reales, Ayuda Real" : "Real People, Real Help"}</h3><p className="text-sm text-gray-600">{lang === "es" ? "¿Cansado de menús automatizados? Llámanos y un miembro local del equipo contesta. Te ayudamos a medir tu espacio, escoger tu estilo y encontrar el financiamiento que te convenga." : "Tired of automated phone trees? Call us and a local team member picks up. We help you measure your space, pick your style, and find financing that fits."}</p></div>
          <div className="text-center p-6"><MapPin size={40} className="mx-auto text-[#C05621] mb-3" /><h3 className="font-bold text-lg mb-2">{lang === "es" ? "Invertimos en el Valle" : "Investing in the Valley"}</h3><p className="text-sm text-gray-600">{lang === "es" ? "Cada dólar gastado en La Bodega Bay se queda en nuestra comunidad. Damos empleo local y apoyamos familias locales en Los Banos y Newman." : "Every dollar spent at La Bodega Bay stays in our community. We provide local jobs and support local families in Los Banos and Newman."}</p></div>
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm mb-12">
          <h2 className="font-heading text-xl font-bold p-6 pb-0">{lang === "es" ? "Tienda Local vs. Gigantes Online" : "Local Showroom vs. Online Giants"}</h2>
          <table className="w-full text-sm mt-4">
            <thead><tr className="bg-gray-50"><th className="p-3 text-left">{lang === "es" ? "Característica" : "Feature"}</th><th className="p-3 text-center">{lang === "es" ? "Tiendas Online Grandes" : "Big Online Stores"}</th><th className="p-3 text-center text-[#C05621]">La Bodega Bay</th></tr></thead>
            <tbody className="divide-y">
              {(lang === "es" ? [
                ["Pruébalo Antes de Comprar", "No", "Sí — 2 Tiendas"],
                ["Instalación Profesional", "Costo Extra", "$99 Plano"],
                ["Financiamiento $0 Enganche", "Limitado", "Snap y Affirm"],
                ["Devoluciones y Soporte", "Dolor de Cabeza", "Local y Personal"],
                ["Garantía de Precio", "No", "Sí + 5% de descuento"],
                ["Recoge y Ahorra", "No", "Ahorra 10%"],
              ] : [
                ["Test Before You Buy", "No", "Yes — 2 Showrooms"],
                ["Expert Assembly", "Extra $$$", "$99 Flat Fee"],
                ["$0 Down Financing", "Limited", "Snap & Affirm"],
                ["Returns & Support", "Headache", "Local & Personal"],
                ["Price Match", "No", "Yes + 5% Off"],
                ["Pickup & Save", "No", "Save 10%"],
              ]).map(([f, big, us], i) => (
                <tr key={i}><td className="p-3">{f}</td><td className="p-3 text-center text-red-400">{big}</td><td className="p-3 text-center text-green-600 font-bold">{us}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-center"><h2 className="font-heading text-2xl font-bold mb-4">{lang === "es" ? "¿Listo para Transformar tu Hogar?" : "Ready to Transform Your Home?"}</h2><p className="text-gray-600 mb-6">{lang === "es" ? "Visítanos hoy y experimenta la diferencia local. Empresa familiar desde el 2000." : "Visit us today and experience the local difference. Family-owned since 2000."}</p>
          <div className="flex justify-center gap-4"><Link to={`${linkPrefix}/contact`} className="px-6 py-3 bg-[#C05621] text-white rounded-lg font-semibold">{lang === "es" ? "Cómo Llegar" : "Get Directions"}</Link><Link to={`${linkPrefix}/categories`} className="px-6 py-3 border border-[#C05621] text-[#C05621] rounded-lg font-semibold">{lang === "es" ? "Comprar Online" : "Shop Online"}</Link></div>
        </div>
      </section>
    </div>
  );
}

function FinancingPage() {
  const { lang } = useTranslation();
  const linkPrefix = lang === "es" ? "/es" : "";
  useEffect(() => {
    const canonical = lang === "es"
      ? "https://labodegabayfurniture.com/es/financing"
      : "https://labodegabayfurniture.com/financing";
    updatePageSEO({
      title: lang === "es"
        ? "Financiamiento de Muebles | $0 Enganche, Sin Crédito | La Bodega Bay Furniture"
        : "Furniture Financing in Los Banos | $0 Down, No Credit Needed | Snap & Affirm",
      description: lang === "es"
        ? "Financia muebles y colchones en Los Banos sin crédito. Snap Finance: $0 enganche, hasta $5,000, 100 días igual que en efectivo. Affirm: 0% APR disponible para compradores calificados."
        : "Finance furniture and mattresses in Los Banos with no credit needed. Snap Finance: $0 down, up to $5,000, 100 days same as cash. Affirm: 0% APR available for qualified buyers, soft credit check only.",
      canonical,
      ogType: "website",
    });
  }, [lang]);
  return (
    <div data-testid="financing-page">
      <section className="bg-[#2D3748] py-16"><div className="max-w-4xl mx-auto px-4 text-center text-white">
        <CreditCard size={48} className="mx-auto mb-4 text-[#DD6B20]" />
        <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">{lang === "es" ? "Amuebla tu Hogar Hoy, Paga Después" : "Furnish Your Home Today, Pay Later"}</h1>
        <p className="text-gray-300 text-lg">{lang === "es" ? "$0 Enganche, Sin Crédito Necesario — Múltiples Opciones de Pago" : "$0 Down, No Credit Needed — Multiple Ways to Pay"}</p>
      </div></section>
      <section className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-[#38A169]/5 border-2 border-[#38A169]/20 rounded-2xl p-8">
            <h2 className="font-heading text-2xl font-bold text-[#38A169] mb-4">Snap Finance</h2>
            <p className="text-gray-600 mb-4 font-medium">{lang === "es" ? "Sin Crédito Necesario — ¡Todos Aprobados!" : "No Credit Needed — Everyone Approved!"}</p>
            <ul className="space-y-3 text-gray-600">
              {(lang === "es" ? [
                <><strong>$0 de Enganche</strong> — Llévatelo a casa hoy</>,
                <>Hasta <strong>$5,000</strong> de crédito</>,
                <><strong>100 Días</strong> Igual que en Efectivo</>,
                <>Alta tasa de aprobación — <strong>sin crédito</strong></>,
                <>No afecta tu puntaje FICO</>,
              ] : [
                <><strong>$0 Down</strong> — Take it home today</>,
                <>Up to <strong>$5,000</strong> in credit</>,
                <><strong>100 Days</strong> Same as Cash payoff option</>,
                <>High approval rates — <strong>no credit needed</strong></>,
                <>Won't affect your FICO score</>,
              ]).map((item, i) => (
                <li key={i} className="flex items-start gap-2"><Check size={18} className="text-[#38A169] mt-0.5 flex-shrink-0" /> <span>{item}</span></li>
              ))}
            </ul>
            <a href="https://snapfinance.com/apply" target="_blank" rel="noreferrer" onClick={async (e) => {
              e.preventDefault();
              try {
                const r = await axios.get(`${API}/payments/snap/apply-url`);
                window.open(r.data?.url || "https://snapfinance.com/apply", "_blank", "noopener,noreferrer");
              } catch {
                window.open("https://snapfinance.com/apply", "_blank", "noopener,noreferrer");
              }
            }} className="mt-6 block text-center px-6 py-3 bg-[#38A169] text-white rounded-lg font-bold hover:bg-[#2F855A]">{lang === "es" ? "Solicitar con Snap Finance" : "Apply with Snap Finance"}</a>
          </div>
          <div className="bg-blue-50/50 border-2 border-blue-100 rounded-2xl p-8">
            <h2 className="font-heading text-2xl font-bold text-blue-600 mb-4">Affirm</h2>
            <p className="text-gray-600 mb-4 font-medium">{lang === "es" ? "Pagos Mensuales — 0% de Interés Disponible" : "Monthly Payments — 0% Interest Available"}</p>
            <ul className="space-y-3 text-gray-600">
              {(lang === "es" ? [
                <><strong>Pagos mensuales</strong> transparentes</>,
                <>Opciones de <strong>0% APR</strong></>,
                <>Sin cargos ocultos ni sorpresas</>,
                <>Elige el plan que más te convenga</>,
                <>Verificación suave que no afecta tu crédito</>,
              ] : [
                <>Transparent <strong>monthly payments</strong></>,
                <><strong>0% APR</strong> options available</>,
                <>No hidden fees or surprises</>,
                <>Pick the plan that fits your budget</>,
                <>Soft credit check won't affect score</>,
              ]).map((item, i) => (
                <li key={i} className="flex items-start gap-2"><Check size={18} className="text-blue-600 mt-0.5 flex-shrink-0" /> <span>{item}</span></li>
              ))}
            </ul>
            <a href={affirmPrequalUrl()} target="_blank" rel="noreferrer" className="mt-6 block text-center px-6 py-3 bg-[#0FA0EA] text-white rounded-lg font-bold hover:bg-[#0C8AC9]" data-testid="financing-affirm-prequal-btn">{lang === "es" ? "Ve si calificas con Affirm" : "See if you qualify with Affirm"} →</a>
            <p className="text-[11px] text-gray-500 text-center mt-2">{lang === "es" ? "Verificar tu elegibilidad no afecta tu puntaje de crédito." : "Checking your eligibility won't affect your credit score."}</p>
            <Link to={`${linkPrefix}/categories`} className="mt-3 block text-center px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700">{lang === "es" ? "Compra y Usa Affirm en el Pago" : "Shop & Use Affirm at Checkout"}</Link>
          </div>
        </div>
        <div className="space-y-6 mb-12">
          <h2 className="font-heading text-2xl font-bold">{lang === "es" ? "Preguntas Frecuentes" : "Common Questions"}</h2>
          {(lang === "es" ? [
            {q: "¿Cómo uso el financiamiento?", a: "Agrega tus artículos al carrito. En el pago, selecciona Snap Finance o Affirm como método de pago. Te guiaremos por una solicitud rápida — la mayoría de aprobaciones son instantáneas."},
            {q: "¿Esto afectará mi puntaje de crédito?", a: "Snap Finance usa una evaluación propia que no afecta tu puntaje FICO. Affirm hace una verificación suave de crédito que no afecta tu puntaje."},
            {q: "¿Puedo usar el financiamiento para envío?", a: "¡Sí! Financia tu pedido completo incluyendo envío y la cuota de instalación de $99. Disponible para Los Banos, Newman y todo el Valle Central."},
            {q: "¿Qué pasa si quiero pagarlo antes?", a: "Snap Finance ofrece una opción de 100 días igual que en efectivo. Affirm no tiene penalizaciones por pago anticipado — paga cuando quieras."},
          ] : [
            {q: "How do I use financing?", a: "Add your items to cart. At checkout, select Snap Finance or Affirm as your payment method. You'll be guided through a quick application — most approvals are instant."},
            {q: "Will this affect my credit score?", a: "Snap Finance uses a proprietary evaluation that doesn't impact your FICO score. Affirm performs a soft credit check that won't affect your score."},
            {q: "Can I use financing for delivery?", a: "Yes! Finance your entire order including delivery and the $99 assembly fee. Available for Los Banos, Newman, and the entire Central Valley."},
            {q: "What if I want to pay it off early?", a: "Snap Finance offers a 100-day same-as-cash option. Affirm has no prepayment penalties — pay it off anytime."},
          ]).map((faq, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-xl p-5"><h3 className="font-bold text-sm mb-2">{faq.q}</h3><p className="text-sm text-gray-600">{faq.a}</p></div>
          ))}
        </div>
        <div className="bg-[#FDFBF7] rounded-2xl p-8 text-center">
          <h3 className="font-heading text-xl font-bold mb-2">{lang === "es" ? "¿Necesitas Ayuda?" : "Need Help Applying?"}</h3>
          <p className="text-gray-600 mb-4">{lang === "es" ? <>Visítanos en nuestras tiendas de <strong>Los Banos</strong> o <strong>Newman</strong> y nuestro equipo te guiará en el proceso.</> : <>Visit us at our <strong>Los Banos</strong> or <strong>Newman</strong> locations and our team will walk you through the process!</>}</p>
          <p className="text-[#C05621] font-bold text-lg">(209) 827-4076</p>
        </div>
      </section>
    </div>
  );
}


// ─── CITY LANDING PAGE (Local SEO) ───
function CityLandingPage() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/city/${citySlug}`);
        setData(r.data);
        const canonical = `https://labodegabayfurniture.com/furniture/${citySlug}`;
        // Build FAQ JSON-LD from backend-provided FAQ data + LocalBusiness with city-specific areaServed.
        const jsonLd = [];
        if (Array.isArray(r.data.faq) && r.data.faq.length > 0) {
          jsonLd.push({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": r.data.faq.map(f => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": { "@type": "Answer", "text": f.a }
            }))
          });
        }
        jsonLd.push({
          "@context": "https://schema.org",
          "@type": "FurnitureStore",
          "@id": `${canonical}#furniturestore`,
          "name": `La Bodega Bay Furniture - Serving ${r.data.city}`,
          "url": canonical,
          "telephone": "+12098274076",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "2225 East Pacheco Blvd",
            "addressLocality": "Los Banos",
            "addressRegion": "CA",
            "postalCode": "93635",
            "addressCountry": "US"
          },
          "geo": { "@type": "GeoCoordinates", "latitude": 37.0583, "longitude": -120.8497 },
          "areaServed": [
            { "@type": "City", "name": r.data.city, "containedInPlace": { "@type": "AdministrativeArea", "name": r.data.county || "California" } }
          ],
          "openingHoursSpecification": [
            { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"], "opens": "10:00", "closes": "19:00" },
            { "@type": "OpeningHoursSpecification", "dayOfWeek": "Sunday", "opens": "10:00", "closes": "17:00" }
          ],
          "priceRange": "$$",
          "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "150", "bestRating": "5" },
          "description": r.data.description || `Free furniture delivery to ${r.data.city}, CA from La Bodega Bay Furniture. Family-owned since 2000.`
        });

        updatePageSEO({
          title: r.data.seo_title,
          description: r.data.seo_description,
          canonical,
          ogType: "website",
          jsonLd,
        });
      } catch { navigate("/", { replace: true }); }
      setLoading(false);
    })();
  }, [citySlug]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#C05621] border-t-transparent rounded-full" /></div>;
  if (!data) return null;

  return (
    <div data-testid={`city-page-${citySlug}`}>
      {/* Hero */}
      <section className="bg-[#2D3748] py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 text-center text-white">
          <p className="text-[#DD6B20] font-semibold text-sm uppercase tracking-wider mb-3">Free Delivery to {data.city}</p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Furniture Store Near {data.city}, CA
          </h1>
          <p className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto mb-6">{data.description}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/shop" className="px-6 py-3 bg-[#C05621] text-white font-bold rounded-xl hover:bg-[#DD6B20] transition-colors">Shop All Furniture</Link>
            <Link to="/financing" className="px-6 py-3 border border-white/30 text-white font-bold rounded-xl hover:bg-white/10 transition-colors">$0 Down Financing</Link>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="border-b border-gray-100 bg-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex flex-wrap justify-center gap-6 text-sm text-gray-600">
          <span className="flex items-center gap-1.5"><Truck size={16} className="text-[#C05621]" /> Free Delivery to {data.city}</span>
          <span className="flex items-center gap-1.5"><CreditCard size={16} className="text-[#C05621]" /> $0 Down Financing</span>
          <span className="flex items-center gap-1.5"><Shield size={16} className="text-[#C05621]" /> Price Match + 5% Off</span>
          <span className="flex items-center gap-1.5"><Store size={16} className="text-[#C05621]" /> Family-Owned 25+ Years</span>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Why Shop With Us for [City] */}
        <section className="mb-14">
          <h2 className="font-heading text-2xl font-bold text-[#1A202C] mb-6">Why {data.city} Families Choose La Bodega Bay</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-[#FDFBF7] rounded-xl p-5 border border-gray-100">
              <Truck size={28} className="text-[#C05621] mb-3" />
              <h3 className="font-bold text-sm mb-1">Free Delivery to {data.city}</h3>
              <p className="text-xs text-gray-600">We deliver to all {data.county} zip codes including {data.zip_codes.join(", ")}. Most orders arrive in 1-7 business days.</p>
            </div>
            <div className="bg-[#FDFBF7] rounded-xl p-5 border border-gray-100">
              <CreditCard size={28} className="text-[#C05621] mb-3" />
              <h3 className="font-bold text-sm mb-1">Easy Financing for {data.city} Residents</h3>
              <p className="text-xs text-gray-600">Snap Finance: $0 down, no credit needed. Affirm: Pay over time. Most {data.city} customers are approved instantly.</p>
            </div>
            <div className="bg-[#FDFBF7] rounded-xl p-5 border border-gray-100">
              <Award size={28} className="text-[#C05621] mb-3" />
              <h3 className="font-bold text-sm mb-1">Price Match Guarantee</h3>
              <p className="text-xs text-gray-600">Found it cheaper? We'll match any competitor's price and beat it by 5%. Or pick up in-store and save 10%.</p>
            </div>
          </div>
        </section>

        {/* Featured Products */}
        {data.featured_products?.length > 0 && (
          <section className="mb-14">
            <h2 className="font-heading text-2xl font-bold text-[#1A202C] mb-2">Popular Furniture for {data.city} Homes</h2>
            <p className="text-sm text-gray-500 mb-6">Free delivery on every order to {data.city}, CA</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {data.featured_products.map(p => (
                <div key={p.id} className="group cursor-pointer" onClick={() => navigate(`/product/${p.slug}`)}>
                  <div className="rounded-lg overflow-hidden bg-[#F7FAFC] aspect-square mb-2">
                    {p.images?.[0]?.url && <img src={p.images[0].url} alt={`${p.name} - delivery to ${data.city}`} className="w-full h-full object-contain p-1 group-hover:scale-105 transition-transform" loading="lazy" />}
                  </div>
                  <p className="text-[10px] font-medium text-[#C05621] uppercase">{p.brand}</p>
                  <p className="text-xs font-semibold text-[#1A202C] line-clamp-2 group-hover:text-[#C05621] transition-colors">{p.name}</p>
                  <p className="text-sm font-bold mt-0.5">${p.price?.toFixed(2)}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-6">
              <Link to="/shop" className="text-[#C05621] font-semibold text-sm hover:underline">Browse all 15,000+ products &rarr;</Link>
            </div>
          </section>
        )}

        {/* Shop by Category */}
        {data.categories?.length > 0 && (
          <section className="mb-14">
            <h2 className="font-heading text-2xl font-bold text-[#1A202C] mb-6">Shop by Category</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {data.categories.map(cat => (
                <Link key={cat.name} to={`/shop?category=${encodeURIComponent(cat.name)}`} className="bg-white border border-gray-100 rounded-xl p-4 hover:border-[#C05621] hover:shadow-sm transition-all group">
                  <h3 className="font-bold text-sm text-[#1A202C] group-hover:text-[#C05621]">{cat.name}</h3>
                  <p className="text-xs text-gray-500">{cat.count.toLocaleString()} products</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* FAQ Section */}
        <section className="mb-14">
          <h2 className="font-heading text-2xl font-bold text-[#1A202C] mb-6">Frequently Asked Questions — {data.city}</h2>
          <div className="space-y-3">
            {data.faq?.map((f, i) => (
              <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                <button className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <h3 className="font-semibold text-sm text-[#1A202C] pr-4">{f.q}</h3>
                  <ChevronDown size={18} className={`text-gray-400 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-4 pb-4 text-sm text-gray-600 border-t border-gray-50 pt-3">{f.a}</div>}
              </div>
            ))}
          </div>
        </section>

        {/* Nearby Cities */}
        <section className="mb-14">
          <h2 className="font-heading text-xl font-bold text-[#1A202C] mb-4">We Also Deliver To</h2>
          <div className="flex flex-wrap gap-2">
            {data.all_cities?.filter(c => c.slug !== citySlug).map(c => (
              <Link key={c.slug} to={`/furniture/${c.slug}`} className="text-xs px-3 py-1.5 bg-gray-50 text-gray-700 rounded-full border border-gray-100 hover:border-[#C05621] hover:text-[#C05621] transition-colors">
                Furniture {c.name}
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#2D3748] rounded-2xl p-8 md:p-12 text-center text-white">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-3">Ready to Furnish Your {data.city} Home?</h2>
          <p className="text-gray-300 mb-6 max-w-lg mx-auto">Free delivery. $0 down financing. Price match guarantee. Family-owned for 25+ years.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/shop" className="px-6 py-3 bg-[#C05621] text-white font-bold rounded-xl hover:bg-[#DD6B20] transition-colors">Shop Now</Link>
            <a href="tel:2098274076" className="px-6 py-3 border border-white/30 text-white font-bold rounded-xl hover:bg-white/10 transition-colors flex items-center gap-2"><Phone size={16} /> (209) 827-4076</a>
          </div>
        </section>
      </div>
    </div>
  );
}

// ─── POLICIES PAGE ───
function PoliciesPage() {
  const { lang } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "policies";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    const canonical = lang === "es"
      ? "https://labodegabayfurniture.com/es/policies"
      : "https://labodegabayfurniture.com/policies";
    updatePageSEO({
      title: lang === "es" ? "Políticas y Legal | La Bodega Bay Furniture" : "Privacy Policy, Terms & Store Policies | La Bodega Bay Furniture",
      description: lang === "es"
        ? "Políticas de la tienda, política de privacidad y términos de servicio de La Bodega Bay Furniture en Los Banos y Newman, CA."
        : "Store policies, privacy policy, and terms of service for La Bodega Bay Furniture in Los Banos and Newman, CA.",
      canonical,
      ogType: "website",
    });
  }, [lang]);
  return (
    <div className="max-w-4xl mx-auto px-4 py-16" data-testid="policies-page">
      <h1 className="font-heading text-4xl font-bold text-[#1A202C] mb-6 text-center">{lang === "es" ? "Políticas y Legal" : "Policies & Legal"}</h1>
      <div className="flex justify-center gap-2 mb-10">
        {(lang === "es"
          ? [{k:"policies",l:"Políticas de la Tienda"},{k:"privacy",l:"Política de Privacidad"},{k:"terms",l:"Términos de Servicio"}]
          : [{k:"policies",l:"Store Policies"},{k:"privacy",l:"Privacy Policy"},{k:"terms",l:"Terms of Service"}]
        ).map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`policy-tab-${t.k}`}
            className={`px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${tab === t.k ? "bg-[#1A202C] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{t.l}</button>
        ))}
      </div>

      {tab === "policies" && (
      <div className="space-y-8">
        {(lang === "es" ? [
          { title: "Entrega e Instalación", icon: <Truck size={24} />, content: "Entrega gratis dentro del Valle Central, Bay Area y región de Fresno. La entrega usualmente toma pocos días. Instalación disponible por una cuota plana de $99. Entregamos desde ciudades grandes como Merced, Turlock y Atwater hasta vecinos cercanos como Dos Palos, Gustine y Firebaugh." },
          { title: "Garantía de Igualar Precios", icon: <Shield size={24} />, content: "Igualamos cualquier precio más bajo de un vendedor autorizado en artículos idénticos, más 5% extra de descuento sobre la diferencia. Contáctanos dentro de los 30 días de tu compra para más detalles. Creemos en ofrecer el mejor valor a nuestros clientes." },
          { title: "Opciones de Financiamiento", icon: <CreditCard size={24} />, content: "Hacemos los muebles accesibles para todos. Snap Finance: $0 enganche, sin crédito necesario, 100 días igual que en efectivo — verifica tu límite en segundos sin afectar tu puntaje FICO. Affirm: compra ahora, paga después con opciones de 0% de interés en crédito aprobado. Ambas opciones disponibles al pagar." },
          { title: "Descuento por Recoger", icon: <Store size={24} />, content: "Recoge tus muebles en nuestra ubicación de Los Banos (2225 East Pacheco Blvd) y ¡ahorra 10% automáticamente en tu pedido completo al pagar!" },
          { title: "Devoluciones y Cancelaciones", icon: <Package size={24} />, content: "No se aceptan devoluciones ni cancelaciones después de 24 horas desde el momento del pedido. Nuestro compromiso de ofrecer los precios más competitivos del mercado requiere que operemos con máxima eficiencia en todos los niveles — desde almacenamiento hasta logística. Este enfoque optimizado es lo que nos permite trasladar ahorros significativos directamente a ti. Por favor revisa tu pedido cuidadosamente antes de confirmar. Si necesitas hacer cambios, contáctanos dentro de las 24 horas de hacer tu pedido y nuestro equipo te asistirá rápidamente." },
        ] : [
          { title: "Delivery & Assembly", icon: <Truck size={24} />, content: "Free drop-off delivery within the Central Valley, Bay Area, and Fresno region. Delivery is usually within a few days. Assembly is available for a flat fee of $99. We deliver from major cities like Merced, Turlock, and Atwater to close neighbors like Dos Palos, Gustine, and Firebaugh." },
          { title: "Price Match Guarantee", icon: <Shield size={24} />, content: "We'll match any lower price from authorized retailers on identical items, plus 5% off the difference. Contact us within 30 days of purchase for details. We believe in offering the best value to our customers." },
          { title: "Financing Options", icon: <CreditCard size={24} />, content: "We make furniture affordable for everyone. Snap Finance: $0 down, no credit needed, 100 days same as cash — check your shopping limit in seconds without affecting your FICO score. Affirm: Buy now, pay later with 0% interest options available on approved credit. Both options are available at checkout so you can choose what works best for you." },
          { title: "Pickup Discount", icon: <Store size={24} />, content: "Pick up your furniture at our Los Banos location (2225 East Pacheco Blvd) and automatically save 10% on your entire order at checkout!" },
          { title: "Returns & Cancellations", icon: <Package size={24} />, content: "No returns or cancellations are accepted beyond 24 hours from the time of order. Our commitment to offering the most competitive prices in the market requires us to operate with maximum efficiency at every level — from warehousing to logistics. This streamlined approach is what allows us to pass significant savings directly to you. We kindly ask that you review your order carefully before confirming. If you need to make any changes, please contact us within 24 hours of placing your order and our team will assist you promptly." },
        ]).map((policy, i) => (
          <div key={i} className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="text-[#C05621]">{policy.icon}</div>
              <h2 className="font-heading text-xl font-bold">{policy.title}</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">{policy.content}</p>
          </div>
        ))}
      </div>
      )}

      {(tab === "privacy" || tab === "terms") && lang === "es" && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6 text-sm text-orange-800" data-testid="legal-english-notice">
          <strong>Nota:</strong> Nuestros documentos legales (Política de Privacidad y Términos de Servicio) están disponibles en inglés conforme a los requerimientos legales de California. Si tienes preguntas sobre estos documentos, llámanos al (209) 827-4076 y te ayudamos a entenderlos en español.
        </div>
      )}

      {tab === "privacy" && (
      <div className="bg-white border border-gray-100 rounded-2xl p-8 md:p-10 shadow-sm prose prose-sm max-w-none" data-testid="privacy-policy">
        <p className="text-xs text-gray-400 mb-6">Effective Date: March 21, 2026 &middot; Last Updated: August 15, 2026</p>

        <h2 className="font-heading text-xl font-bold text-[#1A202C] mt-0">Privacy Policy</h2>
        <p className="text-gray-600 leading-relaxed">La Bodega Bay Furniture ("we," "us," or "our"), located at 2225 East Pacheco Blvd, Los Banos, CA 93635, respects your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you visit our website at labodegabayfurniture.com, shop in our store, or interact with us in any way. This policy complies with the California Consumer Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA), effective January 1, 2023, and subsequent regulations effective January 1, 2026.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">1. Information We Collect</h3>
        <p className="text-gray-600 leading-relaxed">We collect the following categories of personal information:</p>
        <div className="text-gray-600 text-sm space-y-3 ml-4">
          <p><strong>Identifiers:</strong> Name, email address, phone number, mailing/delivery address when you place an order, create an account, subscribe to emails, or contact us.</p>
          <p><strong>Commercial Information:</strong> Products purchased or considered, order history, shopping cart contents, delivery preferences, and payment method used.</p>
          <p><strong>Financial Information:</strong> Payment card details are processed securely by our third-party payment processors (Stripe, Affirm, Snap Finance). We do not store your full credit card number, CVV, or bank account information on our servers.</p>
          <p><strong>Internet/Electronic Activity:</strong> Browsing history on our website, pages visited, products viewed, search queries, device type, browser type, IP address, and referring URLs. This data is collected through cookies and similar technologies.</p>
          <p><strong>Geolocation Data:</strong> Approximate location derived from your IP address to provide delivery estimates and regional pricing.</p>
          <p><strong>Communications:</strong> Chat transcripts from our AI-powered assistant (including style preferences, budget range, and room preferences shared during conversations), email correspondence, and phone call records.</p>
        </div>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">2. How We Use Your Information</h3>
        <div className="text-gray-600 text-sm space-y-2 ml-4">
          <p>- To process and fulfill your orders, including delivery and assembly coordination</p>
          <p>- To communicate order confirmations, delivery updates, and customer service responses</p>
          <p>- To provide personalized product recommendations through our AI chat assistant</p>
          <p>- To process payments through our secure payment partners</p>
          <p>- To send marketing communications (with your consent), including promotional offers and abandoned cart reminders</p>
          <p>- To improve our website, products, and customer experience</p>
          <p>- To detect and prevent fraud or unauthorized transactions</p>
          <p>- To comply with legal obligations</p>
        </div>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">3. How We Share Your Information</h3>
        <p className="text-gray-600 leading-relaxed">We do not sell your personal information for money. However, like most online retailers, we use advertising and analytics technologies that may transmit certain identifiers (cookies, device information, browsing activity) to third parties for cross-context behavioral advertising. Under the CPRA, this may be considered "sharing" of personal information. You have the right to opt out of this sharing at any time — use the "Do Not Sell or Share My Personal Information" link in the footer of every page or the choices in our cookie banner. We share information with the following categories of recipients:</p>
        <div className="text-gray-600 text-sm space-y-2 ml-4">
          <p><strong>Payment Processors:</strong> Stripe (credit/debit card processing), Affirm (buy now, pay later), and Snap Finance (financing). These providers have their own privacy policies governing how they handle your data.</p>
          <p><strong>Advertising &amp; Analytics Partners:</strong> Meta Platforms (Meta Pixel for Facebook/Instagram advertising), Google (Google Analytics 4, Google Ads conversion tracking and remarketing), and PostHog (site analytics). These partners may set cookies or receive identifiers such as your IP address, device information, and pages viewed in order to measure advertising performance and show you relevant ads. You may opt out of this sharing at any time (see Sections 4 and 7 below).</p>
          <p><strong>AI Services:</strong> Our chat assistant is powered by xAI (Grok). Chat conversations are sent to this provider to generate helpful responses. No personally identifiable information is shared beyond what you type in the chat.</p>
          <p><strong>Email Service:</strong> We use Gmail SMTP to send transactional and marketing emails.</p>
          <p><strong>Delivery Partners:</strong> We share your name, address, and phone number with our delivery team to fulfill your order.</p>
        </div>
        <p className="text-gray-600 leading-relaxed mt-3">We may also disclose your information if required by law, regulation, or legal process, or to protect our rights, safety, or property.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">4. Your California Privacy Rights (CCPA/CPRA)</h3>
        <p className="text-gray-600 leading-relaxed">As a California resident, you have the following rights under the CCPA/CPRA:</p>
        <div className="text-gray-600 text-sm space-y-3 ml-4">
          <p><strong>Right to Know:</strong> You can request that we disclose what personal information we have collected about you, the categories of sources, the business purpose for collecting it, and the categories of third parties with whom we share it.</p>
          <p><strong>Right to Delete:</strong> You can request that we delete the personal information we have collected from you, subject to certain exceptions (e.g., completing a transaction, detecting fraud, complying with legal obligations).</p>
          <p><strong>Right to Correct:</strong> You can request that we correct inaccurate personal information we maintain about you.</p>
          <p><strong>Right to Opt-Out of Sale/Sharing:</strong> You can opt out of the "sharing" of your personal information for cross-context behavioral advertising at any time by clicking the <button onClick={() => window.dispatchEvent(new Event("lbb-open-privacy"))} className="underline text-[#C05621] font-semibold" data-testid="policy-open-privacy-choices">"Do Not Sell or Share My Personal Information"</button> link in the footer of every page, or by selecting "Opt Out of Sale/Sharing" in our cookie banner. When you opt out, we disable advertising and analytics cookies (Meta Pixel, Google Ads, Google Analytics, PostHog) in your browser, delete their existing cookies, and stop transmitting your identifiers to those partners — including server-side events. We also automatically honor Global Privacy Control (GPC) browser signals as valid opt-out requests.</p>
          <p><strong>Right to Limit Use of Sensitive Information:</strong> You can limit how we use sensitive personal information (such as your precise geolocation) to only what is necessary to provide services you request.</p>
          <p><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising any of your privacy rights. You will not receive different pricing or quality of service.</p>
          <p><strong>Right Regarding Automated Decision-Making:</strong> Our AI chat assistant provides product recommendations. You can opt out of AI-personalized recommendations at any time through the chat privacy panel. These recommendations do not affect pricing or credit decisions.</p>
        </div>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">5. How to Exercise Your Rights</h3>
        <p className="text-gray-600 leading-relaxed">You can submit a privacy rights request through any of the following methods:</p>
        <div className="text-gray-600 text-sm space-y-2 ml-4">
          <p><strong>Email:</strong> sales@labodegabayfurniture.com (subject line: "Privacy Request")</p>
          <p><strong>Phone:</strong> (209) 827-4076</p>
          <p><strong>Chat:</strong> Use the "Your Privacy" panel in our chat widget to view, export, or delete your conversation data</p>
        </div>
        <p className="text-gray-600 leading-relaxed mt-3">We will verify your identity before processing any request. We aim to respond within 45 days. You may authorize an agent to submit a request on your behalf with proper written authorization.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">6. Opt-Out Preference Signals</h3>
        <p className="text-gray-600 leading-relaxed">We recognize and honor Global Privacy Control (GPC) signals and other opt-out preference signals as valid requests to opt out of the sale or sharing of your personal information, as required by California law effective January 1, 2026.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">7. Cookies & Tracking Technologies</h3>
        <p className="text-gray-600 leading-relaxed">Our website uses the following categories of cookies and similar technologies:</p>
        <div className="text-gray-600 text-sm space-y-3 ml-4">
          <p><strong>Strictly Necessary Cookies:</strong> Required for core site functions — shopping cart, checkout, session security, and language preference. These cannot be disabled because the site cannot function without them.</p>
          <p><strong>Analytics Cookies:</strong> Google Analytics (cookies beginning with "_ga") and PostHog help us understand site traffic, page performance, and how visitors use our store.</p>
          <p><strong>Advertising Cookies:</strong> Meta Pixel (cookies "_fbp" and "_fbc") and Google Ads ("_gcl_au") measure advertising performance and enable remarketing. Use of these technologies may constitute "sharing" of personal information under the CPRA.</p>
        </div>
        <p className="text-gray-600 leading-relaxed mt-3">When you first visit our site, a consent banner at the bottom of the screen lets you accept or opt out of analytics and advertising cookies. You can change your choice at any time using the "Do Not Sell or Share My Personal Information" link in the footer. Opting out disables the Meta Pixel, Google Ads, Google Analytics, and PostHog for your browser and deletes their existing cookies; strictly necessary cookies remain active. We also honor Global Privacy Control (GPC) signals as a valid opt-out. You can additionally control cookies through your browser settings.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">8. Data Retention</h3>
        <div className="text-gray-600 text-sm space-y-2 ml-4">
          <p><strong>Order data:</strong> Retained for 7 years for tax and legal compliance</p>
          <p><strong>Chat conversations:</strong> Retained for 90 days, then automatically deleted. You can delete your data at any time via the chat privacy panel.</p>
          <p><strong>Email subscriptions:</strong> Until you unsubscribe</p>
          <p><strong>Browsing data:</strong> Retained for 12 months for analytics, then aggregated and anonymized</p>
          <p><strong>Cart data:</strong> Active carts retained for 30 days, then deleted</p>
        </div>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">9. Data Security</h3>
        <p className="text-gray-600 leading-relaxed">We implement industry-standard security measures to protect your personal information, including: encrypted data transmission (HTTPS/TLS), secure password hashing (bcrypt), rate-limited authentication, HTTP security headers (HSTS, X-Frame-Options, CSP), role-based access controls for staff, and PCI-compliant payment processing through certified third-party processors. No method of transmission or storage is 100% secure, and we cannot guarantee absolute security.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">10. Children's Privacy</h3>
        <p className="text-gray-600 leading-relaxed">Our website is not directed to children under 16. We do not knowingly collect personal information from children under 16. If we learn that we have collected information from a child under 16, we will promptly delete it. If you believe a child has provided us with personal information, please contact us.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">11. Changes to This Policy</h3>
        <p className="text-gray-600 leading-relaxed">We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically. Continued use of our website after changes constitutes acceptance of the updated policy.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">12. Contact Us</h3>
        <div className="text-gray-600 text-sm space-y-1 ml-4">
          <p>La Bodega Bay Furniture</p>
          <p>2225 East Pacheco Blvd, Los Banos, CA 93635</p>
          <p>Phone: (209) 827-4076</p>
          <p>Email: sales@labodegabayfurniture.com</p>
        </div>
      </div>
      )}

      {tab === "terms" && (
      <div className="bg-white border border-gray-100 rounded-2xl p-8 md:p-10 shadow-sm prose prose-sm max-w-none" data-testid="terms-of-service">
        <p className="text-xs text-gray-400 mb-6">Effective Date: March 21, 2026 &middot; Last Updated: March 21, 2026</p>

        <h2 className="font-heading text-xl font-bold text-[#1A202C] mt-0">Terms of Service</h2>
        <p className="text-gray-600 leading-relaxed">These Terms of Service ("Terms") govern your access to and use of the La Bodega Bay Furniture website at labodegabayfurniture.com and all related services. By using our website or purchasing from us, you agree to these Terms.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">1. Orders & Pricing</h3>
        <div className="text-gray-600 text-sm space-y-2 ml-4">
          <p>- All prices are listed in U.S. Dollars and are subject to change without notice</p>
          <p>- We reserve the right to correct pricing errors. If a product's correct price is higher than listed, we will contact you before processing the order</p>
          <p>- An order is not confirmed until payment is successfully processed</p>
          <p>- Product availability is subject to change. If an item becomes unavailable after your order, we will notify you and offer alternatives or a full refund</p>
        </div>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">2. Payment</h3>
        <div className="text-gray-600 text-sm space-y-2 ml-4">
          <p>- We accept credit/debit cards (Visa, Mastercard, American Express, Discover) via Stripe</p>
          <p>- Financing is available through Affirm and Snap Finance, subject to their terms and approval</p>
          <p>- In-store payments including cash are accepted at our Los Banos location</p>
          <p>- All payment processing is handled by PCI-compliant third-party providers</p>
        </div>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">3. Delivery</h3>
        <div className="text-gray-600 text-sm space-y-2 ml-4">
          <p>- Free drop-off delivery is available within the Central Valley, Bay Area, and Fresno region</p>
          <p>- Delivery timeframes are estimates and not guaranteed</p>
          <p>- You are responsible for inspecting items upon delivery and reporting any damage within 24 hours</p>
          <p>- Assembly service is available for $99 per order</p>
        </div>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">4. Returns & Cancellations</h3>
        <p className="text-gray-600 leading-relaxed">No returns or cancellations are accepted beyond 24 hours from the time of order. Please review your order carefully before confirming. Contact us within 24 hours at (209) 827-4076 if changes are needed.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">5. Product Descriptions & Images</h3>
        <p className="text-gray-600 leading-relaxed">We make every effort to display product colors, dimensions, and descriptions accurately. However, actual colors may vary due to monitor settings, and minor variations in furniture dimensions are normal. Product images may show styling accessories that are not included with the item.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">6. Intellectual Property</h3>
        <p className="text-gray-600 leading-relaxed">All content on this website — including text, graphics, logos, images, and software — is the property of La Bodega Bay Furniture or its content suppliers and is protected by U.S. and international copyright laws. You may not reproduce, distribute, or create derivative works without our written consent.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">7. AI Chat Assistant</h3>
        <p className="text-gray-600 leading-relaxed">Our website features an AI-powered chat assistant to help you find products and answer questions. The assistant provides recommendations based on your preferences but does not make binding commitments on pricing, availability, or delivery. Final terms are as displayed at checkout. You can view, export, or delete your chat data at any time through the chat privacy panel.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">8. Limitation of Liability</h3>
        <p className="text-gray-600 leading-relaxed">To the maximum extent permitted by law, La Bodega Bay Furniture shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of our website or products. Our total liability shall not exceed the amount paid by you for the specific product or service giving rise to the claim.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">9. Governing Law</h3>
        <p className="text-gray-600 leading-relaxed">These Terms are governed by the laws of the State of California without regard to conflict of law provisions. Any disputes shall be resolved in the courts of Merced County, California.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">10. Changes to Terms</h3>
        <p className="text-gray-600 leading-relaxed">We reserve the right to update these Terms at any time. Changes take effect when posted on this page. Your continued use of the website after changes constitutes acceptance.</p>

        <h3 className="font-bold text-[#1A202C] text-base mt-8">11. Contact</h3>
        <div className="text-gray-600 text-sm space-y-1 ml-4">
          <p>La Bodega Bay Furniture</p>
          <p>2225 East Pacheco Blvd, Los Banos, CA 93635</p>
          <p>Phone: (209) 827-4076</p>
          <p>Email: sales@labodegabayfurniture.com</p>
        </div>
      </div>
      )}
    </div>
  );
}

// ─── BACK TO TOP ───
// ─── QUICK VIEW MODAL ───
function QuickViewModal({ product, onClose }) {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const pickupPrice = product.price * 0.9;
  const images = product.images?.slice(0, 5).map(i => i.url) || ["https://placehold.co/400x400/F7FAFC/718096?text=No+Image"];
  const [imgIdx, setImgIdx] = useState(0);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose} data-testid="quick-view-modal">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-start p-1">
          <button onClick={onClose} className="ml-auto p-2 hover:bg-gray-100 rounded-full" data-testid="quick-view-close"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-6 pb-6">
          <div>
            <div className="rounded-xl overflow-hidden bg-[#F7FAFC] aspect-square relative">
              <img loading="lazy" decoding="async" src={images[imgIdx]} alt={product.name} className="w-full h-full object-contain" />
              {images.length > 1 && (<>
                <button onClick={(e) => { e.stopPropagation(); setImgIdx(i => (i - 1 + images.length) % images.length); }} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center shadow hover:bg-white"><ChevronRight size={16} className="rotate-180" /></button>
                <button onClick={(e) => { e.stopPropagation(); setImgIdx(i => (i + 1) % images.length); }} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center shadow hover:bg-white"><ChevronRight size={16} /></button>
              </>)}
            </div>
            {images.length > 1 && <div className="flex gap-1.5 mt-2 justify-center">{images.map((img, i) => (
              <button key={i} onClick={(e) => { e.stopPropagation(); setImgIdx(i); }} className={`w-12 h-12 rounded-lg overflow-hidden border-2 ${i === imgIdx ? "border-[#C05621]" : "border-transparent"}`}>
                <img loading="lazy" decoding="async" src={img} alt="" className="w-full h-full object-contain" />
              </button>
            ))}</div>}
          </div>
          <div className="space-y-3">
            <p className="text-[10px] font-semibold text-[#C05621] uppercase tracking-wider">{product.brand}</p>
            <h2 className="font-heading text-xl font-bold text-[#1A202C] leading-tight">{product.name}</h2>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-[#1A202C]">${product.price.toFixed(2)}</span>
              {product.original_price > product.price && <span className="text-sm text-gray-400 line-through">${product.original_price.toFixed(2)}</span>}
            </div>
            <p className="text-xs text-[#38A169] font-medium flex items-center gap-1"><Store size={12} /> Pickup: ${pickupPrice.toFixed(2)}</p>
            <p className="text-xs text-gray-500 flex items-center gap-1"><Truck size={12} /> <span className="text-[#38A169] font-semibold">FREE</span> Ships in {product.shipping_days_min}-{product.shipping_days_max} days</p>
            {product.description && <p className="text-xs text-gray-600 line-clamp-3">{product.description}</p>}
            {product.dimensions && <p className="text-[10px] text-gray-400"><strong>Dimensions:</strong> {product.dimensions}</p>}
            <div className="flex gap-2 pt-2">
              <button onClick={() => { addToCart(product.id); onClose(); }}
                className="flex-1 py-3 bg-[#C05621] text-white rounded-xl font-bold text-sm hover:bg-[#DD6B20] transition-colors flex items-center justify-center gap-2"
                data-testid="quick-view-add-to-cart"><ShoppingCart size={16} /> Add to Cart</button>
              <button onClick={() => { onClose(); navigate(`/product/${product.slug}`); }}
                className="px-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                data-testid="quick-view-details">Details</button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── VISUAL SEARCH MODAL ───
function VisualSearchModal({ onClose }) {
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [textSearch, setTextSearch] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async (query) => {
    if (!query || query.length < 2) return;
    setUploading(true);
    try {
      const r = await axios.get(`${API}/products?search=${encodeURIComponent(query)}&limit=12`);
      setResults(r.data.products);
      setSearchQuery(query);
    } catch { toast.error("Search failed"); }
    setUploading(false);
  };

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10MB)");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const r = await axios.post(`${API}/visual-search`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setResults(r.data.results);
      setSearchQuery(r.data.search_query);
    } catch (e) {
      toast.error("Visual search failed. Try another image.");
    }
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose} data-testid="visual-search-modal">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="font-heading text-lg font-bold text-[#1A202C]">Visual Search</h2>
            <p className="text-xs text-gray-500 mt-0.5">Upload a photo to find similar furniture</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" data-testid="visual-search-close"><X size={20} /></button>
        </div>

        {!results ? (
          <div className="p-6 space-y-4">
            {/* Text Search */}
            <div>
              <p className="text-sm font-semibold text-[#1A202C] mb-2">Describe what you're looking for</p>
              <div className="flex gap-2">
                <input value={textSearch} onChange={e => setTextSearch(e.target.value)} placeholder="e.g., brown leather reclining sofa, round dining table..."
                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:border-[#C05621] focus:outline-none"
                  onKeyDown={e => { if (e.key === "Enter") handleSearch(textSearch); }} data-testid="visual-text-search" />
                <button onClick={() => handleSearch(textSearch)} disabled={uploading || textSearch.length < 2} className="px-5 py-3 bg-[#C05621] text-white font-bold text-sm rounded-xl hover:bg-[#DD6B20] disabled:opacity-50">Search</button>
              </div>
            </div>
            <div className="flex items-center gap-3"><div className="flex-1 h-px bg-gray-200" /><span className="text-xs text-gray-400">or upload a photo</span><div className="flex-1 h-px bg-gray-200" /></div>
            {/* Image Upload */}
            <div
              className={`border-2 border-dashed rounded-2xl p-10 text-center transition-colors ${dragOver ? "border-[#C05621] bg-[#FFF5F1]" : "border-gray-200 hover:border-gray-300"}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
              data-testid="visual-search-dropzone"
            >
              {uploading ? (
                <div className="space-y-3">
                  <div className="w-12 h-12 mx-auto border-3 border-[#C05621] border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-gray-600 font-medium">Analyzing your image with AI...</p>
                </div>
              ) : (
                <>
                  <Camera size={40} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-sm font-semibold text-[#1A202C] mb-1">Drag & drop a furniture photo</p>
                  <p className="text-xs text-gray-400 mb-4">or click to browse</p>
                  <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C05621] text-white text-sm font-bold rounded-xl cursor-pointer hover:bg-[#DD6B20] transition-colors">
                    <Upload size={16} /> Choose Image
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files[0])} data-testid="visual-search-input" />
                  </label>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="p-5">
            <div className="flex items-center gap-2 mb-4 bg-gray-50 px-4 py-2 rounded-lg">
              <Search size={14} className="text-gray-400" />
              <span className="text-sm text-gray-600">AI detected: <strong className="text-[#C05621]">{searchQuery}</strong></span>
              <button onClick={() => { setResults(null); setSearchQuery(""); }} className="ml-auto text-xs text-gray-400 hover:text-gray-600">Try another</button>
            </div>
            {results.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {results.map((p) => (
                  <div key={p.id} className="cursor-pointer group" onClick={() => { onClose(); navigate(`/product/${p.slug}`); }} data-testid={`visual-result-${p.slug}`}>
                    <div className="rounded-lg overflow-hidden bg-[#F7FAFC] aspect-square mb-1.5">
                      <img loading="lazy" decoding="async" src={p.images?.[0]?.url || "https://placehold.co/200x200"} alt={p.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
                    </div>
                    <p className="text-[10px] text-[#C05621] font-medium">{p.brand}</p>
                    <p className="text-xs font-semibold text-[#1A202C] line-clamp-2">{p.name}</p>
                    <p className="text-sm font-bold text-[#1A202C]">${p.price?.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No matching products found. Try a different photo.</p>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}

// ─── EMAIL CAPTURE POPUP ───
function CartAddedPopup() {
  const { cart, lastAdded, dismissLastAdded } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useTranslation();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      prevPath.current = location.pathname;
      dismissLastAdded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const total = cart?.total ?? lastAdded?.total ?? 0;
  const count = cart?.item_count ?? lastAdded?.itemCount ?? 0;
  const affirmPromo = useAffirmPromo(total);

  if (!lastAdded || /^\/(admin|checkout|cart)/.test(location.pathname)) return null;

  const p = lastAdded.item.product || {};

  const eta = (() => {
    const d = new Date();
    let days = Number(p.shipping_days_min ?? 3);
    while (days > 0) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) days--; }
    return d.toLocaleDateString(lang === "es" ? "es-US" : "en-US", { weekday: "short", month: "short", day: "numeric" });
  })();

  const goCheckout = () => { dismissLastAdded(); navigate("/checkout"); };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[80] md:hidden" onClick={dismissLastAdded} />
      <div className="fixed z-[85] inset-x-0 bottom-0 md:inset-x-auto md:bottom-auto md:right-4 md:top-24 md:w-[400px] bg-white md:rounded-2xl rounded-t-2xl shadow-2xl border border-gray-100 animate-in slide-in-from-bottom md:slide-in-from-right duration-300" data-testid="cart-added-popup" role="dialog" aria-label="Added to cart">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center"><Check size={14} className="text-green-600" /></span>
            <span className="font-heading font-bold text-[#1A202C]">{lang === "es" ? "¡Agregado al carrito!" : "Added to Cart!"}</span>
          </div>
          <button onClick={dismissLastAdded} aria-label="Close" data-testid="cart-popup-close"><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>
        <div className="px-5 py-4">
          <div className="flex gap-3">
            <img src={p.images?.[0]?.url || "https://placehold.co/80x80"} alt={p.name || ""} className="w-16 h-16 rounded-lg object-cover border border-gray-100 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#1A202C] line-clamp-2">{p.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">{lang === "es" ? "Cant" : "Qty"}: {lastAdded.quantity} · <span className="font-bold text-[#1A202C]">${Number(p.price || 0).toFixed(2)}</span></p>
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            <p className="flex items-center gap-2 text-xs text-green-700 font-semibold" data-testid="cart-popup-eta">
              <Truck size={14} /> {lang === "es" ? `Recíbelo tan pronto como el ${eta}` : `Get it as soon as ${eta}`}
            </p>
            <p className="flex items-center gap-2 text-xs text-gray-600" data-testid="cart-popup-financing">
              <CreditCard size={14} className="text-[#C05621]" />
              {affirmPromo?.monthly
                ? (lang === "es" ? <>{affirmPromo.zero_apr ? "0% APR — desde" : "Desde"} <strong>${affirmPromo.monthly}/mes</strong> con Affirm — o $0 de enganche con Snap</> : <>{affirmPromo.zero_apr ? "0% APR — as low as" : "As low as"} <strong>${affirmPromo.monthly}/mo</strong> with Affirm — or $0 down with Snap</>)
                : (lang === "es" ? <>Financiamiento flexible con Affirm — o $0 de enganche con Snap</> : <>Flexible financing with Affirm — or $0 down with Snap</>)}
            </p>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm border-t border-gray-100 pt-3">
            <span className="text-gray-500">{lang === "es" ? `Subtotal (${count} artículos)` : `Cart subtotal (${count} ${count === 1 ? "item" : "items"})`}</span>
            <span className="font-bold text-[#1A202C]" data-testid="cart-popup-subtotal">${Number(total || 0).toFixed(2)}</span>
          </div>
          <div className="mt-3 space-y-2">
            <button onClick={goCheckout} className="w-full py-3 bg-[#C05621] text-white rounded-xl font-bold text-sm hover:bg-[#DD6B20] transition-colors" data-testid="cart-popup-checkout">
              {lang === "es" ? "Pagar Ahora" : "Checkout Now"} →
            </button>
            <button onClick={dismissLastAdded} className="w-full py-2.5 border border-gray-300 text-gray-700 rounded-xl font-semibold text-sm hover:bg-gray-50 transition-colors" data-testid="cart-popup-continue">
              {lang === "es" ? "Seguir Comprando" : "Continue Shopping"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function CookieConsentBanner() {
  const [show, setShow] = useState(false);
  const [gpcApplied, setGpcApplied] = useState(false);
  const { lang } = useTranslation();
  const linkPrefix = lang === "es" ? "/es" : "";

  const applyConsent = (allowed, source = "banner") => {
    try {
      window.gtag && window.gtag("consent", "update", allowed
        ? { ad_storage: "granted", ad_user_data: "granted", ad_personalization: "granted", analytics_storage: "granted" }
        : { ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: "denied" });
    } catch { /* noop */ }
    try { window.fbq && window.fbq("consent", allowed ? "grant" : "revoke"); } catch { /* noop */ }
    try { window.posthog && (allowed ? window.posthog.opt_in_capturing() : window.posthog.opt_out_capturing()); } catch { /* noop */ }
    if (!allowed) {
      try {
        const host = window.location.hostname;
        const rootDomain = "." + host.split(".").slice(-2).join(".");
        document.cookie.split(";").forEach(c => {
          const name = c.split("=")[0].trim();
          if (/^(_fbp|_fbc|_ga|_gid|_gcl_)/.test(name)) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${host}`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${rootDomain}`;
          }
        });
      } catch { /* noop */ }
    }
    const gpc = typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;
    try { localStorage.setItem("lbb_privacy_consent", JSON.stringify({ adsAllowed: allowed, ts: new Date().toISOString(), gpc, source })); } catch { /* noop */ }
    axios.post(`${API}/privacy/consent`, { ads_allowed: allowed, gpc, source }).catch(() => {});
    setShow(false);
  };

  useEffect(() => {
    const open = () => { setGpcApplied(false); setShow(true); };
    window.addEventListener("lbb-open-privacy", open);
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem("lbb_privacy_consent") || "null"); } catch { /* noop */ }
    const gpc = typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;
    if (gpc && !stored) {
      applyConsent(false, "gpc");
      setGpcApplied(true);
      setShow(true);
    } else if (!stored) {
      setShow(true);
    }
    return () => window.removeEventListener("lbb-open-privacy", open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!show) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[70] bg-[#1A202C] text-white border-t border-white/15 shadow-[0_-6px_24px_rgba(0,0,0,0.35)]" data-testid="cookie-consent-banner" role="dialog" aria-label={lang === "es" ? "Preferencias de privacidad" : "Privacy preferences"}>
      <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col md:flex-row md:items-center gap-3">
        <p className="text-xs leading-relaxed text-gray-300 flex-1">
          {gpcApplied && (
            <span className="block font-semibold text-green-400 mb-1" data-testid="gpc-honored-notice">
              {lang === "es"
                ? "Detectamos la señal Global Privacy Control de tu navegador y ya te excluimos de la venta/compartición de datos."
                : "We detected your browser's Global Privacy Control signal and have already opted you out of data sale/sharing."}
            </span>
          )}
          {lang === "es"
            ? "Usamos cookies para operar el sitio, analizar el tráfico y para publicidad. Compartir datos con socios publicitarios (Meta, Google) puede considerarse \u201Cventa/compartir\u201D bajo la ley de California. Respetamos las señales Global Privacy Control. "
            : "We use cookies to run our site, analyze traffic, and for advertising. Sharing data with ad partners (Meta, Google) may be a \u201Csale/share\u201D under California law. We honor Global Privacy Control signals. "}
          <Link to={`${linkPrefix}/policies?tab=privacy`} className="underline text-white hover:text-orange-300" data-testid="cookie-banner-privacy-link">
            {lang === "es" ? "Política de Privacidad" : "Privacy Policy"}
          </Link>
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => applyConsent(false)} className="px-4 py-2 rounded-full border border-white/40 text-xs font-bold hover:bg-white/10 transition-colors whitespace-nowrap" data-testid="cookie-opt-out-btn">
            {lang === "es" ? "No Vender/Compartir Mis Datos" : "Opt Out of Sale/Sharing"}
          </button>
          <button onClick={() => applyConsent(true)} className="px-4 py-2 rounded-full bg-white text-[#1A202C] text-xs font-bold hover:bg-gray-200 transition-colors whitespace-nowrap" data-testid="cookie-accept-btn">
            {lang === "es" ? "Aceptar" : "Accept"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmailCapturePopup() {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    const dismissed = localStorage.getItem("lbb_email_dismissed");
    const captured = localStorage.getItem("lbb_email_captured");
    if (dismissed || captured) return;

    axios.get(`${API}/storefront/popup-config`).then(r => {
      const cfg = r.data;
      setConfig(cfg);
      if (!cfg.enabled) return;
      const timer = setTimeout(() => setShow(true), (cfg.delay_seconds || 15) * 1000);
      return () => clearTimeout(timer);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/email-capture`, { email, name });
      if (r.data.already_subscribed) {
        setPromoCode(r.data.promo_code);
        toast.success("You're already subscribed!");
      } else {
        setPromoCode(r.data.promo_code);
        toast.success(`Welcome! Your ${config?.discount_pct || 5}% discount is ready.`);
      }
      setSubmitted(true);
      localStorage.setItem("lbb_email_captured", "1");
    } catch {
      toast.error("Something went wrong");
    }
    setLoading(false);
  };

  const dismiss = () => {
    setShow(false);
    localStorage.setItem("lbb_email_dismissed", "1");
  };

  if (!show || !config) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] flex items-center justify-center p-4" onClick={dismiss} data-testid="email-capture-overlay">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="email-capture-popup">
          <div className="bg-gradient-to-r from-[#2D3748] to-[#1A202C] p-6 text-center text-white">
            <div className="w-14 h-14 bg-[#C05621] rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Tag size={24} />
            </div>
            <h2 className="font-heading text-xl font-bold">{config.title}</h2>
            <p className="text-sm text-gray-300 mt-1">{config.subtitle}</p>
          </div>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="p-6 space-y-3">
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name (optional)"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30 focus:border-[#C05621]"
                data-testid="email-capture-name" />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C05621]/30 focus:border-[#C05621]"
                data-testid="email-capture-email" />
              <button type="submit" disabled={loading}
                className="w-full py-3 bg-[#C05621] text-white rounded-xl font-bold text-sm hover:bg-[#DD6B20] transition-colors disabled:opacity-50"
                data-testid="email-capture-submit">
                {loading ? "Sending..." : config.cta_text}
              </button>
              <button type="button" onClick={dismiss} className="w-full text-xs text-gray-400 hover:text-gray-600 pt-1">{config.dismiss_text}</button>
            </form>
          ) : (
            <div className="p-6 text-center space-y-3" data-testid="email-capture-success">
              <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto">
                <Check size={28} className="text-[#38A169]" />
              </div>
              <h3 className="font-heading text-lg font-bold text-[#1A202C]">Your code is ready!</h3>
              <div className="bg-[#FDFBF7] border-2 border-dashed border-[#C05621] rounded-xl px-4 py-3">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Your promo code</p>
                <p className="text-xl font-mono font-bold text-[#C05621]" data-testid="promo-code-display">{promoCode}</p>
              </div>
              <p className="text-xs text-gray-500">Valid for 30 days. Apply at checkout.</p>
              <button onClick={dismiss} className="mt-2 px-6 py-2 bg-[#2D3748] text-white text-sm font-bold rounded-xl hover:bg-[#1A202C] transition-colors"
                data-testid="email-capture-start-shopping">Start Shopping</button>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

// ─── COMPARE BAR ───
function CompareBar() {
  const { compareItems, removeFromCompare, clearCompare } = useCompare();
  const [showFull, setShowFull] = useState(false);
  const [fullProducts, setFullProducts] = useState([]);
  const navigate = useNavigate();

  const openComparison = async () => {
    if (compareItems.length < 2) {
      toast.error("Select at least 2 products to compare");
      return;
    }
    try {
      const ids = compareItems.map(c => c.id).join(",");
      const r = await axios.get(`${API}/products/compare?ids=${ids}`);
      setFullProducts(r.data.products);
      setShowFull(true);
    } catch { toast.error("Failed to load comparison"); }
  };

  if (compareItems.length === 0) return null;

  return (
    <>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] bg-[#2D3748] text-white rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 max-w-lg w-[calc(100%-2rem)]"
        data-testid="compare-bar">
        <Scale size={18} className="text-[#DD6B20] flex-shrink-0" />
        <div className="flex items-center gap-2 flex-1 overflow-x-auto">
          {compareItems.map(item => (
            <div key={item.id} className="flex items-center gap-1.5 bg-white/10 rounded-lg px-2 py-1 flex-shrink-0">
              <img loading="lazy" decoding="async" src={item.images?.[0]?.url || ""} alt="" className="w-6 h-6 rounded object-cover" />
              <span className="text-[10px] font-medium max-w-[80px] truncate">{item.name}</span>
              <button onClick={() => removeFromCompare(item.id)} className="text-white/50 hover:text-white"><X size={12} /></button>
            </div>
          ))}
        </div>
        <button onClick={openComparison}
          className="px-4 py-1.5 bg-[#C05621] text-white text-xs font-bold rounded-lg hover:bg-[#DD6B20] flex-shrink-0"
          data-testid="compare-now-btn">Compare ({compareItems.length})</button>
        <button onClick={clearCompare} className="text-white/40 hover:text-white"><X size={16} /></button>
      </div>

      {showFull && fullProducts.length >= 2 && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setShowFull(false)} data-testid="compare-modal">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl max-w-5xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h2 className="font-heading text-lg font-bold text-[#1A202C]">Compare Products</h2>
              <button onClick={() => setShowFull(false)} className="p-2 hover:bg-gray-100 rounded-full" data-testid="compare-close"><X size={20} /></button>
            </div>
            <div className="p-5">
              <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${fullProducts.length}, 1fr)` }}>
                {/* Images */}
                {fullProducts.map(p => (
                  <div key={p.id} className="text-center">
                    <div className="rounded-xl overflow-hidden bg-[#F7FAFC] aspect-square mb-3">
                      <img loading="lazy" decoding="async" src={p.images?.[0]?.url || "https://placehold.co/300x300"} alt={p.name} className="w-full h-full object-contain" />
                    </div>
                    <p className="text-[10px] text-[#C05621] font-semibold uppercase">{p.brand}</p>
                    <p className="text-sm font-bold text-[#1A202C] line-clamp-2 cursor-pointer hover:text-[#C05621]" onClick={() => { setShowFull(false); navigate(`/product/${p.slug}`); }}>{p.name}</p>
                  </div>
                ))}
              </div>
              {/* Comparison rows */}
              <div className="mt-6 divide-y divide-gray-100">
                {[
                  { label: "Price", fn: p => <span className="text-lg font-bold text-[#1A202C]">${p.price?.toFixed(2)}</span> },
                  { label: "Pickup Price", fn: p => <span className="text-sm font-semibold text-[#38A169]">${(p.price * 0.9).toFixed(2)}</span> },
                  { label: "Category", fn: p => <span className="text-sm text-gray-600">{p.category}</span> },
                  { label: "Dimensions", fn: p => <span className="text-xs text-gray-500">{p.dimensions || "—"}</span> },
                  { label: "Material", fn: p => <span className="text-xs text-gray-500">{p.material || "—"}</span> },
                  { label: "Color", fn: p => <span className="text-xs text-gray-500">{p.color || "—"}</span> },
                  { label: "Shipping", fn: p => <span className="text-xs text-gray-500">{p.shipping_days_min}-{p.shipping_days_max} days</span> },
                  { label: "In Stock", fn: p => p.in_stock ? <span className="text-xs text-green-600 font-semibold">Yes</span> : <span className="text-xs text-red-500">No</span> },
                ].map((row, i) => (
                  <div key={i} className="grid py-3" style={{ gridTemplateColumns: `120px repeat(${fullProducts.length}, 1fr)` }}>
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider self-center">{row.label}</span>
                    {fullProducts.map(p => <div key={p.id} className="text-center self-center">{row.fn(p)}</div>)}
                  </div>
                ))}
              </div>
              {/* Add to cart buttons */}
              <div className="grid gap-4 mt-4" style={{ gridTemplateColumns: `repeat(${fullProducts.length}, 1fr)` }}>
                {fullProducts.map(p => (
                  <button key={p.id} onClick={() => { toast.success(`${p.name} added to cart`); }}
                    className="py-2.5 bg-[#C05621] text-white text-sm font-bold rounded-xl hover:bg-[#DD6B20] transition-colors"
                    data-testid={`compare-add-${p.id}`}>Add to Cart</button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}

// ─── PRODUCT MANAGEMENT PANEL ───
function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const handleScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  if (!show) return null;
  return (
    <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-24 left-6 z-40 w-10 h-10 bg-[#2D3748] text-white rounded-full shadow-lg hover:bg-[#1A202C] transition-all flex items-center justify-center"
      aria-label="Back to top">
      <ChevronUp size={20} />
    </button>
  );
}

// ─── CHAT MESSAGE RENDERER ───
// ─── SHARED CHAT STATE — one conversation across the floating widget, product-page
// ask boxes, page navigation, and full reloads (history restored from backend) ───
const ChatContext = createContext(null);
const useChat = () => useContext(ChatContext);

function ChatProvider({ children }) {
  const { lang } = useTranslation();
  const location = useLocation();
  const { cart } = useCart();
  const sessionId = getSessionId();
  const welcomeMsg = lang === "es"
    ? "¡Hola! Bienvenido a La Bodega Bay Furniture. Puedo ayudarte a encontrar el mueble perfecto para tu hogar, responder dudas sobre envío o financiamiento, o recomendarte piezas que combinen con tu estilo. ¿Qué buscas hoy?"
    : "Hi! Welcome to La Bodega Bay Furniture. I can help you find the perfect piece for your home, answer questions about shipping or financing, or suggest items that match your style. What are you looking for today?";
  const [messages, setMessages] = useState([{ role: "assistant", content: welcomeMsg, id: "welcome" }]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    axios.get(`${API}/chat/history/${sessionId}_chat`).then(r => {
      const hist = r.data?.messages || [];
      if (hist.length > 0) setMessages([{ role: "assistant", content: welcomeMsg, id: "welcome" }, ...hist]);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPageContext = () => {
    const path = location.pathname;
    if (path.includes("/cart")) return "cart";
    if (path.includes("/checkout")) return "checkout";
    if (path.includes("/product/")) return "product";
    if (path.includes("/shop") || path.includes("/category")) return "shop";
    return "home";
  };

  const sendChat = async (text) => {
    const userMsg = (text || "").trim();
    if (!userMsg || loading) return;
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);
    setSuggestions([]);
    try {
      const cartItems = cart?.items?.map(i => ({ product_id: i.product_id, name: i.product?.name || "", quantity: i.quantity })) || [];
      const recentlyViewed = JSON.parse(localStorage.getItem("lbb_recently_viewed") || "[]").map(p => p.name).slice(0, 5);
      const slugMatch = location.pathname.match(/\/product\/([^\/]+)/);
      const r = await axios.post(`${API}/chat`, {
        session_id: sessionId + "_chat",
        message: userMsg,
        context: {
          page: getPageContext(),
          cart_items: cartItems,
          recently_viewed: recentlyViewed,
          product_slug: slugMatch ? slugMatch[1] : "",
          lang: typeof window !== "undefined" && (window.location.pathname === "/es" || window.location.pathname.startsWith("/es/")) ? "es" : "en",
          url: typeof window !== "undefined" ? window.location.pathname : "",
        }
      });
      const msgId = r.data.message_id || "";
      if (r.data.taken_over) {
        setMessages(prev => [...prev, { role: "assistant", content: "Your message has been sent to our team. They'll reply shortly!", id: "waiting" }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: r.data.response, id: msgId, products: r.data.products || [] }]);
        if (r.data.suggestions?.length > 0) setSuggestions(r.data.suggestions);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: lang === "es" ? "Lo siento, tengo problemas para conectarme. ¡Llámanos al (209) 827-4076!" : "Sorry, I'm having trouble connecting. Please call us at (209) 827-4076!" }]);
    }
    setLoading(false);
  };

  return (
    <ChatContext.Provider value={{ messages, setMessages, loading, suggestions, setSuggestions, sendChat }}>
      {children}
    </ChatContext.Provider>
  );
}

// ─── PRODUCT PAGE ASK BOX — same conversation as the floating widget ───
function ProductChatBox({ productName }) {
  const { messages, loading, sendChat } = useChat();
  const [expanded, setExpanded] = useState(false);
  const [input, setInput] = useState("");
  const { lang } = useTranslation();
  const scrollRef = useRef(null);
  const es = lang === "es";
  const convo = messages.filter(m => m.id !== "welcome");

  useEffect(() => {
    if (expanded && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, expanded]);

  const submit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const q = input.trim();
    setInput("");
    setExpanded(true);
    await sendChat(q);
  };

  return (
    <div className="border border-[#C05621]/25 bg-[#FFFBF7] rounded-2xl overflow-hidden" data-testid="product-chat-box">
      <div className="px-4 pt-3.5 pb-2 flex items-start gap-2.5">
        <div className="w-8 h-8 rounded-full bg-[#C05621] flex items-center justify-center flex-shrink-0"><MessageCircle size={15} className="text-white" /></div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-sm text-[#1A202C]">{es ? "¿Tienes una pregunta? ¡Deja que nuestro Asistente IA te ayude!" : "Have a question? Let our AI Assistant help!"}</p>
          <p className="text-[10px] text-gray-400">{es ? "IA — puede cometer errores. Confirma detalles: (209) 827-4076" : "AI — may make mistakes. Verify details: (209) 827-4076"}</p>
        </div>
      </div>
      {!expanded && convo.length > 0 && (
        <button onClick={() => setExpanded(true)} className="mx-4 mb-2 text-xs font-semibold text-[#C05621] hover:underline" data-testid="product-chat-continue">
          {es ? `Continuar tu conversación (${convo.length}) →` : `Continue your conversation (${convo.length} messages) →`}
        </button>
      )}
      {expanded && (
        <div ref={scrollRef} className="max-h-80 overflow-y-auto px-4 py-2.5 space-y-2 bg-white/70 border-t border-[#C05621]/10" data-testid="product-chat-thread">
          {convo.map((m, i) => (
            <div key={i}>
              <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-xs leading-relaxed ${m.role === "user" ? "bg-[#2D3748] text-white rounded-br-sm" : "bg-gray-100 text-gray-800 rounded-bl-sm"}`}>
                  <ChatMessageContent text={m.content} isUser={m.role === "user"} />
                </div>
              </div>
              {m.role === "assistant" && m.products?.length > 0 && (
                <div className="flex gap-2 overflow-x-auto scrollbar-hide mt-1.5 pb-1">
                  {m.products.map(p => (
                    <Link key={p.id} to={`/product/${p.slug}`} className="flex-shrink-0 w-24 bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-[#C05621] transition-colors" data-testid={`product-chat-card-${p.slug}`}>
                      <img src={p.image || "https://placehold.co/96x64?text=No+Photo"} alt={p.name} className="w-24 h-16 object-cover" loading="lazy" />
                      <div className="p-1">
                        <p className="text-[9px] font-semibold text-[#1A202C] leading-tight line-clamp-2">{p.name}</p>
                        <p className="text-[10px] font-bold text-[#C05621]">${Number(p.price || 0).toFixed(2)}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex gap-1 px-3 py-2">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }} />
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }} />
            </div>
          )}
        </div>
      )}
      <form onSubmit={submit} className="flex gap-2 p-3 border-t border-[#C05621]/10">
        <input value={input} onChange={e => setInput(e.target.value)}
          placeholder={es ? "Escribe tu pregunta sobre este producto..." : "Type your question about this piece..."}
          className="flex-1 px-3.5 py-2.5 bg-white border border-gray-200 rounded-full text-sm outline-none focus:border-[#C05621] min-w-0"
          data-testid="product-chat-input" />
        <button type="submit" disabled={loading || !input.trim()} className="px-4 py-2 bg-[#C05621] text-white rounded-full text-sm font-bold disabled:opacity-40 hover:bg-[#DD6B20] transition-colors" data-testid="product-chat-send">
          {es ? "Preguntar" : "Ask"}
        </button>
      </form>
    </div>
  );
}

function ChatMessageContent({ text, isUser }) {
  if (isUser) return <span>{text}</span>;
  // Parse markdown links [text](url), **bold**, bare /product/ paths, and bullet points
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|(?:^|\s)(\/product\/[\w-]+))/gm);
  return (
    <span>
      {parts.map((part, i) => {
        if (!part) return null;
        const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (linkMatch) {
          let linkUrl = linkMatch[2].replace("https://labodegabayfurniture.com", "");
          return <a key={i} href={linkUrl} className="text-[#C05621] underline font-medium hover:text-[#DD6B20]" data-testid="chat-product-link">{linkMatch[1]}</a>;
        }
        const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
        if (boldMatch) return <strong key={i}>{boldMatch[1]}</strong>;
        // Handle bare /product/ paths
        const pathMatch = part.trim().match(/^(\/product\/[\w-]+)$/);
        if (pathMatch) return <a key={i} href={pathMatch[1]} className="text-[#C05621] underline font-medium hover:text-[#DD6B20]" data-testid="chat-product-link">View Product</a>;
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}

// ─── AI CHATBOT ───
function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const { lang } = useTranslation();
  const welcomeMsg = lang === "es"
    ? "¡Hola! Bienvenido a La Bodega Bay Furniture. Puedo ayudarte a encontrar el mueble perfecto para tu hogar, responder dudas sobre envío o financiamiento, o recomendarte piezas que combinen con tu estilo. ¿Qué buscas hoy?"
    : "Hi! Welcome to La Bodega Bay Furniture. I can help you find the perfect piece for your home, answer questions about shipping or financing, or suggest items that match your style. What are you looking for today?";
  const { messages, setMessages, loading, suggestions, setSuggestions, sendChat } = useChat();
  const [input, setInput] = useState("");
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [proactiveShown, setProactiveShown] = useState({});
  const sessionId = getSessionId();
  const { cart, addToCart } = useCart();
  const location = useLocation();

  // Proactive triggers
  useEffect(() => {
    if (!isOpen) return;
    // Cart linger detection
    const cartTimer = setTimeout(() => {
      if (location.pathname.includes("/cart") && !proactiveShown.cart_linger) {
        axios.post(`${API}/chat/proactive-trigger?trigger_type=cart_linger&session_id=${sessionId}`).then(r => {
          if (r.data.message) { setMessages(prev => [...prev, { role: "assistant", content: r.data.message, id: "proactive_cart" }]); setProactiveShown(prev => ({...prev, cart_linger: true})); }
        }).catch(() => {});
      }
    }, 30000);
    // Checkout hesitation
    const checkoutTimer = setTimeout(() => {
      if (location.pathname.includes("/checkout") && !proactiveShown.checkout) {
        axios.post(`${API}/chat/proactive-trigger?trigger_type=checkout_hesitation&session_id=${sessionId}`).then(r => {
          if (r.data.message) { setMessages(prev => [...prev, { role: "assistant", content: r.data.message, id: "proactive_checkout" }]); setProactiveShown(prev => ({...prev, checkout: true})); }
        }).catch(() => {});
      }
    }, 20000);
    return () => { clearTimeout(cartTimer); clearTimeout(checkoutTimer); };
  }, [isOpen, location.pathname, sessionId, proactiveShown]);

  const getPageContext = () => {
    const path = location.pathname;
    if (path.includes("/cart")) return "cart";
    if (path.includes("/checkout")) return "checkout";
    if (path.includes("/product/")) return "product";
    if (path.includes("/shop") || path.includes("/category")) return "shop";
    return "home";
  };

  const getCurrentProductSlug = () => {
    const path = location.pathname;
    const match = path.match(/\/product\/([^\/]+)/);
    return match ? match[1] : "";
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    await sendChat(userMsg);
  };

  const addSuggestion = async (s) => {
    await addToCart(s.id);
    axios.post(`${API}/chat/feedback`, null, { params: { suggestion_id: s.id, action: "accepted" } }).catch(() => {});
    setSuggestions(prev => prev.filter(x => x.id !== s.id));
    const addMsg = lang === "es"
      ? `¡Excelente elección! Agregué **${s.name}** a tu carrito${s.discount_pct ? ` con ${s.discount_pct}% de descuento` : ""}. ¿Algo más en lo que pueda ayudarte?`
      : `Great choice! I've added **${s.name}** to your cart${s.discount_pct ? ` with ${s.discount_pct}% off` : ""}. Anything else I can help with?`;
    setMessages(prev => [...prev, { role: "assistant", content: addMsg, id: "" }]);
  };

  const sendFeedback = (msgId, rating) => {
    if (feedbackGiven[msgId]) return;
    axios.post(`${API}/chat/feedback`, null, { params: { message_id: msgId, rating } }).catch(() => {});
    setFeedbackGiven(prev => ({ ...prev, [msgId]: rating }));
  };

  const clearMyData = async () => {
    await axios.post(`${API}/chat/clear-data?session_id=${sessionId}_chat`);
    setMessages([{ role: "assistant", content: "All your conversation data has been deleted. Your privacy matters to us. How can I help you today?", id: "cleared" }]);
    setShowPrivacy(false);
    toast.success("Your data has been cleared");
  };

  return (
    <>
      <button
        onClick={() => {
          const next = !isOpen;
          setIsOpen(next);
          if (next && typeof window !== "undefined" && window.gtag) {
            window.gtag("event", "chat_started", { engagement_time_msec: 1, event_category: "engagement" });
          }
        }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#C05621] text-white rounded-full shadow-xl hover:bg-[#DD6B20] transition-all flex items-center justify-center hover:scale-110"
        data-testid="chat-toggle" aria-label="Chat with us"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
            style={{ height: "540px" }}
            data-testid="chat-window"
          >
            <div className="bg-[#2D3748] text-white p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-[#C05621] rounded-full flex items-center justify-center"><MessageCircle size={20} /></div>
              <div className="flex-1">
                <h3 className="font-semibold text-sm">La Bodega Bay Assistant</h3>
                <p className="text-[10px] text-gray-300" data-testid="chat-ai-disclaimer">{lang === "es" ? "Asistente de IA — puede cometer errores. Confirma detalles con la tienda: (209) 827-4076" : "AI assistant — may make mistakes. Verify details with our store: (209) 827-4076"}</p>
              </div>
              <button onClick={() => setShowPrivacy(!showPrivacy)} className="text-white/40 hover:text-white/80 transition-colors" title="Privacy & Data">
                <Shield size={16} />
              </button>
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            </div>
            {/* Privacy Panel */}
            {showPrivacy && (
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 space-y-2" data-testid="chat-privacy">
                <p className="text-[10px] font-semibold text-gray-600 uppercase">Your Privacy</p>
                <p className="text-[10px] text-gray-500 leading-relaxed">We store your conversation to personalize recommendations. We never share your data with third parties.</p>
                <div className="flex gap-2">
                  <button onClick={() => { axios.get(`${API}/chat/my-data?session_id=${sessionId}_chat`).then(r => { toast.success(`We store: ${r.data.message_count} messages and your style preferences.`); }); }}
                    className="px-2.5 py-1 text-[9px] font-bold border border-gray-300 rounded-full text-gray-600 hover:bg-gray-100">View My Data</button>
                  <button onClick={clearMyData} className="px-2.5 py-1 text-[9px] font-bold border border-red-200 rounded-full text-red-500 hover:bg-red-50">Delete My Data</button>
                  <button onClick={() => setShowPrivacy(false)} className="px-2.5 py-1 text-[9px] font-bold text-gray-400 hover:text-gray-600 ml-auto">Close</button>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.map((msg, i) => (
                <div key={i}>
                  <div className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${msg.role === "user" ? "bg-[#C05621] text-white rounded-br-md" : "bg-gray-100 text-gray-700 rounded-bl-md"}`}>
                      <ChatMessageContent text={msg.content} isUser={msg.role === "user"} />
                    </div>
                  </div>
                  {/* Product photo cards for recommended items */}
                  {msg.role === "assistant" && msg.products?.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto scrollbar-hide mt-2 pb-1" data-testid={`chat-product-cards-${i}`}>
                      {msg.products.map(p => (
                        <Link key={p.id} to={`/product/${p.slug}`} className="flex-shrink-0 w-28 bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-[#C05621] transition-colors shadow-sm" data-testid={`chat-product-card-${p.slug}`}>
                          <img src={p.image || "https://placehold.co/112x84?text=No+Photo"} alt={p.name} className="w-28 h-20 object-cover" loading="lazy" />
                          <div className="p-1.5">
                            <p className="text-[10px] font-semibold text-[#1A202C] leading-tight line-clamp-2">{p.name}</p>
                            <p className="text-[11px] font-bold text-[#C05621] mt-0.5">${Number(p.price || 0).toFixed(2)}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                  {/* Feedback buttons for assistant messages */}
                  {msg.role === "assistant" && msg.id && msg.id !== "welcome" && (
                    <div className="flex gap-1 mt-1 ml-1">
                      {feedbackGiven[msg.id] ? (
                        <span className="text-[9px] text-gray-400">{feedbackGiven[msg.id] === "up" ? "Thanks!" : "We'll improve"}</span>
                      ) : (
                        <>
                          <button onClick={() => sendFeedback(msg.id, "up")} className="text-gray-300 hover:text-green-500 transition-colors" data-testid={`feedback-up-${i}`} title="Helpful">
                            <ChevronUp size={14} />
                          </button>
                          <button onClick={() => sendFeedback(msg.id, "down")} className="text-gray-300 hover:text-red-400 transition-colors" data-testid={`feedback-down-${i}`} title="Not helpful">
                            <ChevronDown size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Suggestion Cards */}
              {suggestions.length > 0 && (
                <div className="space-y-2" data-testid="chat-suggestions">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Suggested for you</p>
                  {suggestions.map(s => (
                    <div key={s.id} className="bg-gradient-to-r from-[#FFF5F1] to-white border border-[#C05621]/20 rounded-xl p-3 flex items-center gap-3" data-testid={`suggestion-${s.id}`}>
                      {s.image && <img src={s.image} alt={s.name} className="w-12 h-12 rounded-lg object-cover border border-gray-100 flex-shrink-0" loading="lazy" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-[#1A202C] line-clamp-1">{s.name}</p>
                        <p className="text-[10px] text-gray-500">{s.brand} &middot; {s.reason}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm font-bold text-[#C05621]">${Number(s.discounted_price ?? s.price).toFixed(2)}</span>
                        </div>
                      </div>
                      <button onClick={() => addSuggestion(s)}
                        className="px-3 py-1.5 bg-[#C05621] text-white text-[10px] font-bold rounded-lg hover:bg-[#DD6B20] flex-shrink-0"
                        data-testid={`add-suggestion-${s.id}`}>
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            {messages.length <= 2 && (
              <div className="px-3 pb-1 flex gap-1.5 flex-wrap" data-testid="quick-actions">
                {["I need a sofa", "Show me bedroom sets", "What financing options?", "Delivery info"].map(q => (
                  <button key={q} onClick={() => { setInput(q); }} className="px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-full text-[10px] font-medium text-gray-600 hover:bg-gray-100 transition-colors">{q}</button>
                ))}
              </div>
            )}

            <form onSubmit={sendMessage} className="p-3 border-t border-gray-100 flex gap-2">
              <input
                value={input} onChange={e => setInput(e.target.value)}
                placeholder={lang === "es" ? "Pregunta sobre muebles, envío, financiamiento..." : "Ask about furniture, shipping, financing..."}
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C05621]/30"
                data-testid="chat-input"
              />
              <button type="submit" disabled={loading} className="p-2 bg-[#C05621] text-white rounded-lg hover:bg-[#DD6B20] disabled:opacity-50" data-testid="chat-send">
                <Send size={18} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── CODE EDITOR PANEL ───
function BlogPage() {
  const [posts, setPosts] = useState([]);
  const { t, lang } = useTranslation();
  useEffect(() => {
    axios.get(`${API}/blog?limit=50`).then(r => setPosts(r.data)).catch(() => {});
    updatePageSEO({
      title: lang === "es"
        ? "Guías de Muebles y Blog | La Bodega Bay Furniture"
        : "Furniture Buying Guides & Blog | La Bodega Bay Furniture",
      description: lang === "es"
        ? "Guías de compra, consejos de diseño e inspiración para tu hogar. Más de 25 años sirviendo al Valle Central y Bay Area."
        : "In-depth furniture buying guides, design tips, and shopping inspiration from La Bodega Bay Furniture. 25+ years serving the Central Valley & Bay Area.",
      canonical: lang === "es"
        ? "https://labodegabayfurniture.com/es/blog"
        : "https://labodegabayfurniture.com/blog",
      ogType: "website",
    });
  }, [lang]);

  const pillars = posts.filter(p => p.subtype === "pillar");
  const daily = posts.filter(p => p.subtype !== "pillar");

  const linkPrefix = lang === "es" ? "/es" : "";
  const linkFor = (post) => {
    // Slug URLs whenever a post has one (pillars + Topic Radar dailies); legacy dailies fall back to id
    if (post.slug) return `${linkPrefix}/blog/${post.slug}`;
    return `${linkPrefix}/blog/${post.id}`;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-12 min-h-screen" data-testid="blog-page">
      <h1 className="font-heading text-4xl font-bold text-[#1A202C] mb-2">{t("blog.title")}</h1>
      <p className="text-gray-500 mb-10">{t("blog.subtitle")}</p>

      {posts.length === 0 ? (
        <p className="text-gray-400 text-center py-20">{t("blog.noPosts")}</p>
      ) : (
        <>
          {/* PILLAR BUYING GUIDES — prominently featured */}
          {pillars.length > 0 && (
            <section className="mb-14" data-testid="pillar-section">
              <div className="flex items-baseline justify-between mb-5">
                <h2 className="font-heading text-2xl font-bold text-[#1A202C]">{t("blog.buyingGuides")}</h2>
                <span className="text-xs text-gray-400 uppercase tracking-wider">{t("blog.buyingGuidesTag")}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {pillars.map((post, idx) => (
                  <Link key={post.id} to={linkFor(post)} className="block group" data-testid={`pillar-card-${idx}`}>
                    <article className="relative bg-gradient-to-br from-[#FFF5F1] to-white border border-[#FBD9C8] rounded-2xl p-6 hover:shadow-lg hover:border-[#C05621] transition-all h-full">
                      <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wider bg-[#C05621] text-white px-2 py-1 rounded-full">{t("blog.guide")}</span>
                      <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                        <span>{new Date(post.published_at || post.created_at).toLocaleDateString(lang === "es" ? "es-MX" : "en-US")}</span>
                        {post.internal_links_count > 0 && (
                          <span className="text-[#C05621] font-semibold">· {t("blog.productsLinked", { count: post.internal_links_count })}</span>
                        )}
                      </div>
                      <h3 className="font-heading text-lg font-bold text-[#1A202C] group-hover:text-[#C05621] transition-colors mb-2 leading-tight">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">{post.meta_description}</p>
                      <div className="mt-4 text-xs font-bold text-[#C05621] group-hover:underline">{t("blog.readTheGuide")} →</div>
                    </article>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* DAILY POSTS — listed below */}
          {daily.length > 0 && (
            <section data-testid="daily-section">
              {pillars.length > 0 && (
                <h2 className="font-heading text-2xl font-bold text-[#1A202C] mb-5">{t("blog.latestPosts")}</h2>
              )}
              <div className="space-y-6">
                {daily.map((post, idx) => (
                  <Link key={post.id} to={linkFor(post)} className="block group" data-testid={`daily-card-${idx}`}>
                    <article className="bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
                        <span>{new Date(post.published_at || post.created_at).toLocaleDateString(lang === "es" ? "es-MX" : "en-US")}</span>
                        {post.tags?.slice(0, 3).map(t => (
                          <span key={t} className="bg-gray-100 px-2 py-0.5 rounded-full">{t}</span>
                        ))}
                      </div>
                      <h3 className="font-heading text-xl font-bold text-[#1A202C] group-hover:text-[#C05621] transition-colors mb-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2">{post.meta_description}</p>
                    </article>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function BlogShopTheGuide({ products, lang, postSlug }) {
  // Live cards: price/stock come from the API at read time, so an article never shows a stale price.
  const { addToCart } = useCart();
  const [adding, setAdding] = useState("");
  if (!products || products.length === 0) return null;
  const es = lang === "es";
  const pfx = es ? "/es" : "";
  const listName = `blog:${postSlug}`;
  const add = async (p) => {
    setAdding(p.id);
    try {
      await addToCart(p.id, 1);
      track("add_to_cart_from_blog", { item_list_name: listName, item_id: p.id, item_name: p.name, value: Number(p.price || 0), currency: "USD" });
      toast.success(es ? `${p.name} agregado al carrito` : `${p.name} added to cart`);
    } catch { toast.error(es ? "No se pudo agregar" : "Could not add to cart"); }
    setAdding("");
  };
  return (
    <section className="mt-12 pt-8 border-t border-gray-200" data-testid="blog-shop-the-guide">
      <h2 className="font-heading text-2xl font-bold text-[#1A202C] mb-1">{es ? "Compra los muebles de esta guía" : "Shop the furniture in this guide"}</h2>
      <p className="text-sm text-gray-500 mb-6">{es ? "Precios y disponibilidad actualizados al momento." : "Live prices and availability — updated as you read."}</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {products.map((p, i) => {
          const name = (es && p.name_es) || p.name;
          const low = p.in_stock && p.stock_quantity > 0 && p.stock_quantity <= 3;
          return (
            <div key={p.id} className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col" data-testid={`blog-product-card-${i}`}>
              <Link to={`${pfx}/product/${p.slug}`} onClick={() => track("select_item", { item_list_name: listName, items: [{ item_id: p.id, item_name: p.name, price: Number(p.price || 0) }] })} className="block aspect-square bg-[#F7FAFC]">
                {p.image && <img src={getOptimizedImage(p.image, 400)} alt={name} loading="lazy" decoding="async" width="400" height="400" className="w-full h-full object-contain" onError={imgFallback} />}
              </Link>
              <div className="p-3 flex flex-col flex-1">
                <Link to={`${pfx}/product/${p.slug}`} className="text-sm font-semibold text-[#1A202C] line-clamp-2 hover:text-[#C05621] min-h-[2.5rem]" data-testid={`blog-product-name-${i}`}>{name}</Link>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-lg font-bold text-[#1A202C]" data-testid={`blog-product-price-${i}`}>${Number(p.price || 0).toFixed(2)}</span>
                  {p.original_price > p.price && <span className="text-xs text-gray-400 line-through">${Number(p.original_price).toFixed(2)}</span>}
                </div>
                <p className={`text-[11px] font-semibold mt-1 ${p.in_stock ? (low ? "text-[#E53E3E]" : "text-[#38A169]") : "text-gray-400"}`} data-testid={`blog-product-stock-${i}`}>
                  {!p.in_stock ? (es ? "Agotado" : "Out of stock") : low ? (es ? `Solo quedan ${p.stock_quantity}` : `Only ${p.stock_quantity} left`) : (es ? "Disponible" : "In stock")}
                </p>
                <button onClick={() => add(p)} disabled={!p.in_stock || adding === p.id}
                  className="mt-auto pt-3 w-full inline-flex items-center justify-center gap-1.5 bg-[#C05621] hover:bg-[#9C4221] disabled:bg-gray-300 text-white text-xs font-bold py-2 rounded-lg transition-colors"
                  data-testid={`blog-add-to-cart-${i}`}>
                  <ShoppingCart size={14} /> {adding === p.id ? "…" : es ? "Agregar al carrito" : "Add to Cart"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function BlogPostPage() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const { t, lang } = useTranslation();
  useEffect(() => {
    axios.get(`${API}/blog/${postId}`).then(r => {
      setPost(r.data);
      const isPillar = r.data.subtype === "pillar";
      const canonicalKey = r.data.slug || postId;
      const canonicalUrl = lang === "es"
        ? `https://labodegabayfurniture.com/es/blog/${canonicalKey}`
        : `https://labodegabayfurniture.com/blog/${canonicalKey}`;
      updatePageSEO({
        title: lang === "es"
          ? `${r.data.title} | Blog La Bodega Bay Furniture`
          : `${r.data.title} | La Bodega Bay Furniture Blog`,
        description: r.data.meta_description || r.data.content?.substring(0, 160)?.replace(/<[^>]*>/g, '') || "",
        canonical: canonicalUrl,
        ogType: "article",
      });

      // ─── AEO: inject Article + FAQPage + Author schemas ───
      document.querySelectorAll('script[data-seo="post-aeo"]').forEach(s => s.remove());
      const inject = (schema) => {
        const sc = document.createElement("script");
        sc.type = "application/ld+json";
        sc.setAttribute("data-seo", "post-aeo");
        sc.textContent = JSON.stringify(schema);
        document.head.appendChild(sc);
      };
      const authorOrg = {
        "@type": "Organization",
        "name": lang === "es" ? "Equipo de La Bodega Bay Furniture" : "La Bodega Bay Furniture Team",
        "url": lang === "es" ? "https://labodegabayfurniture.com/es/about" : "https://labodegabayfurniture.com/about",
        "description": lang === "es"
          ? "Más de 25 años vendiendo muebles en el Valle Central y Bay Area de California."
          : "25+ years selling furniture in California's Central Valley & Bay Area.",
      };
      inject({
        "@context": "https://schema.org",
        "@type": isPillar ? "Article" : "BlogPosting",
        "headline": r.data.title,
        "description": r.data.meta_description || "",
        "datePublished": r.data.published_at || r.data.created_at,
        "dateModified": r.data.updated_at || r.data.published_at || r.data.created_at,
        "author": authorOrg,
        "publisher": {
          "@type": "Organization",
          "name": "La Bodega Bay Furniture",
          "logo": { "@type": "ImageObject", "url": "https://labodegabayfurniture.com/og-image.jpg" },
        },
        "mainEntityOfPage": canonicalUrl,
        "inLanguage": lang === "es" ? "es-US" : "en-US",
      });
      // BreadcrumbList schema
      const siteBase = "https://labodegabayfurniture.com";
      inject({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": lang === "es" ? "Inicio" : "Home", "item": lang === "es" ? `${siteBase}/es` : siteBase },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": lang === "es" ? `${siteBase}/es/blog` : `${siteBase}/blog` },
          { "@type": "ListItem", "position": 3, "name": r.data.title, "item": canonicalUrl },
        ],
      });
      // FAQPage schema
      if (r.data.faqs && r.data.faqs.length > 0) {
        inject({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": r.data.faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        });
      }
    }).catch(() => {});
    return () => {
      document.querySelectorAll('script[data-seo="post-aeo"]').forEach(s => s.remove());
    };
  }, [postId, lang]);

  if (!post) return (
    <div key="blog-skeleton" className="max-w-3xl mx-auto px-4 py-12 min-h-screen animate-pulse" aria-busy="true" data-testid="blog-post-skeleton">
      <div className="h-4 w-24 bg-gray-100 rounded mb-8" />
      <div className="h-10 w-5/6 bg-gray-200 rounded mb-3" /><div className="h-10 w-2/3 bg-gray-200 rounded mb-6" />
      <div className="h-4 w-40 bg-gray-100 rounded mb-6" />
      <div className="h-20 bg-gray-100 rounded-lg mb-8" />
      {[...Array(8)].map((_, i) => <div key={i} className={`h-4 bg-gray-100 rounded mb-3 ${i % 3 === 2 ? "w-3/4" : "w-full"}`} />)}
    </div>
  );

  // Render markdown → HTML (handles links, headings, paragraphs, bold)
  const renderMarkdown = (md) => {
    if (!md) return "";
    let html = md;
    // Strip H1 lines — the page already renders post.title as the H1
    html = html.replace(/^# .*$/gm, '').trim();
    // Markdown links [text](url) → <a>
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#C05621] hover:underline font-medium">$1</a>');
    // Bold **text** → <strong>
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // Italic *text* → <em>
    html = html.replace(/\*([^*\n]+)\*/g, '<em>$1</em>');
    // H2 ## text → styled h2
    html = html.replace(/^## (.*)$/gm, '<h2 class="font-heading text-2xl font-bold text-[#1A202C] mt-10 mb-4">$1</h2>');
    // H3 ### text
    html = html.replace(/^### (.*)$/gm, '<h3 class="font-heading text-xl font-bold text-[#1A202C] mt-7 mb-3">$1</h3>');
    // List items
    html = html.replace(/^- (.*)$/gm, '<li class="ml-5 list-disc mb-1">$1</li>');
    // Wrap consecutive <li> in <ul>
    html = html.replace(/(<li[^>]*>.*?<\/li>\s*)+/gs, m => `<ul class="my-4">${m}</ul>`);
    // Paragraph breaks (double newlines)
    html = html.replace(/\n\n+/g, '</p><p class="mb-4">');
    return `<p class="mb-4">${html}</p>`;
  };

  const isPillar = post.subtype === "pillar";
  const blogHomePath = lang === "es" ? "/es/blog" : "/blog";
  const shopPath = lang === "es" ? "/es/shop" : "/shop";

  return (
    <div key="blog-page" className="max-w-3xl mx-auto px-4 py-12" data-testid="blog-post-page">
      <div className="mb-6">
        <Link to={blogHomePath} className="text-[#C05621] text-sm hover:underline">&larr; {t("blog.backToBlog")}</Link>
      </div>
      <article>
        {isPillar && (
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider bg-[#C05621] text-white px-3 py-1 rounded-full mb-4">
            <span>{t("blog.buyingGuideBadge")}</span>
            {post.internal_links_count > 0 && <span>· {t("blog.productsLinked", { count: post.internal_links_count })}</span>}
          </div>
        )}
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-4 leading-tight">{post.title}</h1>
        <div className="flex items-center gap-3 text-sm text-gray-400 mb-4">
          <span>{new Date(post.published_at || post.created_at).toLocaleDateString(lang === "es" ? "es-MX" : "en-US")}</span>
          {post.tags?.slice(0, 4).map(tg => <span key={tg} className="bg-gray-100 px-2 py-0.5 rounded-full text-xs">{tg}</span>)}
        </div>
        {/* AEO: author byline (E-E-A-T signal) */}
        <div className="bg-gradient-to-r from-[#FFF5F1] to-white border border-[#FBD9C8] rounded-lg p-4 mb-8 flex items-start gap-3" data-testid="author-byline">
          <div className="w-10 h-10 bg-[#C05621] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
            <Award size={20} />
          </div>
          <div className="text-sm">
            <p className="font-bold text-[#1A202C]">
              {lang === "es" ? "Por el Equipo de La Bodega Bay Furniture" : "By the La Bodega Bay Furniture Team"}
            </p>
            <p className="text-gray-600 text-xs mt-0.5">
              {lang === "es"
                ? "Más de 25 años vendiendo muebles en el Valle Central y Bay Area de California. Empresa familiar desde el año 2000."
                : "25+ years selling furniture in California's Central Valley & Bay Area. Family-owned since 2000."}
            </p>
          </div>
        </div>
        {/* AEO: Key Takeaways — snippet-ready summary box */}
        {post.key_takeaways && post.key_takeaways.length > 0 && (
          <section className="bg-[#FFF9F5] border-l-4 border-[#C05621] rounded-r-lg p-5 mb-8" data-testid="key-takeaways">
            <h2 className="font-heading text-lg font-bold text-[#1A202C] mb-3">{lang === "es" ? "Puntos Clave" : "Key Takeaways"}</h2>
            <ul className="space-y-2">
              {post.key_takeaways.map((k, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700 leading-relaxed">
                  <Check size={16} className="text-[#C05621] flex-shrink-0 mt-0.5" />
                  <span>{k}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
        <div
          className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content || post.content_markdown || "") }}
        />

        <BlogShopTheGuide products={post.featured_products} lang={lang} postSlug={post.slug || post.id} />

        {/* FAQ section for pillar posts */}
        {post.faqs && post.faqs.length > 0 && (
          <section className="mt-12 pt-8 border-t border-gray-200" data-testid="post-faqs">
            <h2 className="font-heading text-2xl font-bold text-[#1A202C] mb-6">{t("blog.faqs")}</h2>
            <div className="space-y-3">
              {post.faqs.map((f, i) => (
                <details key={i} className="group bg-gray-50 rounded-lg p-5 hover:bg-gray-100 transition-colors">
                  <summary className="font-semibold text-[#1A202C] cursor-pointer">{f.q}</summary>
                  <p className="mt-3 text-gray-600 text-sm leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Bottom CTA on pillars */}
        {isPillar && (
          <section className="mt-12 p-8 bg-[#FFF5F1] border border-[#FBD9C8] rounded-2xl text-center">
            <h2 className="font-heading text-xl font-bold text-[#1A202C] mb-2">{t("blog.readyToShop")}</h2>
            <p className="text-gray-600 mb-4">{t("blog.readyToShopSub")}</p>
            <Link to={shopPath} className="inline-block bg-[#C05621] hover:bg-[#9C4221] text-white font-bold px-6 py-3 rounded-lg transition-colors">{t("blog.browseFurniture")}</Link>
            <span className="ml-3 text-sm text-gray-500">{t("blog.orCall")} (209) 827-4076</span>
          </section>
        )}
      </article>
    </div>
  );
}


// ─── AGENT HEALTH DASHBOARD WIDGET ───
function QuotePage() {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("review"); // review | checkout | paying | done
  const [deliveryMethod, setDeliveryMethod] = useState("delivery");
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [address, setAddress] = useState("");
  const [custName, setCustName] = useState("");
  const [custEmail, setCustEmail] = useState("");
  const [custPhone, setCustPhone] = useState("");

  useEffect(() => {
    axios.get(`${API}/quote/${quoteId}`).then(r => {
      setQuote(r.data);
      setCustName(r.data.customer_name || "");
      setCustEmail(r.data.customer_email || "");
      setCustPhone(r.data.customer_phone || "");
    }).catch(() => {}).finally(() => setLoading(false));
  }, [quoteId]);

  const pickupPct = quote?.pickup_discount_pct || 10;
  const subtotal = quote?.subtotal || 0;
  const discountAmt = quote?.discount_amount || 0;
  const taxAmt = quote?.tax_amount || 0;
  const assemblyFee = deliveryMethod === "delivery_assembly" ? 99 : 0;
  const pickupSavings = deliveryMethod === "pickup" ? Math.round((subtotal - discountAmt) * pickupPct / 100 * 100) / 100 : 0;
  const finalTotal = Math.round((subtotal - discountAmt + taxAmt + assemblyFee - pickupSavings) * 100) / 100;

  const handleAcceptAndPay = async () => {
    if (!custName || !custEmail) { toast.error("Please fill in your name and email"); return; }
    if (deliveryMethod !== "pickup" && !address) { toast.error("Please enter a delivery address"); return; }
    setStep("paying");
    try {
      const r = await axios.post(`${API}/quote/${quoteId}/accept`, {
        delivery_method: deliveryMethod,
        delivery_address: deliveryMethod === "pickup" ? "2225 E Pacheco Blvd, Los Banos, CA 93635" : address,
        customer_name: custName, customer_email: custEmail, customer_phone: custPhone,
        payment_method: paymentMethod,
      });
      if (r.data.already_completed) { setStep("done"); return; }
      const orderId = r.data.order.id;

      if (paymentMethod === "stripe") {
        const payRes = await axios.post(`${API}/payments/create-session`, { order_id: orderId, origin_url: window.location.origin });
        window.location.href = payRes.data.url;
      } else if (paymentMethod === "affirm") {
        // Redirect to Affirm checkout
        const affirmConfig = await axios.get(`${API}/payments/affirm-config`);
        const affirmUrl = `https://api.affirm.com/api/v1/checkout?public_api_key=${affirmConfig.data.public_key}&amount=${Math.round(finalTotal * 100)}&currency=USD`;
        toast.success("Order created! Affirm financing will be processed. Order ID: " + orderId.substring(0, 8));
        setStep("done");
      } else if (paymentMethod === "snap") {
        toast.success("Order created! Snap Finance application will be processed. Order ID: " + orderId.substring(0, 8));
        setStep("done");
      }
    } catch (e) {
      toast.error("Payment failed. Please try again.");
      setStep("checkout");
    }
  };

  const handlePrint = () => window.print();

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Clock size={32} className="text-gray-300 animate-spin" /></div>;
  if (!quote) return <div className="min-h-[60vh] flex items-center justify-center"><h2 className="text-2xl font-bold">Quote Not Found</h2></div>;

  const isCompleted = quote.status === "completed";
  const isExpired = quote.expired && !isCompleted;

  return (
    <div className="max-w-2xl mx-auto px-4 py-12 print:py-4 print:max-w-none" data-testid="quote-page">
      {/* Print-only header */}
      <div className="hidden print:block text-center mb-6">
        <h1 className="text-2xl font-bold">La Bodega Bay Furniture</h1>
        <p className="text-sm">Los Banos & Newman, CA | (209) 827-4076</p>
      </div>

      {/* Screen header */}
      <div className="text-center mb-8 print:hidden">
        <div className="w-16 h-16 bg-[#C05621] rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Store size={28} className="text-white" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-[#1A202C]">La Bodega Bay Furniture</h1>
        <p className="text-gray-500 text-sm mt-1">Quote #{quote.quote_id}</p>
        <button onClick={handlePrint} className="mt-3 px-4 py-1.5 text-xs font-bold text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 print:hidden" data-testid="print-quote-btn">
          Print Quote
        </button>
      </div>

      {isCompleted && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 text-center print:border-black" data-testid="quote-accepted">
          <Check size={32} className="text-green-600 mx-auto mb-2" />
          <h3 className="font-bold text-green-700">Order Confirmed & Paid</h3>
          <p className="text-sm text-green-600 mt-1">Thank you! We'll be in touch shortly about delivery.</p>
        </div>
      )}
      {isExpired && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-center">
          <AlertCircle size={24} className="text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-600 font-medium">This quote has expired. Please contact us for an updated quote.</p>
        </div>
      )}

      {quote.customer_name && (
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <p className="text-sm"><strong>Prepared for:</strong> {quote.customer_name}</p>
          {quote.customer_email && <p className="text-sm text-gray-500">{quote.customer_email}</p>}
          <p className="text-xs text-gray-400 mt-1">Valid until: {new Date(quote.expires_at).toLocaleDateString()}</p>
        </div>
      )}

      {/* Items Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-100 text-left text-xs text-gray-400 uppercase">
            <th className="px-4 py-3">Item</th><th className="px-4 py-3 text-right">Price</th><th className="px-4 py-3 text-center">Qty</th><th className="px-4 py-3 text-right">Total</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-50">
            {quote.items?.map((item, i) => (
              <tr key={i}>
                <td className="px-4 py-3"><span className="font-medium">{item.name}</span>{item.is_custom && <span className="ml-1 text-[9px] text-purple-500 font-bold">CUSTOM</span>}</td>
                <td className="px-4 py-3 text-right">${item.price?.toFixed(2)}</td>
                <td className="px-4 py-3 text-center">{item.quantity}</td>
                <td className="px-4 py-3 text-right font-semibold">${(item.price * item.quantity).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="bg-[#FDFBF7] rounded-xl p-5 mb-6">
        <div className="flex justify-between text-sm mb-1"><span>Subtotal</span><span className="font-bold">${subtotal.toFixed(2)}</span></div>
        {quote.discount_pct > 0 && <div className="flex justify-between text-sm text-green-600 mb-1"><span>Discount ({quote.discount_pct}%)</span><span>-${discountAmt.toFixed(2)}</span></div>}
        {quote.tax_pct > 0 && <div className="flex justify-between text-sm text-gray-500 mb-1"><span>Tax ({quote.tax_pct}%)</span><span>${taxAmt.toFixed(2)}</span></div>}
        {step === "checkout" && assemblyFee > 0 && <div className="flex justify-between text-sm text-orange-600 mb-1"><span>Assembly</span><span>+$99.00</span></div>}
        {step === "checkout" && pickupSavings > 0 && <div className="flex justify-between text-sm text-green-600 mb-1"><span>Pickup Savings ({pickupPct}%)</span><span>-${pickupSavings.toFixed(2)}</span></div>}
        <div className="flex justify-between text-xl font-bold pt-3 mt-2 border-t border-gray-200">
          <span>Total</span><span className="text-[#C05621]">${step === "checkout" ? finalTotal.toFixed(2) : quote.total?.toFixed(2)}</span>
        </div>
      </div>

      {quote.notes && <div className="bg-gray-50 rounded-xl p-4 mb-6 print:block"><p className="text-xs text-gray-500"><strong>Notes:</strong> {quote.notes}</p></div>}

      {/* Checkout Flow */}
      {!isCompleted && !isExpired && step === "review" && (
        <button onClick={() => setStep("checkout")} className="w-full py-4 bg-[#C05621] text-white rounded-xl font-bold text-lg hover:bg-[#DD6B20] transition-all shadow-lg flex items-center justify-center gap-2 print:hidden" data-testid="proceed-checkout-btn">
          <ShoppingCart size={20} /> Proceed to Checkout
        </button>
      )}

      {step === "checkout" && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 space-y-4 print:hidden" data-testid="quote-checkout-form">
          <h3 className="font-heading text-lg font-bold text-[#1A202C]">Complete Your Order</h3>

          {/* Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input value={custName} onChange={e => setCustName(e.target.value)} placeholder="Full name *" className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm" data-testid="quote-cust-name" />
            <input value={custEmail} onChange={e => setCustEmail(e.target.value)} placeholder="Email *" type="email" className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm" data-testid="quote-cust-email" />
            <input value={custPhone} onChange={e => setCustPhone(e.target.value)} placeholder="Phone" className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm" data-testid="quote-cust-phone" />
          </div>

          {/* Fulfillment Method */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Fulfillment Method</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "delivery", label: "Free Delivery", desc: "Drop-off at your door", icon: <Truck size={18} /> },
                { key: "delivery_assembly", label: "Delivery + Assembly", desc: "+$99 white-glove setup", icon: <Package size={18} /> },
                { key: "pickup", label: `Pickup & Save ${pickupPct}%`, desc: "Los Banos location", icon: <Store size={18} /> },
              ].map(opt => (
                <button key={opt.key} onClick={() => setDeliveryMethod(opt.key)}
                  className={`p-3 border-2 rounded-xl text-left transition-all ${deliveryMethod === opt.key ? "border-[#C05621] bg-[#FFF5F1]" : "border-gray-200 hover:border-gray-300"}`}
                  data-testid={`quote-delivery-${opt.key}`}>
                  <div className={`mb-1 ${deliveryMethod === opt.key ? "text-[#C05621]" : "text-gray-400"}`}>{opt.icon}</div>
                  <p className="text-xs font-bold text-[#1A202C]">{opt.label}</p>
                  <p className="text-[10px] text-gray-500">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Address */}
          {deliveryMethod !== "pickup" && (
            <input value={address} onChange={e => setAddress(e.target.value)} placeholder="Delivery address *"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm" data-testid="quote-address" />
          )}

          {/* Payment Method */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Payment Method</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "stripe", label: "Credit / Debit Card", desc: "Visa, Mastercard, Amex", icon: <CreditCard size={18} /> },
                { key: "affirm", label: "Affirm", desc: "Buy now, pay monthly", icon: <Tag size={18} /> },
                { key: "snap", label: "Snap Finance", desc: "$0 down, no credit needed", icon: <Percent size={18} /> },
              ].map(opt => (
                <button key={opt.key} onClick={() => setPaymentMethod(opt.key)}
                  className={`p-3 border-2 rounded-xl text-left transition-all ${paymentMethod === opt.key ? "border-[#C05621] bg-[#FFF5F1]" : "border-gray-200 hover:border-gray-300"}`}
                  data-testid={`quote-payment-${opt.key}`}>
                  <div className={`mb-1 ${paymentMethod === opt.key ? "text-[#C05621]" : "text-gray-400"}`}>{opt.icon}</div>
                  <p className="text-xs font-bold text-[#1A202C]">{opt.label}</p>
                  <p className="text-[10px] text-gray-500">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Pay Button */}
          <button onClick={handleAcceptAndPay} disabled={step === "paying"}
            className="w-full py-4 bg-[#38A169] text-white rounded-xl font-bold text-lg hover:bg-green-600 transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="quote-pay-btn">
            {step === "paying" ? "Processing..." : <><CreditCard size={20} /> {paymentMethod === "stripe" ? `Pay $${finalTotal.toFixed(2)}` : paymentMethod === "affirm" ? `Finance $${finalTotal.toFixed(2)} with Affirm` : `Apply with Snap Finance — $${finalTotal.toFixed(2)}`}</>}
          </button>
        </div>
      )}

      {/* Store Policies (always visible, prominent on print) */}
      <div className="border border-gray-200 rounded-xl p-5 mt-6 text-[10px] text-gray-500 leading-relaxed" data-testid="quote-policies">
        <h4 className="text-xs font-bold text-[#1A202C] mb-2 uppercase tracking-wider">Store Policies</h4>
        <p className="mb-1"><strong>Delivery:</strong> Free standard delivery (drop-off). White-glove assembly +$99. Homelegance 1-3 days, Coaster 3-5 days, Ashley 4-7 days.</p>
        <p className="mb-1"><strong>Returns:</strong> All sales are final. Damaged/defective items must be reported within 48 hours with photos. We arrange replacement/repair at no cost for manufacturer defects.</p>
        <p className="mb-1"><strong>Warranty:</strong> All products carry manufacturer warranty. Contact us for claims.</p>
        <p className="mb-1"><strong>Cancellation:</strong> Orders may be cancelled within 24 hours if not yet shipped.</p>
        <p className="mb-1"><strong>Price Match:</strong> We match any competitor's lower price + 5% off the difference.</p>
        <p><strong>Contact:</strong> (209) 827-4076 | Los Banos & Newman, CA | Family-owned since 2000</p>
      </div>

      <div className="text-center mt-6 print:hidden">
        <p className="text-xs text-gray-400">La Bodega Bay Furniture &middot; (209) 827-4076</p>
      </div>
    </div>
  );
}

// ─── DYNAMIC STOREFRONT (White-Label) ───
function TrafficTracker() {
  const location = useLocation();
  const sessionId = getSessionId();

  // Parse UTM params once
  const utmParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
    };
  }, []);

  // Send heartbeat to track visitor activity
  const sendHeartbeat = useCallback((event = null, eventData = null) => {
    try {
      const payload = {
        session_id: sessionId,
        page: location.pathname + location.search,
        referrer: document.referrer || "",
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        ...utmParams,
      };
      if (event) {
        payload.event = event;
        if (eventData) payload.event_data = eventData;
      }
      axios.post(`${API}/track/heartbeat`, payload).catch(() => {});
    } catch {}
  }, [location.pathname, location.search, sessionId, utmParams]);

  // Expose sendHeartbeat for event tracking from other components
  useEffect(() => {
    window.__trackVisitorEvent = (event, data) => sendHeartbeat(event, data);
    return () => { delete window.__trackVisitorEvent; };
  }, [sendHeartbeat]);

  // Send on page change + legacy track endpoint + GA4 page_view
  useEffect(() => {
    sendHeartbeat();
    try {
      // GA4 page_view (standard SPA pattern — gtag config uses send_page_view=true for INITIAL load,
      // we manually fire on route changes)
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "page_view", {
          page_path: location.pathname + location.search,
          page_location: window.location.href,
          page_title: document.title,
        });
      }
      const params = new URLSearchParams(window.location.search);
      axios.post(`${API}/track`, {
        page: location.pathname + location.search,
        referrer: document.referrer || "direct",
        source: params.get("utm_source") || "",
        medium: params.get("utm_medium") || "",
        campaign: params.get("utm_campaign") || "",
        session_id: sessionId,
      }).catch(() => {});
    } catch {}
  }, [location.pathname, location.search, sendHeartbeat, sessionId]);

  // Heartbeat every 15 seconds for live tracking
  useEffect(() => {
    const interval = setInterval(sendHeartbeat, 15000);
    return () => clearInterval(interval);
  }, [sendHeartbeat]);

  return null;
}

// ─── MAIN APP ───
function App() {
  const [cart, setCart] = useState(null);
  const [cartLoading, setCartLoading] = useState(true);
  const [lastAdded, setLastAdded] = useState(null);
  const sessionId = getSessionId();

  const fetchCart = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cart/${sessionId}`);
      setCart(r.data);
    } catch (e) {
      console.error(e);
    }
    setCartLoading(false);
  }, [sessionId]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  // Capture ad attribution (first-touch + last-touch) as early as possible
  useEffect(() => { captureAttribution(); }, []);

  // Keep the sitewide FurnitureStore aggregateRating in sync with admin-editable
  // store-info (real Google Business numbers) — patches the static shell schema live.
  useEffect(() => {
    axios.get(`${API}/store-info`).then(r => {
      const rating = r.data?.google_rating, count = r.data?.google_review_count;
      if (!rating || !count) return;
      document.querySelectorAll('script[type="application/ld+json"]').forEach(s => {
        try {
          const j = JSON.parse(s.textContent);
          if (j && j["@type"] === "FurnitureStore") {
            j.aggregateRating = { "@type": "AggregateRating", ratingValue: String(rating), reviewCount: String(count), bestRating: "5", worstRating: "1" };
            s.textContent = JSON.stringify(j);
          }
        } catch { /* noop */ }
      });
    }).catch(() => {});
  }, []);

  const addToCart = async (productId, quantity = 1) => {
    try {
      await axios.post(`${API}/cart/${sessionId}/add`, { product_id: productId, quantity });
      const fresh = (await axios.get(`${API}/cart/${sessionId}`)).data;
      setCart(fresh);
      const item = (fresh?.items || []).find(i => i.product_id === productId);
      const p = item?.product || {};
      // Fire GA4 add_to_cart event using enriched product info
      try {
        if (item && typeof window !== "undefined" && window.gtag) {
          window.gtag("event", "add_to_cart", {
            currency: "USD",
            value: Number(p.price || 0) * Number(quantity || 1),
            items: [{
              item_id: productId,
              item_name: p.name || "",
              item_brand: p.brand || "",
              item_category: p.category || "",
              price: Number(p.price || 0),
              quantity: Number(quantity || 1),
            }],
          });
          // Google Ads remarketing add_to_cart
          window.gtag("event", "add_to_cart", {
            send_to: AW_ID,
            value: Number(p.price || 0) * Number(quantity || 1),
            items: [{ id: productId, google_business_vertical: "retail" }],
          });
        }
        if (item) fbTrack("AddToCart", { content_ids: [productId], content_type: "product", content_name: p.name || "", contents: [{ id: productId, quantity: Number(quantity || 1) }], value: Number(p.price || 0) * Number(quantity || 1), currency: "USD" });
      } catch {}
      if (item) {
        setLastAdded({ item, quantity, total: fresh.total, itemCount: fresh.item_count });
      } else {
        toast.success("Added to cart!");
      }
    } catch (e) {
      toast.error("Failed to add to cart");
    }
  };

  const updateQuantity = async (productId, quantity) => {
    try {
      await axios.post(`${API}/cart/${sessionId}/update`, { product_id: productId, quantity });
      await fetchCart();
    } catch (e) {
      toast.error("Failed to update cart");
    }
  };

  const removeItem = async (productId) => {
    try {
      const item = (cart?.items || []).find(i => i.product_id === productId);
      if (item) track("remove_from_cart", {
        currency: "USD",
        value: Number(item.price || 0) * Number(item.quantity || 1),
        items: [{ item_id: productId, item_name: item.name || "", price: Number(item.price || 0), quantity: Number(item.quantity || 1) }],
      });
      await axios.delete(`${API}/cart/${sessionId}/remove/${productId}`);
      await fetchCart();
      toast.success("Item removed");
    } catch (e) {
      toast.error("Failed to remove item");
    }
  };

  const cartCount = cart?.item_count || 0;
  const [compareItems, setCompareItems] = useState([]);

  const toggleCompare = (product) => {
    setCompareItems(prev => {
      const exists = prev.find(p => p.id === product.id);
      if (exists) return prev.filter(p => p.id !== product.id);
      if (prev.length >= 4) { toast.error("Max 4 products to compare"); return prev; }
      return [...prev, { id: product.id, name: product.name, slug: product.slug, images: product.images, price: product.price }];
    });
  };
  const removeFromCompare = (id) => setCompareItems(prev => prev.filter(p => p.id !== id));
  const clearCompare = () => setCompareItems([]);

  return (
    <CartContext.Provider value={{ cart, cartCount, loading: cartLoading, addToCart, updateQuantity, removeItem, fetchCart, lastAdded, dismissLastAdded: () => setLastAdded(null) }}>
      <CompareContext.Provider value={{ compareItems, toggleCompare, removeFromCompare, clearCompare }}>
        <BrowserRouter>
          <LanguageProvider>
            <TrafficTracker />
            <ChatProvider>
              <AppLayout />
            </ChatProvider>
          </LanguageProvider>
        </BrowserRouter>
      </CompareContext.Provider>
    </CartContext.Provider>
  );
}

function AppLayout() {
  const location = useLocation();
  const isMobile = location.pathname === "/mobile";
  const isStoreDashboard = location.pathname.startsWith("/store/");
  const isStorefront = location.pathname.startsWith("/s/");

  const navigationType = useNavigationType();

  // Auto scroll to top on new navigations only (not back/forward)
  useEffect(() => {
    try { window.history.scrollRestoration = "manual"; } catch { /* older browsers */ }
  }, []);
  useEffect(() => { if (navigationType !== 'POP') window.scrollTo(0, 0); }, [location.pathname, navigationType]);

  // Always inject hreflang alternates on every route — even pages that don't call updatePageSEO.
  // This guarantees Google sees en/es alternates for all URLs.
  useEffect(() => {
    try {
      const base = "https://labodegabayfurniture.com";
      const path = location.pathname || "/";
      const isEs = path === "/es" || path.startsWith("/es/");
      const enPath = isEs ? (path === "/es" ? "/" : path.slice(3)) : path;
      const esPath = enPath === "/" ? "/es" : `/es${enPath.startsWith("/") ? enPath : "/" + enPath}`;
      const search = location.search || "";
      document.querySelectorAll('link[rel="alternate"][data-i18n="1"]').forEach(el => el.remove());
      const addAlt = (hreflang, href) => {
        const l = document.createElement("link");
        l.rel = "alternate";
        l.hreflang = hreflang;
        l.href = href;
        l.setAttribute("data-i18n", "1");
        document.head.appendChild(l);
      };
      addAlt("en-US", `${base}${enPath}${search}`);
      addAlt("es-US", `${base}${esPath}${search}`);
      addAlt("x-default", `${base}${enPath}${search}`);
    } catch (e) {
      // non-fatal
    }
  }, [location.pathname, location.search]);

  if (isMobile) {
    return (
      <div className="min-h-screen" data-testid="app-root">
        <Toaster position="top-center" toastOptions={{ duration: 3000, style: { fontFamily: "Manrope, sans-serif" } }} />
        <Suspense fallback={<LazyFallback />}><MobileDashboard /></Suspense>
      </div>
    );
  }

  if (isStorefront) {
    return (
      <div className="min-h-screen" data-testid="app-root">
        <Toaster position="top-center" toastOptions={{ duration: 3000, style: { fontFamily: "Manrope, sans-serif" } }} />
        <Suspense fallback={<LazyFallback />}>
        <Routes>
          <Route path="/s/:storeId/*" element={<WhiteLabelStorefront />} />
        </Routes>
        </Suspense>
      </div>
    );
  }

  if (isStoreDashboard) {
    return (
      <div className="min-h-screen" data-testid="app-root">
        <Toaster position="top-center" toastOptions={{ duration: 3000, style: { fontFamily: "Manrope, sans-serif" } }} />
        <Suspense fallback={<LazyFallback />}>
        <Routes>
          <Route path="/store/:storeId" element={<StoreDashboard />} />
        </Routes>
        </Suspense>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]" data-testid="app-root">
      <Toaster position="top-center" toastOptions={{ duration: 3000, style: { fontFamily: "Manrope, sans-serif" } }} />
      <Header />
      <main className="flex-1">
        <Suspense fallback={<LazyFallback />}>
        <Routes>
          {/* English (default) routes — UNCHANGED. Existing customers see no behavior change. */}
          <Route path="/" element={<HomePage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/shop/:featureSlug" element={<ShopPage />} />
          <Route path="/brand/:brandSlug" element={<ShopPage />} />
          <Route path="/brand/:brandSlug/:categorySlug" element={<ShopPage />} />
          <Route path="/categories" element={<AllCategoriesPage />} />
          <Route path="/category/:categoryName" element={<CategoryLandingPage />} />
          <Route path="/product/:slug" element={<ProductDetailPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:postId" element={<BlogPostPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/policies" element={<PoliciesPage />} />
          <Route path="/mattress-store" element={<MattressStorePage />} />
          <Route path="/price-guarantee" element={<PriceGuaranteePage />} />
          <Route path="/why-shop-local" element={<WhyShopLocalPage />} />
          <Route path="/financing" element={<FinancingPage />} />
          <Route path="/furniture/:citySlug" element={<CityLandingPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/quote/:quoteId" element={<QuotePage />} />
          <Route path="/store/:storeId" element={<StoreDashboard />} />
          <Route path="/master-admin" element={<MasterDashboard />} />

          {/* Spanish mirror — same components, language context derived from URL path. */}
          <Route path="/es" element={<HomePage />} />
          <Route path="/es/shop" element={<ShopPage />} />
          <Route path="/es/categories" element={<AllCategoriesPage />} />
          <Route path="/es/category/:categoryName" element={<CategoryLandingPage />} />
          <Route path="/es/product/:slug" element={<ProductDetailPage />} />
          <Route path="/es/cart" element={<CartPage />} />
          <Route path="/es/checkout" element={<CheckoutPage />} />
          <Route path="/es/order-confirmation" element={<OrderConfirmationPage />} />
          <Route path="/es/about" element={<AboutPage />} />
          <Route path="/es/blog" element={<BlogPage />} />
          <Route path="/es/blog/:postId" element={<BlogPostPage />} />
          <Route path="/es/contact" element={<ContactPage />} />
          <Route path="/es/policies" element={<PoliciesPage />} />
          <Route path="/es/mattress-store" element={<MattressStorePage />} />
          <Route path="/es/price-guarantee" element={<PriceGuaranteePage />} />
          <Route path="/es/why-shop-local" element={<WhyShopLocalPage />} />
          <Route path="/es/financing" element={<FinancingPage />} />
          <Route path="/es/furniture/:citySlug" element={<CityLandingPage />} />
          <Route path="/es/quote/:quoteId" element={<QuotePage />} />

          <Route path="*" element={
            <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center" data-testid="not-found-page">
              <h1 className="text-6xl font-black text-gray-800 mb-4">404</h1>
              <p className="text-xl text-gray-600 mb-6">Page not found</p>
              <p className="text-gray-400 mb-8">The page you're looking for doesn't exist or has been moved.</p>
              <Link to="/" className="px-6 py-3 bg-[#C05621] text-white font-bold rounded-xl hover:bg-[#DD6B20] transition-colors">Back to Home</Link>
            </div>
          } />
        </Routes>
        </Suspense>
      </main>
      <Footer />
      <ChatBot />
      <BackToTop />
      <CompareBar />
      {!location.pathname.startsWith("/admin") && <EmailCapturePopup />}
      {!location.pathname.startsWith("/admin") && <CookieConsentBanner />}
      {!location.pathname.startsWith("/admin") && <CartAddedPopup />}
    </div>
  );
}

export default App;
